import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import Icon from "../components/Icon";
import ProfileModal from "../components/ProfileModal";
import LandlordView from "../components/views/LandlordView";
import TenantView from "../components/views/TenantView";
import PayScoreGauge from "../components/PayScoreGauge";
import jsPDF from 'jspdf';
import LeaseAgreementModal from "../components/LeaseAgreementModal";
import AsyncActionModal from "../components/AsyncActionModal";
import ConfirmModal from "../components/ConfirmModal";
import Skeleton, { SkeletonList, SkeletonStatGrid, SkeletonTable } from "../components/Skeleton";
import { formatMoney, formatDateLong, formatDateWithTime, formatDateInputValue, formatTimeOnly, getNairobiMonth, getNairobiYear, getMonthName } from "../utils/format";
import UnitImagesUploader from "../components/UnitImagesUploader";
import ManagementCompanyStaffModal from "../components/ManagementCompanyStaffModal";
import {
  UNIT_TYPE_OPTIONS,
  formatPayScorePercent,
  getCurrentMonthRange,
  getMissingAssignmentFields,
  getPayScoreStatusClasses,
  getUserRoleChipLabel,
  normalizeId,
} from "./frp/frpHelpers";
import {
  canCaptureWaterPurchaseUser,
  canManageCompanyUser,
  canManageManagementUser,
  canManagePropertyUser,
  canUpdateWaterReadingUser,
  getUserRoleDisplayUser,
  getUserRolesUser,
  hasAnyTenantAccessUser,
  isLandlordUser,
  isManagerUser,
} from "./frp/frpAccess";
import {
  buildTenantMonthlyInvoiceSummary,
  computeUtilityStatementRunningBalances,
  filterRowsByDateRange,
  getUtilityStatementDate,
  getPaymentSplit,
  sumCurrentUnitBalances,
  sumPaymentRows,
} from "./frp/frpStatements";
import {
  getInitials,
  getRoleDisplayName,
  getShortName,
} from "./frp/frpDisplay";
import { maskKraPin } from "../utils/kraNameMatch";
import WaiveBillModal from "./utbills/WaiveBillModal";
import EditInvoiceModal from "./utbills/EditInvoiceModal";
import PropertyAutoInvoicing from "./utbills/PropertyAutoInvoicing";
import UtilityInvoiceStatusBadge from "./utbills/UtilityInvoiceStatusBadge";
import {
  getUtilityDebtSummary,
  getUtilityInvoiceSettlement,
  getUtilityRowChargeAmount,
  getUtilityRowPaymentAmount,
  getUtilitySettlementId,
  getUtilityWalletBalance,
  getVisibleUtilitySettlementRows,
} from "./utbills/utilitySettlement";
import { getStableWaterPurchaseRequest } from "./frp/waterPurchaseRequest";
import { getWaterPurchaseValidation } from "./frp/waterPurchasePlan";
import {
  buildRequestFingerprint,
  clearStableRequestKey,
  getStableRequestKey,
} from "../utils/requestKey";

const TENANT_MONTHLY_STATUS_LIMIT = 6;

// Visual metadata for the "Choose your role" selection cards.
const ROLE_VIEW_META = {
  manager: {
    icon: "buildings-fill",
    description: "Manage properties, units & tenants",
    iconWrap: "bg-emerald-100 text-emerald-600",
    accent: "from-emerald-500 to-green-500",
    hover: "hover:border-emerald-300",
    arrow: "text-emerald-600",
  },
  landlord: {
    icon: "key-fill",
    description: "Track your properties & earnings",
    iconWrap: "bg-amber-100 text-amber-600",
    accent: "from-amber-500 to-orange-500",
    hover: "hover:border-amber-300",
    arrow: "text-amber-600",
  },
  tenant: {
    icon: "house-door-fill",
    description: "Pay rent & view your statements",
    iconWrap: "bg-sky-100 text-sky-600",
    accent: "from-sky-500 to-cyan-500",
    hover: "hover:border-sky-300",
    arrow: "text-sky-600",
  },
};

const DEFAULT_ROLE_VIEW_META = {
  icon: "person-badge-fill",
  description: "Enter this workspace",
  iconWrap: "bg-slate-100 text-slate-600",
  accent: "from-slate-400 to-slate-500",
  hover: "hover:border-slate-300",
  arrow: "text-slate-600",
};

function FRPModule() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [managementCompanies, setManagementCompanies] = useState([]);
  const [units, setUnits] = useState([]);
  const [myUnits, setMyUnits] = useState([]); // Separate state for tenant/landlord units
  // eslint-disable-next-line no-unused-vars
  const [users, setUsers] = useState({ landlords: [], tenants: [] });
  // Computed per-unit balances for manager/landlord views using tenant-view statements
  const [managerUnitBalances, setManagerUnitBalances] = useState({});
  const [managerUnitUtilityBalances, setManagerUnitUtilityBalances] = useState({});
  const [managerBalancesLoading, setManagerBalancesLoading] = useState(false);
  const utilityStatementRequestRef = useRef(0);
  const [wallet, setWallet] = useState(null);
  const [payScore, setPayScore] = useState(null);
  const [payScoreLoading, setPayScoreLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [activeTab, setActiveTab] = useState("management");
  // Banks catalog for bank dropdown and paybill auto-fill
  const [banksCatalog, setBanksCatalog] = useState([]);

  // Load banks catalog (JSON preferred; fallback to xlsx if available)
  useEffect(() => {
    let mounted = true;
    const loadBanks = async () => {
      try {
        let response = await fetch('/bankslist.json');
        if (response.ok) {
          const ct = (response.headers.get('content-type') || '').toLowerCase();
          if (ct.includes('application/json')) {
            const data = await response.json();
            const list = Array.isArray(data) ? data.filter(b => b && b.name) : [];
            if (mounted) setBanksCatalog(list);
            return;
          }
        }
        // Fallback: parse xlsx if present
        try {
          const XLSX = await import('xlsx');
          response = await fetch('/bankslist.xlsx');
          if (response.ok) {
            const arrayBuffer = await response.arrayBuffer();
            const wb = XLSX.read(arrayBuffer, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(ws, { header: 1 });
            const rows = Array.isArray(json) ? json : [];
            const header = rows[0] || [];
            const nameIdx = header.findIndex(h => String(h).toLowerCase() === 'name');
            const paybillIdx = header.findIndex(h => String(h).toLowerCase() === 'paybill');
            const list = rows.slice(1)
              .map(r => ({ name: r[nameIdx] || '', paybill: r[paybillIdx] || '' }))
              .filter(b => b.name);
            if (mounted) setBanksCatalog(list);
          }
        } catch (e) {
          console.warn('XLSX not available; skipping banks xlsx parsing');
        }
      } catch (e) {
        console.warn('Failed to load banks catalog', e);
      }
    };
    loadBanks();
    return () => { mounted = false };
  }, []);

  // Role-based navigation state
  const [activeRole, setActiveRole] = useState(null);
  // Keep tenant pay action deterministic across environments (no localStorage gate).
  const showTenantPayRent = true;

  // Company details view state
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyDetails, setShowCompanyDetails] = useState(false);

  // Units view state
  const [showUnitsView, setShowUnitsView] = useState(false);
  const [staffModalCompany, setStaffModalCompany] = useState(null);



  // Form states
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [showUpdatePropertyForm, setShowUpdatePropertyForm] = useState(false);
  const [showManagementForm, setShowManagementForm] = useState(false);
  const [showUpdateManagementForm, setShowUpdateManagementForm] = useState(false);
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [showUpdateUnitForm, setShowUpdateUnitForm] = useState(false);
  const [showCreateUnitImages, setShowCreateUnitImages] = useState(false);
  const [showEditUnitImages, setShowEditUnitImages] = useState(false);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [showAssignBlockedModal, setShowAssignBlockedModal] = useState(false);
  const [assignBlockedMissingFields, setAssignBlockedMissingFields] = useState([]);
  const [showAddFundsForm, setShowAddFundsForm] = useState(false);
  const [showTransactionStatement, setShowTransactionStatement] = useState(false);
  const [showTenantHistory, setShowTenantHistory] = useState(false);
  const [showTenancyHistoryModal, setShowTenancyHistoryModal] = useState(false);
  const [showVacateForm, setShowVacateForm] = useState(false);
  const [showUnitDetailsModal, setShowUnitDetailsModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedManagementCompany, setSelectedManagementCompany] = useState(null);
  const [assignmentType, setAssignmentType] = useState("");
  const [assignmentActionResult, setAssignmentActionResult] = useState(null);
  const [moveInDate, setMoveInDate] = useState(""); // New state for move-in date
  const [transactionStatement, setTransactionStatement] = useState(null);
  const [tenantHistory, setTenantHistory] = useState(null);
  const [managedTenants, setManagedTenants] = useState([]);
  const [tenancyHistoryLoading, setTenancyHistoryLoading] = useState(false);
  const [selectedPropertyForTenancy, setSelectedPropertyForTenancy] = useState(null);
  const [selectedCompanyForTenancy, setSelectedCompanyForTenancy] = useState(null);
  const [tenantHistorySearch, setTenantHistorySearch] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [selectedTenantForHistory, setSelectedTenantForHistory] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementStartDate, setStatementStartDate] = useState(""); // Date filter for statements
  const [statementEndDate, setStatementEndDate] = useState(""); // Date filter for statements
  const [statementFilterOpen, setStatementFilterOpen] = useState(false);
  const [showTenantMonthlyStatus, setShowTenantMonthlyStatus] = useState(false);
  const [tenantMonthlyStatusLoading, setTenantMonthlyStatusLoading] = useState(false);
  const [tenantMonthlyStatus, setTenantMonthlyStatus] = useState(null);
  const [showTenantInvoicePaymentsModal, setShowTenantInvoicePaymentsModal] = useState(false);
  const [selectedTenantInvoiceRow, setSelectedTenantInvoiceRow] = useState(null);
  const [tenantMonthlyStatusVisibleInvoices, setTenantMonthlyStatusVisibleInvoices] = useState(TENANT_MONTHLY_STATUS_LIMIT);
  const [vacateForm, setVacateForm] = useState({ vacateDate: "", confirmVacate: "" });
  const [vacateLoading, setVacateLoading] = useState(false);

  // Compute per-unit balances from tenant-view statements so managers/landlords see correct balances
  useEffect(() => {
    let cancelled = false;
    const fetchBalancesForUnits = async () => {
      setManagerBalancesLoading(true);
      if (!units || units.length === 0) {
        if (!cancelled) setManagerUnitBalances({});
        setManagerBalancesLoading(false);
        return;
      }
      // Determine property id
      const derivePropertyId = () => {
        if (selectedProperty?._id) return selectedProperty._id;
        const first = units[0];
        if (!first) return null;
        const prop = first.property;
        return (prop && (prop._id || prop)) || null;
      };
      const propertyId = derivePropertyId();
      if (!propertyId) {
        setManagerBalancesLoading(false);
        return;
      }
      if (!canManagePropertyUser(selectedProperty || units[0]?.property, user, managementCompanies)) {
        if (!cancelled) { setManagerUnitBalances({}); setManagerUnitUtilityBalances({}); }
        setManagerBalancesLoading(false);
        return;
      }
      try {
        const resp = await api.get(`/frp/properties/${propertyId}/unit-tenant-balances`);
        const map = {};
        const utilMap = {};
        for (const row of (resp.data?.balances || [])) {
          const uid = row.unitId?._id || row.unitId;
          // Backend returns the same rent-only balance used by tenant statements.
          map[uid] = typeof row.balance === 'number' ? row.balance : (parseFloat(row.balance) || 0);
          utilMap[uid] = typeof row.utilityBalance === 'number' ? row.utilityBalance : (parseFloat(row.utilityBalance) || 0);
        }
        if (!cancelled) { setManagerUnitBalances(map); setManagerUnitUtilityBalances(utilMap); }
      } catch (e) {
        // leave map empty on error
      }
      setManagerBalancesLoading(false);
    };
    fetchBalancesForUnits();
    return () => { cancelled = true; };
  }, [units, selectedProperty, user, managementCompanies]);

  // Form data
  const [propertyForm, setPropertyForm] = useState({ name: "", address: "", managementCompanyId: "" });
  const [updatePropertyForm, setUpdatePropertyForm] = useState({
    name: "",
    cityTown: "",
    location: "",
    area: "",
    managementPercentage: 10,
    waterUnitCharge: "",
    waterUnitsMeasure: "",
    trashMonthlyCharge: ""
  });
  const [unitForm, setUnitForm] = useState({ label: "", rentAmount: "", type: "", images: [] });
  const [updateUnitForm, setUpdateUnitForm] = useState({
    label: "",
    type: "",
    deposit: "",
    rentAmount: "",
    lateFee: "",
    fromDate: "",
    images: []
  });
  const [selectedUserId, setSelectedUserId] = useState("");

  // When opening the Create Unit form, default Unit No to next sequential label like "# 12"
  useEffect(() => {
    if (showUnitForm) {
      const nextIndex = (Array.isArray(units) ? units.length : 0) + 1;
      setShowCreateUnitImages(false);
      setUnitForm((prev) => {
        if (prev.label && prev.label.trim().length > 0) return prev;
        return { ...prev, label: `# ${nextIndex}` };
      });
    }
  }, [showUnitForm, units]);

  useEffect(() => {
    if (showUpdateUnitForm) return;
    setShowEditUnitImages(false);
  }, [showUpdateUnitForm]);
  const [addFundsAmount, setAddFundsAmount] = useState("");

  // Payment modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentUnit, setSelectedPaymentUnit] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDescription, setPaymentDescription] = useState("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  // Invoice modal states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoiceUnit, setSelectedInvoiceUnit] = useState(null);
  const [selectedInvoiceTenant, setSelectedInvoiceTenant] = useState(null);
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [invoiceDescription, setInvoiceDescription] = useState("");
  const [invoiceDueDate, setInvoiceDueDate] = useState("");
  const [showWaterMeterModal, setShowWaterMeterModal] = useState(false);
  const [selectedWaterUnit, setSelectedWaterUnit] = useState(null);
  const [waterMeterState, setWaterMeterState] = useState(null);
  const [waterMeterWalletBalance, setWaterMeterWalletBalance] = useState(0);
  const [waterHistoryLoading, setWaterHistoryLoading] = useState(false);
  const [waterUnitTransactions, setWaterUnitTransactions] = useState([]);
  const [selectedUtilityType, setSelectedUtilityType] = useState("water");
  const [utilityStatementLoading, setUtilityStatementLoading] = useState(false);
  const [utilityStatement, setUtilityStatement] = useState(null);
  const [utilityBillForm, setUtilityBillForm] = useState({ amount: "", description: "", dueDate: "" });
  const [utilityBillSubmitting, setUtilityBillSubmitting] = useState(false);
  const [utilitySettlementSubmitting, setUtilitySettlementSubmitting] = useState(false);
  const [utilitySettlementLoadingByUnit, setUtilitySettlementLoadingByUnit] = useState({});
  const [showPropertyWaterStatementsModal, setShowPropertyWaterStatementsModal] = useState(false);
  const [propertyWaterLoading, setPropertyWaterLoading] = useState(false);
  const [waterPropertyTransactions, setWaterPropertyTransactions] = useState([]);
  const [waterPropertyLatestByUnit, setWaterPropertyLatestByUnit] = useState([]);
  const [waterPropertySummary, setWaterPropertySummary] = useState(null);
  const [propertyWaterStartDate, setPropertyWaterStartDate] = useState("");
  const [propertyWaterEndDate, setPropertyWaterEndDate] = useState("");
  const [propertyWaterSearch, setPropertyWaterSearch] = useState("");
  const [showMeterReplacementForm, setShowMeterReplacementForm] = useState(false);
  const [meterReplacementConfirm, setMeterReplacementConfirm] = useState(null);
  const [meterReplacementForm, setMeterReplacementForm] = useState({
    oldMeterFinalReading: "",
    newMeterCurrentReading: "",
    replacementDate: "",
    note: ""
  });
  const [showMeterCorrectionForm, setShowMeterCorrectionForm] = useState(false);
  const [meterCorrectionConfirm, setMeterCorrectionConfirm] = useState(null);
  const [meterCorrectionForm, setMeterCorrectionForm] = useState({
    currentReading: "",
    cutOffReading: "",
    correctionDate: "",
    reason: ""
  });
  const [waterMeterForm, setWaterMeterForm] = useState({
    currentReading: "",
    currentReadingWhole: "",
    currentReadingDecimal: "",
    amountPaid: "",
    readingDate: ""
  });
  const waterPurchaseRequestRef = useRef({ fingerprint: "", requestId: "" });

  // Waive Bill modal states
  const [showWaiveBillModal, setShowWaiveBillModal] = useState(false);
  const [selectedWaiveBillUnit, setSelectedWaiveBillUnit] = useState(null);
  const [selectedWaiveBillTenant, setSelectedWaiveBillTenant] = useState(null);
  const [selectedWaiveBillInvoice, setSelectedWaiveBillInvoice] = useState(null);
  const [waiveBillAmount, setWaiveBillAmount] = useState("");
  const [waiveBillDescription, setWaiveBillDescription] = useState("");
  const [showUtilityWaiveModal, setShowUtilityWaiveModal] = useState(false);
  const [selectedUtilityWaiveInvoice, setSelectedUtilityWaiveInvoice] = useState(null);
  const [utilityWaiveCategoryOptions, setUtilityWaiveCategoryOptions] = useState([]);
  const [showUtilityEditModal, setShowUtilityEditModal] = useState(false);
  const [selectedUtilityEditInvoice, setSelectedUtilityEditInvoice] = useState(null);

  // Lease Agreement modal states
  const [showLeaseAgreementModal, setShowLeaseAgreementModal] = useState(false);
  // Lease logic is handled inside LeaseAgreementModal.

  // Search states for assignment forms
  const [landlordSearch, setLandlordSearch] = useState("");
  const [tenantSearch, setTenantSearch] = useState("");
  const [propertySearch, setPropertySearch] = useState("");
  const [unitsSearch, setUnitsSearch] = useState(""); // Added for units search
  const [unitsBalanceFilter, setUnitsBalanceFilter] = useState(""); // Added for balance sorting
  const [landlordResults, setLandlordResults] = useState([]);
  const [tenantResults, setTenantResults] = useState([]);
  const [userPayScoreById, setUserPayScoreById] = useState({});
  const [userPayScoreLoadingById, setUserPayScoreLoadingById] = useState({});
  const [showLandlordDropdown, setShowLandlordDropdown] = useState(false);
  const [showTenantDropdown, setShowTenantDropdown] = useState(false);
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const ASSIGN_SEARCH_DEBOUNCE_MS = 350;
  const ASSIGN_SEARCH_MIN_CHARS = 2;
  const landlordSearchDebounceRef = useRef(null);
  const tenantSearchDebounceRef = useRef(null);
  const landlordSearchRequestSeqRef = useRef(0);
  const tenantSearchRequestSeqRef = useRef(0);
  const [createTenantAccountMode, setCreateTenantAccountMode] = useState(false);
  const [creatingTenantAccount, setCreatingTenantAccount] = useState(false);
  const [newTenantAccountForm, setNewTenantAccountForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    idNumber: "",
    confirmIdNumber: "",
    pin: "",
    confirmPin: "",
  });
  const [tenantAccountVerificationModal, setTenantAccountVerificationModal] = useState({
    open: false,
    canProceed: false,
    allowKraOverride: false,
    issues: [],
    existingUser: null,
    kraStatus: null,
    submitPayload: null,
  });
  const isAssigningUser = assignmentActionResult?.status === "loading";

  const getReadableUserEmail = (email) => {
    const normalized = String(email || "").trim();
    if (!normalized) return "No email provided";
    if (normalized.toLowerCase().endsWith("@recordlogs.invalid")) return "No email provided";
    return normalized;
  };

  useEffect(() => {
    return () => {
      if (landlordSearchDebounceRef.current) {
        clearTimeout(landlordSearchDebounceRef.current);
      }
      if (tenantSearchDebounceRef.current) {
        clearTimeout(tenantSearchDebounceRef.current);
      }
    };
  }, []);

  // Management form states - SIMPLE FORM
  const [managementForm, setManagementForm] = useState({
    companyName: '',
    telephoneNumber: '',
    headOfficeLocation: '',
    yourPositionInCompany: '',
    ownerNames: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Profile dropdown states
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Wallet transactions states
  const [showWalletTransactionsModal, setShowWalletTransactionsModal] = useState(false);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [walletTransactionsLoading, setWalletTransactionsLoading] = useState(false);
  const [utilityWallet, setUtilityWallet] = useState(null);
  const [showUtilityTopUpButton, setShowUtilityTopUpButton] = useState(false);
  const [showAddUtilityFundsForm, setShowAddUtilityFundsForm] = useState(false);
  const [addUtilityFundsAmount, setAddUtilityFundsAmount] = useState("");
  const [showUtilityWalletTransactionsModal, setShowUtilityWalletTransactionsModal] = useState(false);
  const [utilityWalletTransactions, setUtilityWalletTransactions] = useState([]);
  const [utilityWalletStatementSummary, setUtilityWalletStatementSummary] = useState(null);
  const [utilityWalletTransactionsLoading, setUtilityWalletTransactionsLoading] = useState(false);
  const [utilityWalletStartDate, setUtilityWalletStartDate] = useState("");
  const [utilityWalletEndDate, setUtilityWalletEndDate] = useState("");
  const [utilityWalletFilterOpen, setUtilityWalletFilterOpen] = useState(false);

  // Property statements (payments) states
  const [showPropertyStatementsModal, setShowPropertyStatementsModal] = useState(false);
  const [propertyPayments, setPropertyPayments] = useState([]);
  const [propertyPaymentsLoading, setPropertyPaymentsLoading] = useState(false);
  const [propertyStatementStartDate, setPropertyStatementStartDate] = useState("");
  const [propertyStatementEndDate, setPropertyStatementEndDate] = useState("");
  const [propertyFilterOpen, setPropertyFilterOpen] = useState(false);

  // Company transactions (payments across all properties of a company)
  const [showCompanyTransactionsModal, setShowCompanyTransactionsModal] = useState(false);
  const [companyTransactions, setCompanyTransactions] = useState([]);
  const [companyTransactionsLoading, setCompanyTransactionsLoading] = useState(false);
  const [companyStartDate, setCompanyStartDate] = useState("");
  const [companyEndDate, setCompanyEndDate] = useState("");
  const [companyFilterOpen, setCompanyFilterOpen] = useState(false);

  // Management-level combined transactions (across all companies)
  const [showManagementTransactionsModal, setShowManagementTransactionsModal] = useState(false);
  const [managementTransactions, setManagementTransactions] = useState([]);
  const [managementTransactionsLoading, setManagementTransactionsLoading] = useState(false);
  const [mgmtStartDate, setMgmtStartDate] = useState("");
  const [mgmtEndDate, setMgmtEndDate] = useState("");
  const [mgmtFilterOpen, setMgmtFilterOpen] = useState(false);
  const [triggeringPayments, setTriggeringPayments] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [triggerSummary, setTriggerSummary] = useState(null);

  // Landlord-level combined transactions (across all landlord's properties)
  const [showLandlordTransactionsModal, setShowLandlordTransactionsModal] = useState(false);
  const [landlordTransactions, setLandlordTransactions] = useState([]);
  const [landlordTransactionsLoading, setLandlordTransactionsLoading] = useState(false);
  const [ldStartDate, setLdStartDate] = useState("");
  const [ldEndDate, setLdEndDate] = useState("");
  const [ldFilterOpen, setLdFilterOpen] = useState(false);

  // Monthly summary states
  const [showMonthlySummaryModal, setShowMonthlySummaryModal] = useState(false);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [monthlySummaryLoading, setMonthlySummaryLoading] = useState(false);
  const [selectedSummaryMonth, setSelectedSummaryMonth] = useState(getNairobiMonth());
  const [selectedSummaryYear, setSelectedSummaryYear] = useState(getNairobiYear());
  const [summaryRole, setSummaryRole] = useState(null);

  const canViewMonthlySummary = (role) => {
    if (role === 'landlord') return true;
    if (role === 'manager') return canManageManagement();
    return false;
  };

  // Keep monthly summary in sync with selected role
  useEffect(() => {
    if (!activeRole) return;
    setSummaryRole(activeRole);
    if (canViewMonthlySummary(activeRole)) {
      fetchMonthlySummary();
    } else {
      setMonthlySummary(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRole, managementCompanies, user]);

  // eslint-disable-next-line no-unused-vars
  const [editMode, setEditMode] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    bankAccNumber: '',
    currentPin: '',
    newPin: '',
    confirmPin: ''
  });

  // Property form states - COMPLEX FORM  
  const [complexPropertyForm, setComplexPropertyForm] = useState({
    propertyName: '',
    phoneNumber: '',
    landlordId: '',
    cityTown: '',
    location: '',
    area: '',
    managementPercentage: 0,
    waterUnitCharge: '',
    waterUnitsMeasure: '1',
    trashMonthlyCharge: '',
    managementCompanyId: '',
    numberOfUnits: ''
  });

  // Role/access helpers are centralized in frpAccess.
  const isManager = (currentUser) => isManagerUser(currentUser, managementCompanies);
  const isLandlord = (currentUser) => isLandlordUser(currentUser);
  const canManageCompany = (companyId) =>
    canManageCompanyUser(companyId, managementCompanies);
  const canManageManagement = () => canManageManagementUser(user, managementCompanies);
  const canManageProperty = (property) =>
    canManagePropertyUser(property, user, managementCompanies);
  const canUpdateWaterReading = (property) =>
    canUpdateWaterReadingUser(property, user, managementCompanies);
  const canCaptureWaterPurchase = (property, options = {}) =>
    canCaptureWaterPurchaseUser(property, user, managementCompanies, options);
  const hasAnyTenantAccess = (currentUser) =>
    hasAnyTenantAccessUser(currentUser, myUnits);
  const getUserRoleDisplay = (currentUser) =>
    getUserRoleDisplayUser(currentUser, managementCompanies, myUnits);
  const getUserRoles = (currentUser) =>
    getUserRolesUser(currentUser, managementCompanies, myUnits);

  // Handle viewing company details
  const handleViewCompany = (company) => {
    if (company?.locked) {
      return;
    }
    setSelectedCompany(company);
    setShowCompanyDetails(true);
  };

  const handleBackToCompanies = () => {
    setSelectedCompany(null);
    setShowCompanyDetails(false);
  };

  const handleViewUnits = (property) => {
    setSelectedProperty(property);
    setShowUnitsView(true);
    fetchUnits(property._id);
  };

  const handleBackToProperties = () => {
    setShowUnitsView(false);
    setSelectedProperty(null);
    setUnits([]);
  };

  // Profile dropdown handlers
  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleProfileClick = () => {
    setShowProfileModal(true);
    setEditMode(false);
    setFormError('');
    // Reset form with current user data
    setProfileForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      bankAccNumber: user.bankAccNumber || '',
      currentPin: '',
      newPin: '',
      confirmPin: ''
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleProfileSave = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    // Validate PIN change if provided
    if (profileForm.newPin) {
      if (!profileForm.currentPin) {
        setFormError('Current PIN is required to change PIN');
        setFormLoading(false);
        return;
      }
      if (profileForm.newPin !== profileForm.confirmPin) {
        setFormError('New PIN and confirmation do not match');
        setFormLoading(false);
        return;
      }
      if (!/^\d{4}$/.test(profileForm.newPin)) {
        setFormError('New PIN must be exactly 4 digits');
        setFormLoading(false);
        return;
      }
    }

    try {
      const updateData = {
        name: profileForm.name,
        email: profileForm.email,
        phone: profileForm.phone,
        bankAccNumber: profileForm.bankAccNumber
      };

      // Only include PIN data if changing PIN
      if (profileForm.newPin) {
        updateData.currentPin = profileForm.currentPin;
        updateData.newPin = profileForm.newPin;
      }

      const response = await api.put('/auth/profile', updateData);
      setUser(response.data.user);
      setEditMode(false);
      setFormError('');
      // Reset PIN fields
      setProfileForm({
        name: response.data.user.name || '',
        email: response.data.user.email || '',
        phone: response.data.user.phone || '',
        bankAccNumber: response.data.user.bankAccNumber || '',
        currentPin: '',
        newPin: '',
        confirmPin: ''
      });
      alert('Profile updated successfully!');
    } catch (error) {
      setFormError('Error updating profile: ' + (error.response?.data?.error || error.message));
    } finally {
      setFormLoading(false);
    }
  };

  const fetchMyUnits = useCallback(async () => {
    if (!user) return;

    try {
      const response = await api.get("/frp/my-units?scope=tenant");
      setMyUnits(response.data.units);
    } catch (error) {
      console.error("Error fetching my units:", error);
    }
  }, [user]);

  const fetchPayScore = async () => {
    try {
      setPayScoreLoading(true);
      const res = await api.get("/payscore/me");
      setPayScore(res.data);
    } catch (error) {
      console.error("Error fetching PaySCORE:", error);
    } finally {
      setPayScoreLoading(false);
    }
  };

  const fetchUserData = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
      // Initialize profile form with user data
      setProfileForm({
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        bankAccNumber: response.data.bankAccNumber || '',
        currentPin: '',
        newPin: '',
        confirmPin: ''
      });
      fetchPayScore(); // Fetch PaySCORE separately
    } catch (error) {
      console.error("Error fetching user data:", error);
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // If arriving with companyId, auto-select Property Manager role
  useEffect(() => {
    const url = new URL(window.location.href);
    const companyIdParam = url.searchParams.get('companyId');
    if (!companyIdParam) return;
    if (user && isManagerUser(user, managementCompanies) && activeRole !== 'manager') {
      setActiveRole('manager');
    }
  }, [user, activeRole, managementCompanies]);

  // If a companyId is provided in the URL (e.g., from admin impersonation),
  // open that company after data loads
  useEffect(() => {
    const url = new URL(window.location.href);
    const companyIdParam = url.searchParams.get('companyId');
    if (!companyIdParam) return;

    // Wait until managementCompanies are loaded
    // Poll briefly until managementCompanies has content or 2s timeout
    let attempts = 0;
    const maxAttempts = 20; // 20 * 100ms = 2s
    const interval = setInterval(() => {
      attempts += 1;
      if (attempts > maxAttempts) {
        clearInterval(interval);
        return;
      }
      const found = managementCompanies.find(c => String(c._id) === String(companyIdParam));
      if (found) {
        clearInterval(interval);
        if (found.locked) {
          return;
        }
        setSelectedCompany(found);
        setShowCompanyDetails(true);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [managementCompanies]);
  // If a propertyId is provided, open that property (units view) after data loads
  useEffect(() => {
    const url = new URL(window.location.href);
    const propertyIdParam = url.searchParams.get('propertyId');
    if (!propertyIdParam) return;

    let attempts = 0;
    const maxAttempts = 20;
    const interval = setInterval(() => {
      attempts += 1;
      if (attempts > maxAttempts) {
        clearInterval(interval);
        return;
      }
      const found = properties.find(p => String(p._id) === String(propertyIdParam));
      if (found) {
        clearInterval(interval);
        setActiveRole(prev => prev || 'manager');
        setSelectedCompany(managementCompanies.find(c => String(c._id) === String(found.managementCompany?._id || found.managementCompany)) || null);
        setShowCompanyDetails(true);
        setSelectedProperty(found);
        setShowUnitsView(true);
        fetchUnits(found._id);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [properties, managementCompanies]);

  useEffect(() => {
    if (user) {
      fetchProperties();
      fetchManagementCompanies();
      fetchWallet();
      fetchUtilityWallet();
      fetchFrpUiFlags();

      // Always fetch units with tenant scope to include historical tenancies
      fetchMyUnits();
    }
  }, [user, fetchMyUnits]);

  // Refresh data when the page becomes visible again (e.g., coming back from Dashboard)
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        fetchManagementCompanies();
        fetchProperties();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLandlordDropdown && !event.target.closest('.landlord-search-container')) {
        setShowLandlordDropdown(false);
      }
      if (showTenantDropdown && !event.target.closest('.tenant-search-container')) {
        setShowTenantDropdown(false);
      }
      if (isMenuOpen && !event.target.closest('.dropdown-container')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLandlordDropdown, showTenantDropdown, isMenuOpen]);

  const fetchProperties = async () => {
    try {
      const response = await api.get("/frp/properties");
      setProperties(response.data.properties);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching properties:", error);
      setLoading(false);
    }
  };

  const fetchManagementCompanies = async () => {
    try {
      const response = await api.get("/management-company/my-companies");
      setManagementCompanies(response.data.managementCompanies || []);
    } catch (error) {
      console.error("Error fetching management companies:", error);
    }
  };

  const fetchUnits = async (propertyId) => {
    try {
      const response = await api.get(`/frp/properties/${propertyId}/units?lite=1`);
      setUnits(response.data.units);
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const [landlordsRes, tenantsRes] = await Promise.all([
        api.get("/frp/users/L"),
        api.get("/frp/users/T"),
      ]);
      setUsers({
        landlords: landlordsRes.data.users,
        tenants: tenantsRes.data.users,
      });
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, []);

  useEffect(() => {
    if (user && canManageManagementUser(user, managementCompanies)) {
      fetchUsers();
    }
  }, [user, managementCompanies, fetchUsers]);

  const fetchWallet = async () => {
    try {
      const response = await api.get("/wallet/my-wallet");
      setWallet(response.data.wallet);
    } catch (error) {
      console.error("Error fetching wallet:", error);
    }
  };

  const fetchFrpUiFlags = async () => {
    try {
      const response = await api.get("/frp/ui-flags");
      setShowUtilityTopUpButton(response?.data?.utilityTopUpShow === true);
    } catch (error) {
      setShowUtilityTopUpButton(false);
    }
  };

  const fetchUtilityWallet = async () => {
    try {
      const response = await api.get("/utilities/my-account");
      setUtilityWallet(response.data?.account || null);
    } catch (error) {
      console.error("Error fetching UT-Bills wallet:", error);
      setUtilityWallet(null);
    }
  };

  const fetchPropertyTenants = async (propertyId) => {
    try {
      setTenancyHistoryLoading(true);
      const response = await api.get(`/frp/manager/tenants?propertyId=${propertyId}`);
      setManagedTenants(response.data.tenants || []);
    } catch (error) {
      console.error("Error fetching property tenants:", error);
      alert("Error fetching tenant history: " + error.response?.data?.error);
    } finally {
      setTenancyHistoryLoading(false);
    }
  };

  const fetchCompanyTenants = async (companyId) => {
    try {
      setTenancyHistoryLoading(true);
      const response = await api.get(`/frp/manager/tenants?managementCompanyId=${companyId}`);
      setManagedTenants(response.data.tenants || []);
    } catch (error) {
      console.error("Error fetching company tenants:", error);
      alert("Error fetching tenant history: " + error.response?.data?.error);
    } finally {
      setTenancyHistoryLoading(false);
    }
  };

  const fetchWalletTransactions = async () => {
    try {
      setWalletTransactionsLoading(true);
      const response = await api.get("/wallet/transactions");
      setWalletTransactions(response.data.transactions);
    } catch (error) {
      console.error("Error fetching wallet transactions:", error);
      alert("Error fetching wallet transactions: " + error.response?.data?.error);
    } finally {
      setWalletTransactionsLoading(false);
    }
  };

  const fetchUtilityWalletTransactions = async (startDate = "", endDate = "") => {
    try {
      setUtilityWalletTransactionsLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      const response = await api.get(`/utilities/my-statement${params.toString() ? `?${params.toString()}` : ""}`);
      const statement = response.data?.statement || {};
      setUtilityWalletTransactions(getVisibleUtilitySettlementRows(statement.rows));
      setUtilityWalletStatementSummary(statement.summary || null);
      setUtilityWalletStartDate(startDate || "");
      setUtilityWalletEndDate(endDate || "");
      if (statement.balance != null || statement.walletBalance != null) {
        setUtilityWallet((prev) => ({
          ...(prev || {}),
          balance: getUtilityWalletBalance(statement),
          walletBalance: getUtilityWalletBalance(statement),
        }));
      }
    } catch (error) {
      console.error("Error fetching UT-Bills wallet transactions:", error);
      alert("Error fetching UT-Bills statements: " + (error.response?.data?.error || error.message));
    } finally {
      setUtilityWalletTransactionsLoading(false);
    }
  };

  const fetchFilteredUtilityWalletTransactions = () => {
    fetchUtilityWalletTransactions(utilityWalletStartDate, utilityWalletEndDate);
  };

  const clearUtilityWalletDateFilters = () => {
    setUtilityWalletStartDate("");
    setUtilityWalletEndDate("");
    fetchUtilityWalletTransactions();
  };
  // Fetch all payments and filter to a specific property (manager view)
  const fetchPropertyPayments = async (propertyId) => {
    try {
      setPropertyPaymentsLoading(true);
      // Manager-scoped payments only
      const response = await api.get("/frp/payments?scope=manager");
      const allPayments = response.data.payments || [];
      const normalizeId = (val) => {
        const v = (val && (val._id || val.id || val)) || null;
        return v ? String(v) : null;
      };
      const targetPropertyId = normalizeId(propertyId);
      let filtered = allPayments.filter((payment) => {
        const paymentPropertyId = normalizeId(payment?.unit?.property);
        return paymentPropertyId && paymentPropertyId === targetPropertyId;
      });
      // Fallback: if property is not populated, filter by unit IDs currently loaded for this property
      if (!filtered.length && units && units.length) {
        const unitIds = units.map(u => normalizeId(u._id));
        filtered = allPayments.filter((payment) => {
          const paymentUnitId = normalizeId(payment?.unit);
          return paymentUnitId && unitIds.includes(paymentUnitId);
        });
      }
      setPropertyPayments(filtered);
    } catch (error) {
      console.error("Error fetching property payments:", error);
      alert("Error fetching property statements: " + (error.response?.data?.error || error.message));
    } finally {
      setPropertyPaymentsLoading(false);
    }
  };

  // Fetch transactions for a specific management company (manager view)
  const fetchCompanyTransactions = async (companyId) => {
    try {
      setCompanyTransactionsLoading(true);
      // Manager-scoped payments only
      const response = await api.get("/frp/payments?scope=manager");
      const allPayments = response.data.payments || [];
      const normalizeId = (val) => {
        const v = (val && (val._id || val.id || val)) || null;
        return v ? String(v) : null;
      };
      // Collect property IDs that belong to this company
      const propertyIds = properties
        .filter(p => {
          const mcId = normalizeId(p.managementCompany);
          return mcId && mcId === normalizeId(companyId);
        })
        .map(p => normalizeId(p._id));
      // Filter payments to those from units whose property is in propertyIds
      let filtered = allPayments.filter((payment) => {
        const paymentPropertyId = normalizeId(payment?.unit?.property);
        return paymentPropertyId && propertyIds.includes(paymentPropertyId);
      });
      setCompanyTransactions(filtered);
    } catch (error) {
      console.error("Error fetching company transactions:", error);
      alert("Error fetching company transactions: " + (error.response?.data?.error || error.message));
    } finally {
      setCompanyTransactionsLoading(false);
    }
  };

  // Fetch transactions across ALL management companies (manager role level)
  const fetchManagementTransactions = async () => {
    try {
      setManagementTransactionsLoading(true);
      // Manager-scoped payments only
      const response = await api.get("/frp/payments?scope=manager");
      const allPayments = response.data.payments || [];
      const normalizeId = (val) => {
        const v = (val && (val._id || val.id || val)) || null;
        return v ? String(v) : null;
      };

      // Build a map of propertyId -> companyId for quick lookup
      const propertyIdToCompanyId = new Map();
      properties.forEach(p => {
        const propId = normalizeId(p._id);
        const compId = normalizeId(p.managementCompany);
        if (propId && compId) propertyIdToCompanyId.set(propId, compId);
      });

      // Build a map of companyId -> companyName
      const companyIdToName = new Map();
      managementCompanies.forEach(c => {
        const id = normalizeId(c._id);
        if (id) companyIdToName.set(id, c.companyName || "Company");
      });

      // Attach companyId/companyName and payer to each payment where possible
      const enriched = allPayments.map(p => {
        const paymentPropertyId = normalizeId(p?.unit?.property);
        const compId = paymentPropertyId ? propertyIdToCompanyId.get(paymentPropertyId) : null;
        const compName = compId ? companyIdToName.get(compId) : null;
        // Prefer a proper name; avoid exposing emails
        let payerName = p?.tenant?.name || null;
        if (!payerName) {
          if (typeof p?.paidBy === 'string' && !p.paidBy.includes('@')) payerName = p.paidBy;
          else if (typeof p?.paidBy?.name === 'string') payerName = p.paidBy.name;
        }
        return { ...p, _companyId: compId || null, _companyName: compName || null, _payerName: payerName };
      });

      // Keep only payments that we can associate to a company the manager controls
      const filtered = enriched.filter(p => p._companyId);
      // Sort by date desc (newest first)
      filtered.sort((a, b) => new Date(b.date) - new Date(a.date));

      setManagementTransactions(filtered);
    } catch (error) {
      console.error("Error fetching management-level transactions:", error);
      alert("Error fetching management-level transactions: " + (error.response?.data?.error || error.message));
    } finally {
      setManagementTransactionsLoading(false);
    }
  };
  // Fetch transactions across ALL properties for current landlord (landlord role level)
  const fetchLandlordTransactions = async () => {
    try {
      setLandlordTransactionsLoading(true);
      // Landlord-scoped payments only
      const response = await api.get("/frp/payments?scope=landlord");
      const allPayments = response.data.payments || [];
      const normalizeId = (val) => {
        const v = (val && (val._id || val.id || val)) || null;
        return v ? String(v) : null;
      };

      if (!user?._id) {
        setLandlordTransactions([]);
        return;
      }

      // Properties owned by this landlord
      const landlordId = normalizeId(user._id);
      const propertyIdToName = new Map();
      const propertyIdToManagerName = new Map();
      const propertyIdToCompanyName = new Map();
      const landlordPropertyIds = properties.filter(p => normalizeId(p?.landlord) === landlordId || normalizeId(p?.landlord?._id) === landlordId)
        .map(p => {
          const pid = normalizeId(p._id);
          if (pid) propertyIdToName.set(pid, p.name || 'Property');
          if (pid) {
            const managerName = (p?.manager && p.manager.name) ? p.manager.name
              : (p?.managementCompany && p.managementCompany.user_id && p.managementCompany.user_id.name) ? p.managementCompany.user_id.name
                : null;
            const companyName = (p?.managementCompany && p.managementCompany.companyName)
              ? p.managementCompany.companyName
              : (p?.managementCompany && p.managementCompany.user_id && p.managementCompany.user_id.companyName)
                ? p.managementCompany.user_id.companyName
                : null;
            if (managerName) propertyIdToManagerName.set(pid, managerName);
            if (companyName) propertyIdToCompanyName.set(pid, companyName);
          }
          return pid;
        })
        .filter(Boolean);

      // Enrich payments that belong to landlord properties
      let enriched = allPayments.map(p => {
        const paymentPropertyId = normalizeId(p?.unit?.property);
        const propertyName = paymentPropertyId ? propertyIdToName.get(paymentPropertyId) : null;
        const managerName = paymentPropertyId ? propertyIdToManagerName.get(paymentPropertyId) : null;
        const directCompanyName = p?.unit?.property?.managementCompany?.companyName || null;
        const companyName = directCompanyName || (paymentPropertyId ? (propertyIdToCompanyName.get(paymentPropertyId) || managerName || null) : null);
        // Prefer a proper name; avoid exposing emails
        let payerName = p?.tenant?.name || null;
        if (!payerName) {
          if (typeof p?.paidBy === 'string' && !p.paidBy.includes('@')) payerName = p.paidBy;
          else if (typeof p?.paidBy?.name === 'string') payerName = p.paidBy.name;
        }
        return { ...p, _propertyId: paymentPropertyId || null, _propertyName: propertyName || null, _payerName: payerName, _managerName: managerName || null, _companyName: companyName || null };
      }).filter(p => p._propertyId && landlordPropertyIds.includes(p._propertyId));
      // Fallback: if no properties matched via properties list, try using myUnits to infer landlord's properties
      if (!enriched.length && Array.isArray(myUnits) && myUnits.length) {
        const muPropertyIdToName = new Map();
        const muPropertyIdToManager = new Map();
        const muPropertyIdToCompany = new Map();
        myUnits.forEach(u => {
          const pid = normalizeId(u?.property?._id || u?.property);
          if (pid) {
            muPropertyIdToName.set(pid, u?.property?.name || 'Property');
            // try to resolve manager via properties list
            const propObj = properties.find(pp => normalizeId(pp?._id) === pid);
            const managerName = (propObj?.manager && propObj.manager.name) ? propObj.manager.name
              : (propObj?.managementCompany && propObj.managementCompany.user_id && propObj.managementCompany.user_id.name) ? propObj.managementCompany.user_id.name
                : null;
            const companyName = (propObj?.managementCompany && propObj.managementCompany.companyName)
              ? propObj.managementCompany.companyName
              : (propObj?.managementCompany && propObj.managementCompany.user_id && propObj.managementCompany.user_id.companyName)
                ? propObj.managementCompany.user_id.companyName
                : null;
            if (managerName) muPropertyIdToManager.set(pid, managerName);
            if (companyName) muPropertyIdToCompany.set(pid, companyName);
          }
        });
        const muPropertyIds = Array.from(muPropertyIdToName.keys());
        enriched = allPayments.map(p => {
          const pid = normalizeId(p?.unit?.property);
          const pname = pid ? muPropertyIdToName.get(pid) : null;
          const mname = pid ? (muPropertyIdToManager.get(pid) || propertyIdToManagerName.get(pid) || null) : null;
          const directCompanyName = p?.unit?.property?.managementCompany?.companyName || null;
          const cname = directCompanyName || (pid ? (muPropertyIdToCompany.get(pid) || propertyIdToCompanyName.get(pid) || mname || null) : null);
          let payerName = p?.tenant?.name || null;
          if (!payerName) {
            if (typeof p?.paidBy === 'string' && !p.paidBy.includes('@')) payerName = p.paidBy;
            else if (typeof p?.paidBy?.name === 'string') payerName = p.paidBy.name;
          }
          return { ...p, _propertyId: pid || null, _propertyName: pname || null, _payerName: payerName, _managerName: mname, _companyName: cname };
        }).filter(p => p._propertyId && muPropertyIds.includes(p._propertyId));
      }

      // Sort by date desc
      enriched.sort((a, b) => new Date(b.date) - new Date(a.date));
      setLandlordTransactions(enriched);
    } catch (error) {
      console.error("Error fetching landlord-level transactions:", error);
      alert("Error fetching landlord-level transactions: " + (error.response?.data?.error || error.message));
    } finally {
      setLandlordTransactionsLoading(false);
    }
  };

  // Export Company Transactions to PDF
  const downloadCompanyTransactionsAsPDF = () => {
    if (!selectedCompany) {
      alert("Open a company first");
      return;
    }
    // Filter by current date range
    const rows = filterRowsByDateRange(
      companyTransactions || [],
      companyStartDate,
      companyEndDate
    );
    if (!rows.length) {
      alert("No transaction data to export");
      return;
    }

    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 15;
    const usableW = pageW - margin * 2;
    const topY = 20;
    const bottomMargin = 20;

    // Column layout
    const col = {
      date: 24,
      description: Math.max(usableW * 0.34, 60),
      amount: 24,
      rl: 22,
      mgmt: 26,
      ll: 24,
    };
    // Adjust description to fill the remaining
    const fixedSum = col.date + col.amount + col.rl + col.mgmt + col.ll;
    col.description = Math.max(usableW - fixedSum, 60);

    const x = {
      date: margin,
      description: margin + col.date,
      amount: margin + col.date + col.description,
      rl: margin + col.date + col.description + col.amount,
      mgmt: margin + col.date + col.description + col.amount + col.rl,
      ll: margin + col.date + col.description + col.amount + col.rl + col.mgmt,
    };

    // Header
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Company Transactions', margin, topY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    let infoY = topY + 12;
    doc.text(`Company: ${selectedCompany.companyName}`, margin, infoY); infoY += 8;
    if (companyStartDate || companyEndDate) {
      const dateRange = companyStartDate && companyEndDate
        ? `${companyStartDate} to ${companyEndDate}`
        : companyStartDate
          ? `From ${companyStartDate}`
          : `Until ${companyEndDate}`;
      doc.text(`Date Range: ${dateRange}`, margin, infoY); infoY += 8;
    }

    // Totals
    const paymentTotals = sumPaymentRows(rows);
    const totals = {
      amount: paymentTotals.amount,
      rl: paymentTotals.platform,
      mgmt: paymentTotals.manager,
      ll: paymentTotals.landlord,
    };

    doc.text(`Gross: ${formatMoney(totals.amount)}  •  RL: ${formatMoney(totals.rl)}  •  Mgmt: ${formatMoney(totals.mgmt)}  •  LL: ${formatMoney(totals.ll)}`, margin, infoY); infoY += 12;

    // Table header
    const drawHeader = (y) => {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Date', x.date, y);
      doc.text('Description', x.description, y);
      doc.text('Amount', x.amount, y, { align: 'left' });
      doc.text('RL (1%)', x.rl, y, { align: 'left' });
      doc.text('Mngt Share', x.mgmt, y, { align: 'left' });
      doc.text('LL Share', x.ll, y, { align: 'left' });
      doc.line(margin, y + 2, margin + usableW, y + 2);
    };

    let yPos = Math.max(infoY, 80);
    drawHeader(yPos);
    yPos += 8;

    // First row: Totals
    doc.setFont('helvetica', 'normal');
    const totalLine = `Gross across selected period`;
    const descTotalLines = doc.splitTextToSize(totalLine, col.description - 2);
    const rowHeightTotal = Math.max(6, descTotalLines.length * 6) + 2;
    doc.text('Total', x.date, yPos + 6 - 2);
    doc.text(descTotalLines, x.description, yPos + 6 - 2);
    doc.text(formatMoney(totals.amount), x.amount, yPos + 6 - 2);
    doc.text(formatMoney(totals.rl), x.rl, yPos + 6 - 2);
    doc.text(formatMoney(totals.mgmt), x.mgmt, yPos + 6 - 2);
    doc.text(formatMoney(totals.ll), x.ll, yPos + 6 - 2);
    yPos += rowHeightTotal;

    // Rows
    rows.forEach((p) => {
      const dateStr = formatDateLong(p.date);
      const split = getPaymentSplit(p);
      const desc = p.description || `Payment - ${p.unit?.label || ''}`;
      const descLines = doc.splitTextToSize(desc, col.description - 2);
      const rowH = Math.max(6, descLines.length * 6) + 2;
      if (yPos + rowH > pageH - bottomMargin) {
        doc.addPage();
        yPos = topY;
        drawHeader(yPos);
        yPos += 8;
      }
      doc.text(dateStr, x.date, yPos + 6 - 2);
      doc.text(descLines, x.description, yPos + 6 - 2);
      doc.text(formatMoney(split.amount), x.amount, yPos + 6 - 2);
      doc.text(formatMoney(split.platform), x.rl, yPos + 6 - 2);
      doc.text(formatMoney(split.manager), x.mgmt, yPos + 6 - 2);
      doc.text(formatMoney(split.landlord), x.ll, yPos + 6 - 2);
      yPos += rowH;
    });

    // Footer
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    const now = new Date();
    doc.text(
      `Generated on ${formatDateLong(now)} at ${formatTimeOnly(now)}`,
      margin,
      pageH - 14
    );
    doc.text('This document is read-only and cannot be modified.', margin, pageH - 8);

    const safe = (s) => String(s).replace(/[/\\?%*:|"<>]/g, '-');
    const todayStr = formatDateInputValue(new Date());
    const fileName = `Company_Transactions_${safe(selectedCompany.companyName)}_${todayStr}.pdf`;
    doc.save(fileName);
  };

  const fetchMonthlySummary = async (month = selectedSummaryMonth, year = selectedSummaryYear) => {
    if (!canViewMonthlySummary(activeRole)) {
      setMonthlySummary(null);
      return;
    }
    try {
      setMonthlySummaryLoading(true);
      const response = await api.get(`/frp/monthly-summary?month=${month}&year=${year}`);
      setMonthlySummary(response.data.summary);
    } catch (error) {
      console.error("Error fetching monthly summary:", error);
      const errorMessage = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Network error occurred";
      alert("Error fetching monthly summary: " + errorMessage);
    } finally {
      setMonthlySummaryLoading(false);
    }
  };

  const addFunds = async (e) => {
    e.preventDefault();
    try {
      const amount = parseFloat(addFundsAmount);
      if (amount <= 0) {
        alert("Please enter a valid amount");
        return;
      }

      await api.post("/wallet/add-funds", { amount });
      setAddFundsAmount("");
      setShowAddFundsForm(false);
      fetchWallet();

      // Refresh wallet transactions if modal is open
      if (showWalletTransactionsModal) {
        fetchWalletTransactions();
      }

      alert(`${formatMoney(amount)} added to your wallet successfully!`);
    } catch (error) {
      alert("Error adding funds: " + error.response?.data?.error);
    }
  };

  const addUtilityFunds = async (e) => {
    e.preventDefault();
    try {
      const amount = parseFloat(addUtilityFundsAmount);
      if (isNaN(amount) || amount <= 0) {
        alert("Please enter a valid amount");
        return;
      }

      const response = await api.post("/utilities/add-funds", { amount });
      setAddUtilityFundsAmount("");
      setShowAddUtilityFundsForm(false);
      await fetchUtilityWallet();

      if (showUtilityWalletTransactionsModal) {
        fetchUtilityWalletTransactions(utilityWalletStartDate, utilityWalletEndDate);
      }
      if (showWaterMeterModal) {
        loadUtilityStatement(selectedUtilityType);
      }

      const appliedAmount = Number(response.data?.utilitySettlement?.appliedAmount || 0);
      const processed = Number(response.data?.utilitySettlement?.paymentsProcessed || 0);
      const appliedText = processed > 0
        ? ` ${formatMoney(appliedAmount)} was applied to open utility bills.`
        : "";
      alert(`${formatMoney(amount)} added to your UT-Bills Wallet successfully.${appliedText}`);
    } catch (error) {
      alert("Error adding funds to UT-Bills Wallet: " + (error.response?.data?.error || error.message));
    }
  };

  // Property Creation - COMPLEX CREATION WITH LANDLORD AND UNITS
  const createProperty = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      // Validate form data
      if (!complexPropertyForm.numberOfUnits || parseInt(complexPropertyForm.numberOfUnits) <= 0) {
        setFormError('Please enter a valid number of units (greater than 0)');
        setFormLoading(false);
        return;
      }

      // Create the property with management company and basic details
      const propertyData = {
        name: complexPropertyForm.propertyName,
        // Send structured fields; backend will compose address for backward compatibility
        cityTown: complexPropertyForm.cityTown,
        location: complexPropertyForm.location,
        area: complexPropertyForm.area,
        managementCompanyId: complexPropertyForm.managementCompanyId,
        phone: complexPropertyForm.phoneNumber,
        landlordId: complexPropertyForm.landlordId,
        managementPercentage: complexPropertyForm.managementPercentage,
        waterUnitCharge: complexPropertyForm.waterUnitCharge,
        waterUnitsMeasure: complexPropertyForm.waterUnitsMeasure,
        trashMonthlyCharge: complexPropertyForm.trashMonthlyCharge
      };

      const propertyResponse = await api.post('/frp/properties', propertyData);

      const createdProperty = propertyResponse.data.property || propertyResponse.data;
      const propertyId = createdProperty._id || createdProperty.id;

      if (!propertyId) {
        throw new Error('Property ID not found in response');
      }

      // Create units for the property automatically
      const numberOfUnits = parseInt(complexPropertyForm.numberOfUnits);

      if (numberOfUnits > 0) {
        for (let i = 1; i <= numberOfUnits; i++) {
          try {
            await api.post(`/frp/properties/${propertyId}/units`, {
              label: `# ${i}`,
              rentAmount: 0
            });
          } catch (unitError) {
            console.error('Error creating unit:', i, unitError);
            // Continue with other units even if one fails
          }
        }
      }

      setShowPropertyForm(false);
      setComplexPropertyForm({
        propertyName: '',
        phoneNumber: '',
        landlordId: '',
        cityTown: '',
        location: '',
        area: '',
        managementPercentage: 10,
        waterUnitCharge: '',
        waterUnitsMeasure: '1',
        trashMonthlyCharge: '',
        managementCompanyId: '',
        numberOfUnits: ''
      });
      setSelectedLandlord(null);
      setLandlordSearch('');
      alert("Property created successfully with units!");

      // Refresh data
      fetchProperties();
      fetchManagementCompanies();
    } catch (err) {
      console.error('Property creation error:', err);
      setFormError(err.response?.data?.error || err.message || 'Network error. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };
  // Management Company Functions - SIMPLE CREATION
  const createManagement = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      // eslint-disable-next-line no-unused-vars
      const response = await api.post('/management-company/create', managementForm);

      setShowManagementForm(false);
      setManagementForm({
        companyName: '',
        telephoneNumber: '',
        headOfficeLocation: '',
        yourPositionInCompany: '',
        ownerNames: ''
      });
      alert("Management company created successfully!");
      fetchManagementCompanies();
      // Refresh user data to reflect the role change to "M" (Manager)
      fetchUserData();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Network error. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  // Update Management Company Function
  const updateManagement = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const response = await api.put(`/management-company/${selectedManagementCompany._id}`, managementForm);

      // Update the selected company with the new data to reflect changes immediately
      const updatedCompany = response.data.managementCompany;
      setSelectedCompany(updatedCompany);

      setShowUpdateManagementForm(false);
      setSelectedManagementCompany(null);
      setManagementForm({
        companyName: '',
        telephoneNumber: '',
        headOfficeLocation: '',
        yourPositionInCompany: '',
        ownerNames: ''
      });
      alert("Management company updated successfully!");
      fetchManagementCompanies(); // Refresh the list for when user goes back
    } catch (err) {
      setFormError(err.response?.data?.error || 'Network error. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  // Function to handle opening update form with pre-filled data
  const handleUpdateCompany = (company) => {
    setSelectedManagementCompany(company);
    setManagementForm({
      companyName: company.companyName,
      telephoneNumber: company.telephoneNumber,
      headOfficeLocation: company.headOfficeLocation,
      yourPositionInCompany: company.yourPositionInCompany,
      ownerNames: company.ownerNames
    });
    setShowUpdateManagementForm(true);
  };

  // Update Property Function
  const updateProperty = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const response = await api.put(`/frp/properties/${selectedProperty._id}`, updatePropertyForm);

      // Update the selected property with the new data to reflect changes immediately
      const updatedProperty = response.data.property;
      setSelectedProperty(updatedProperty);

      setShowUpdatePropertyForm(false);
      setUpdatePropertyForm({
        name: '',
        cityTown: '',
        location: '',
        area: '',
        managementPercentage: 10,
        waterUnitCharge: '',
        waterUnitsMeasure: '',
        trashMonthlyCharge: ''
      });
      alert("Property updated successfully!");
      fetchProperties(); // Refresh the list for when user goes back
    } catch (err) {
      setFormError(err.response?.data?.error || 'Network error. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  // Function to handle opening property update form with pre-filled data
  const handleUpdateProperty = (property) => {
    setUpdatePropertyForm({
      name: property.name,
      cityTown: property.cityTown || "",
      location: property.location || "",
      area: property.area || "",
      managementPercentage: property.managementPercentage || 10,
      waterUnitCharge: property.waterUnitCharge ?? "",
      waterUnitsMeasure: property.waterUnitsMeasure ?? "",
      trashMonthlyCharge: property.trashMonthlyCharge ?? ""
    });
    setShowUpdatePropertyForm(true);
  };
  const handlePropertyLandlordSearch = async (value) => {
    setLandlordSearch(value);
    if (!value) {
      setLandlordResults([]);
      setShowLandlordDropdown(false);
      return;
    }

    try {
      const response = await api.get(`/auth/search-users?idNumber=${value}`);
      setLandlordResults(response.data.users || []);
      setShowLandlordDropdown(true);
    } catch (err) {
      setLandlordResults([]);
      setShowLandlordDropdown(false);
    }
  };

  const selectPropertyLandlord = (landlord) => {
    setSelectedLandlord(landlord);
    setComplexPropertyForm({ ...complexPropertyForm, landlordId: landlord._id });
    setLandlordSearch(landlord.idNumber);
    setShowLandlordDropdown(false);
  };

  const createUnit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/frp/properties/${selectedProperty._id}/units`, unitForm);
      setUnitForm({ label: "", rentAmount: "", type: "", images: [] });
      setShowCreateUnitImages(false);
      setShowUnitForm(false);
      fetchUnits(selectedProperty._id);
      alert("Unit created successfully!");
    } catch (error) {
      alert("Error creating unit: " + error.response?.data?.error);
    }
  };
  const updateUnit = async (e) => {
    e.preventDefault();
    try {
      // Automatically set today's date for the fromDate field (Africa/Nairobi time)
      const today = formatDateInputValue(new Date());
      const unitData = {
        ...updateUnitForm,
        fromDate: today
      };

      await api.put(`/frp/units/${selectedUnit._id}`, unitData);
      setShowUpdateUnitForm(false);
      setShowEditUnitImages(false);
      setUpdateUnitForm({
        label: "",
        type: "",
        deposit: "",
        rentAmount: "",
        lateFee: "",
        fromDate: "",
        images: []
      });
      fetchUnits(selectedProperty._id);
      alert("Unit updated successfully!");
    } catch (error) {
      alert("Error updating unit: " + error.response?.data?.error);
    }
  };

  const openUpdateUnitForm = (unit) => {
    if (!unit) return;

    const formatDate = (date) => {
      if (!date) return "";
      return formatDateInputValue(date);
    };

    setUpdateUnitForm({
      label: unit.label,
      type: unit.type || "",
      deposit: unit.deposit ?? "",
      rentAmount: unit.rentAmount,
      lateFee: unit.lateFee ?? "",
      fromDate: formatDate(unit.fromDate),
      images: Array.isArray(unit.images) ? unit.images : []
    });
    setShowEditUnitImages(false);
    setShowUpdateUnitForm(true);
  };

  const resetAssignFormState = () => {
    if (landlordSearchDebounceRef.current) {
      clearTimeout(landlordSearchDebounceRef.current);
      landlordSearchDebounceRef.current = null;
    }
    if (tenantSearchDebounceRef.current) {
      clearTimeout(tenantSearchDebounceRef.current);
      tenantSearchDebounceRef.current = null;
    }
    landlordSearchRequestSeqRef.current += 1;
    tenantSearchRequestSeqRef.current += 1;
    setSelectedUserId("");
    setMoveInDate("");
    setLandlordSearch("");
    setTenantSearch("");
    setSelectedLandlord(null);
    setSelectedTenant(null);
    setLandlordResults([]);
    setTenantResults([]);
    setShowLandlordDropdown(false);
    setShowTenantDropdown(false);
    setCreateTenantAccountMode(false);
    setCreatingTenantAccount(false);
    setNewTenantAccountForm({
      firstName: "",
      lastName: "",
      phone: "",
      idNumber: "",
      confirmIdNumber: "",
      pin: "",
      confirmPin: "",
    });
    setTenantAccountVerificationModal({
      open: false,
      canProceed: false,
      allowKraOverride: false,
      issues: [],
      existingUser: null,
      kraStatus: null,
      submitPayload: null,
    });
  };

  const closeAssignmentActionModal = () => {
    if (isAssigningUser) return;
    setAssignmentActionResult(null);
  };

  const retryAssignmentAction = () => {
    setAssignmentActionResult(null);
    setShowAssignForm(true);
  };

  const finishAssignmentAction = () => {
    setAssignmentActionResult(null);
  };

  const openCreateTenantAccountMode = () => {
    if (tenantSearchDebounceRef.current) {
      clearTimeout(tenantSearchDebounceRef.current);
      tenantSearchDebounceRef.current = null;
    }
    tenantSearchRequestSeqRef.current += 1;
    const seededIdNumber = String(tenantSearch || "").trim();
    setCreateTenantAccountMode(true);
    setShowTenantDropdown(false);
    setTenantResults([]);
    setSelectedTenant(null);
    setSelectedUserId("");
    setNewTenantAccountForm((prev) => ({
      ...prev,
      idNumber: prev.idNumber || seededIdNumber,
    }));
  };

  const closeCreateTenantAccountMode = () => {
    if (tenantSearchDebounceRef.current) {
      clearTimeout(tenantSearchDebounceRef.current);
      tenantSearchDebounceRef.current = null;
    }
    tenantSearchRequestSeqRef.current += 1;
    setCreateTenantAccountMode(false);
    setCreatingTenantAccount(false);
    setNewTenantAccountForm({
      firstName: "",
      lastName: "",
      phone: "",
      idNumber: "",
      confirmIdNumber: "",
      pin: "",
      confirmPin: "",
    });
    setTenantAccountVerificationModal({
      open: false,
      canProceed: false,
      allowKraOverride: false,
      issues: [],
      existingUser: null,
      kraStatus: null,
      submitPayload: null,
    });
  };

  const closeTenantAccountVerificationModal = () => {
    if (creatingTenantAccount) return;
    setTenantAccountVerificationModal({
      open: false,
      canProceed: false,
      allowKraOverride: false,
      issues: [],
      existingUser: null,
      kraStatus: null,
      submitPayload: null,
    });
  };

  const selectExistingTenantAccount = (existingUser, fallbackIdNumber = "") => {
    if (!existingUser?._id) return;
    setSelectedTenant(existingUser);
    setSelectedUserId(existingUser._id);
    setTenantSearch(existingUser.idNumber || fallbackIdNumber);
    setShowTenantDropdown(false);
    setTenantResults([]);
    setCreateTenantAccountMode(false);
    fetchUsersPayScores([existingUser]);
  };

  const createTenantAccountFromPayload = async (submitPayload, options = {}) => {
    const newTenantIdNumber = String(submitPayload?.idNumber || "").trim();
    const allowUnverifiedKra = options?.allowUnverifiedKra === true;

    setCreatingTenantAccount(true);
    try {
      const createResponse = await api.post("/auth/management/create-tenant-account", {
        ...submitPayload,
        allowUnverifiedKra,
      });

      const createdTenantAccount = createResponse?.data || null;
      const createdTenant = createdTenantAccount?.tenant || null;
      const createdTenantId = createdTenant?._id || "";

      if (!createdTenantId) {
        throw new Error("Tenant account created, but no tenant ID was returned.");
      }

      setSelectedTenant(createdTenant);
      setSelectedUserId(createdTenantId);
      setTenantSearch(createdTenant?.idNumber || newTenantIdNumber);
      setShowTenantDropdown(false);
      setTenantResults([]);
      setCreateTenantAccountMode(false);
      fetchUsersPayScores([createdTenant]);
      closeTenantAccountVerificationModal();
      alert("Tenant account created successfully. You can now assign the tenant to this unit.");
    } catch (createError) {
      const existingUser = createError?.response?.data?.existingUser;
      if (createError?.response?.status === 409 && existingUser?._id) {
        selectExistingTenantAccount(existingUser, newTenantIdNumber);
        closeTenantAccountVerificationModal();
        alert("A user with this ID already exists. Selected the existing account. You can now assign.");
      } else {
        alert(createError?.response?.data?.error || createError.message || "Failed to create tenant account.");
      }
    } finally {
      setCreatingTenantAccount(false);
    }
  };

  const registerTenantAccount = async () => {
    const newTenantFirstName = String(newTenantAccountForm.firstName || "").trim();
    const newTenantLastName = String(newTenantAccountForm.lastName || "").trim();
    const newTenantName = `${newTenantFirstName} ${newTenantLastName}`.trim();
    const newTenantPhone = String(newTenantAccountForm.phone || "").trim();
    const newTenantIdNumber = String(newTenantAccountForm.idNumber || "").trim();
    const newTenantConfirmIdNumber = String(newTenantAccountForm.confirmIdNumber || "").trim();
    const newTenantPin = String(newTenantAccountForm.pin || "").trim();
    const newTenantConfirmPin = String(newTenantAccountForm.confirmPin || "").trim();

    if (!newTenantName || !newTenantPhone || !newTenantIdNumber) {
      alert("Please provide tenant first name, last name, phone, and ID number.");
      return;
    }

    if (newTenantIdNumber !== newTenantConfirmIdNumber) {
      alert("ID numbers do not match.");
      return;
    }

    if (newTenantPin !== newTenantConfirmPin) {
      alert("PINs do not match.");
      return;
    }

    if (!/^\d{4}$/.test(newTenantPin)) {
      alert("PIN must be exactly 4 digits.");
      return;
    }

    const submitPayload = {
      name: newTenantName,
      phone: newTenantPhone,
      idNumber: newTenantIdNumber,
      pin: newTenantPin,
    };

    setCreatingTenantAccount(true);
    try {
      const previewResponse = await api.post("/auth/management/create-tenant-account/preview", submitPayload);
      setTenantAccountVerificationModal({
        open: true,
        canProceed: !!previewResponse?.data?.canProceed,
        allowKraOverride: !!previewResponse?.data?.allowKraOverride,
        issues: Array.isArray(previewResponse?.data?.issues) ? previewResponse.data.issues : [],
        existingUser: previewResponse?.data?.existingUser || null,
        kraStatus: previewResponse?.data?.kraStatus || null,
        submitPayload,
      });
    } catch (previewError) {
      alert(previewError?.response?.data?.error || previewError.message || "Failed to preview tenant account verification.");
    } finally {
      setCreatingTenantAccount(false);
    }
  };

  const handleConfirmTenantAccountRegistration = async () => {
    if (!tenantAccountVerificationModal?.submitPayload) {
      return;
    }
    await createTenantAccountFromPayload(tenantAccountVerificationModal.submitPayload);
  };

  const handleConfirmTenantAccountRegistrationWithoutKra = async () => {
    if (!tenantAccountVerificationModal?.submitPayload) {
      return;
    }
    await createTenantAccountFromPayload(tenantAccountVerificationModal.submitPayload, {
      allowUnverifiedKra: true,
    });
  };

  const handleUseExistingTenantAccount = () => {
    const existingUser = tenantAccountVerificationModal?.existingUser;
    const fallbackIdNumber = String(tenantAccountVerificationModal?.submitPayload?.idNumber || "").trim();
    if (!existingUser?._id) {
      return;
    }
    selectExistingTenantAccount(existingUser, fallbackIdNumber);
    closeTenantAccountVerificationModal();
    alert("A user with this ID already exists. Selected the existing account. You can now assign.");
  };

  const assignUser = async (e) => {
    e.preventDefault();
    if (isAssigningUser) return;

    const assigningTenant = assignmentType === "tenant";
    const assigneeTypeLabel = assigningTenant ? "Tenant" : "Landlord";
    const actionLabel = `${assigneeTypeLabel} Assignment`;

    if (!selectedUnit?._id) {
      alert("Select a unit first.");
      return;
    }

    try {
      const endpoint = assignmentType === "landlord"
        ? `/frp/units/${selectedUnit._id}/assign-landlord`
        : `/frp/units/${selectedUnit._id}/assign-tenant`;

      let tenantIdForAssignment = selectedUserId;
      if (assigningTenant) {
        if (!tenantIdForAssignment) {
          alert("Select or register a tenant first, then assign.");
          return;
        }

        if (!moveInDate) {
          alert("Please select move in date.");
          return;
        }
      } else if (!selectedUserId) {
        alert("Please select a landlord before assigning.");
        return;
      }

      const data = assignmentType === "landlord"
        ? { landlordId: selectedUserId }
        : { tenantId: tenantIdForAssignment, moveInDate: moveInDate };
      const selectedAssigneeName = assigningTenant
        ? (selectedTenant?.name || tenantSearch || "Selected tenant")
        : (selectedLandlord?.name || landlordSearch || "Selected landlord");
      const selectedUnitLabel = selectedUnit?.label || "Unit";

      setShowAssignForm(false);
      setAssignmentActionResult({
        status: "loading",
        title: `Assigning ${assigneeTypeLabel}`,
        statusLabel: `Assigning ${assigneeTypeLabel}`,
        message: "Please wait. Do not close this window.",
      });

      // For tenant assignments, check if wallet meets unit requirement (deposit + monthly rent)
      if (assigningTenant) {
        if (!tenantIdForAssignment) {
          setAssignmentActionResult({
            status: "error",
            title: `${actionLabel} Failed`,
            statusLabel: `${actionLabel} Failed`,
            message: "Please select a tenant before assigning.",
          });
          return;
        }

        try {
          const walletResponse = await api.get(`/wallet/user-wallet/${tenantIdForAssignment}`);
          const tenantWallet = walletResponse.data.wallet;
          const totals = getTenantAssignmentWalletTotals(selectedUnit);
          const requiredAmount = totals.total;

          if (!tenantWallet || tenantWallet.balance < requiredAmount) {
            const shortfall = requiredAmount - (tenantWallet?.balance || 0);
            setAssignmentActionResult({
              status: "error",
              title: `${actionLabel} Failed`,
              statusLabel: `${actionLabel} Failed`,
              message: "Insufficient funds in tenant wallet.",
              details: [
                { label: "Required", value: formatMoney(requiredAmount) },
                { label: "Current balance", value: tenantWallet?.balance != null ? formatMoney(tenantWallet.balance) : "0.00" },
                { label: "Shortfall", value: formatMoney(shortfall), tone: "error" },
              ],
            });
            return;
          }
        } catch (walletError) {
          const walletErrorMessage = walletError?.response?.data?.error || "Error checking tenant wallet balance. Please try again.";
          setAssignmentActionResult({
            status: "error",
            title: `${actionLabel} Failed`,
            statusLabel: `${actionLabel} Failed`,
            message: walletErrorMessage,
          });
          return;
        }
      }

      const assignResponse = await api.put(endpoint, data);
      resetAssignFormState();
      // Update the currently selected unit in the modal with the fresh data from the server
      if (assignResponse?.data?.unit) {
        setSelectedUnit(assignResponse.data.unit);
      }
      // Refresh units list so status/counts reflect immediately in lists
      await fetchUnits(selectedProperty._id);
      setAssignmentActionResult({
        status: "success",
        title: `${actionLabel} Successful`,
        statusLabel: `${actionLabel} Successful`,
        message: `${assigneeTypeLabel} assigned successfully.`,
        details: [
          { label: "Unit", value: selectedUnitLabel },
          { label: assigneeTypeLabel, value: selectedAssigneeName },
        ],
      });
    } catch (error) {
      setAssignmentActionResult({
        status: "error",
        title: `${actionLabel} Failed`,
        statusLabel: `${actionLabel} Failed`,
        message: error.response?.data?.error || error.message || `Error assigning ${assignmentType}.`,
      });
    }
  };

  const fetchUsersPayScores = async (usersList = []) => {
    const userIds = [...new Set(
      (Array.isArray(usersList) ? usersList : [])
        .map((entry) => normalizeId(entry?._id))
        .filter(Boolean)
    )];

    const idsToLoad = userIds.filter(
      (id) => !(id in userPayScoreById) && !userPayScoreLoadingById[id]
    );
    if (idsToLoad.length === 0) return;

    setUserPayScoreLoadingById((prev) => {
      const next = { ...prev };
      idsToLoad.forEach((id) => {
        next[id] = true;
      });
      return next;
    });

    try {
      const response = await api.get("/payscore/users", {
        params: { ids: idsToLoad.join(",") },
      });
      const scores = response?.data?.scores || {};

      setUserPayScoreById((prev) => {
        const next = { ...prev };
        idsToLoad.forEach((id) => {
          next[id] = scores[id] || null;
        });
        return next;
      });
    } catch (error) {
      console.error("Error loading user PaySCOREs:", error);
      setUserPayScoreById((prev) => {
        const next = { ...prev };
        idsToLoad.forEach((id) => {
          if (!(id in next)) next[id] = null;
        });
        return next;
      });
    } finally {
      setUserPayScoreLoadingById((prev) => {
        const next = { ...prev };
        idsToLoad.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    }
  };

  const renderUserPayScoreBadges = (userId, mode = "all") => {
    const normalizedUserId = normalizeId(userId);
    if (!normalizedUserId) return null;

    if (userPayScoreLoadingById[normalizedUserId]) {
      return (
        <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-[10px] font-semibold text-gray-600">
          <Icon name="star-fill" size="0.6rem" />
          <span>Loading scores...</span>
        </div>
      );
    }

    const summary = userPayScoreById[normalizedUserId];
    if (!summary) return null;

    if (mode === "frpOnly") {
      const frpScore = summary?.modules?.frp?.score;
      const frpStatusRaw = summary?.modules?.frp?.status || "N/A";
      const frpStatus = frpStatusRaw.charAt(0).toUpperCase() + frpStatusRaw.slice(1).toLowerCase();

      return (
        <div className="mt-1 flex flex-wrap items-center gap-1">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getPayScoreStatusClasses(
              frpStatusRaw
            )}`}
            title={`FRP PaySCORE: ${formatPayScorePercent(frpScore)} (${frpStatus})`}
          >
            <Icon name="star-fill" size="0.6rem" />
            <span>FRP: {formatPayScorePercent(frpScore)} ({frpStatus})</span>
          </span>
        </div>
      );
    }

    const badges = [
      {
        key: "rl",
        label: "RL",
        value: summary?.final?.score,
        status: summary?.final?.status,
      },
      {
        key: "frp",
        label: "FRP",
        value: summary?.modules?.frp?.score,
        status: summary?.modules?.frp?.status,
      },
      {
        key: "groups",
        label: "Groups",
        value: summary?.modules?.group?.score,
        status: summary?.modules?.group?.status,
      },
    ];

    return (
      <div className="mt-1 flex flex-wrap items-center gap-1">
        {badges.map((badge) => (
          <span
            key={badge.key}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getPayScoreStatusClasses(
              badge.status
            )}`}
            title={`${badge.label} PaySCORE: ${formatPayScorePercent(badge.value)} (${badge.status || "N/A"})`}
          >
            <Icon name="star-fill" size="0.6rem" />
            <span>{badge.label}: {formatPayScorePercent(badge.value)}</span>
          </span>
        ))}
      </div>
    );
  };

  // Search functions for landlord and tenant assignment
  const searchLandlords = async (searchTerm, requestSeq) => {
    const normalizedSearchTerm = String(searchTerm || "").trim();
    if (!normalizedSearchTerm || normalizedSearchTerm.length < ASSIGN_SEARCH_MIN_CHARS) {
      setLandlordResults([]);
      setShowLandlordDropdown(false);
      return;
    }

    try {
      // Use search-users to get ALL users, not just landlords
      const response = await api.get(`/auth/search-users?idNumber=${encodeURIComponent(normalizedSearchTerm)}`);
      if (requestSeq !== landlordSearchRequestSeqRef.current) {
        return;
      }
      const usersList = Array.isArray(response?.data?.users) ? response.data.users : [];
      setLandlordResults(usersList);
      setShowLandlordDropdown(true);
      fetchUsersPayScores(usersList);
    } catch (err) {
      if (requestSeq !== landlordSearchRequestSeqRef.current) {
        return;
      }
      console.error('Error searching users for landlord assignment:', err);
      setLandlordResults([]);
      setShowLandlordDropdown(false);
    }
  };

  const searchTenants = async (searchTerm, requestSeq) => {
    const normalizedSearchTerm = String(searchTerm || "").trim();
    if (!normalizedSearchTerm || normalizedSearchTerm.length < ASSIGN_SEARCH_MIN_CHARS) {
      setTenantResults([]);
      setShowTenantDropdown(false);
      return;
    }

    try {
      // Use search-users to get ALL users, not just tenants
      const response = await api.get(`/auth/search-users?idNumber=${encodeURIComponent(normalizedSearchTerm)}`);
      if (requestSeq !== tenantSearchRequestSeqRef.current) {
        return;
      }
      const usersList = Array.isArray(response?.data?.users) ? response.data.users : [];
      setTenantResults(usersList);
      setShowTenantDropdown(true);
      fetchUsersPayScores(usersList);
    } catch (err) {
      if (requestSeq !== tenantSearchRequestSeqRef.current) {
        return;
      }
      console.error('Error searching users for tenant assignment:', err);
      setTenantResults([]);
      setShowTenantDropdown(false);
    }
  };

  const handleLandlordSearch = (value) => {
    setLandlordSearch(value);
    if (landlordSearchDebounceRef.current) {
      clearTimeout(landlordSearchDebounceRef.current);
      landlordSearchDebounceRef.current = null;
    }

    const normalizedValue = String(value || "").trim();
    if (!normalizedValue || normalizedValue.length < ASSIGN_SEARCH_MIN_CHARS) {
      landlordSearchRequestSeqRef.current += 1;
      setLandlordResults([]);
      setShowLandlordDropdown(false);
      return;
    }

    const requestSeq = landlordSearchRequestSeqRef.current + 1;
    landlordSearchRequestSeqRef.current = requestSeq;
    landlordSearchDebounceRef.current = setTimeout(() => {
      searchLandlords(normalizedValue, requestSeq);
    }, ASSIGN_SEARCH_DEBOUNCE_MS);
  };

  const handleTenantSearch = (value) => {
    setTenantSearch(value);
    if (tenantSearchDebounceRef.current) {
      clearTimeout(tenantSearchDebounceRef.current);
      tenantSearchDebounceRef.current = null;
    }

    const normalizedValue = String(value || "").trim();
    if (!normalizedValue || normalizedValue.length < ASSIGN_SEARCH_MIN_CHARS) {
      tenantSearchRequestSeqRef.current += 1;
      setTenantResults([]);
      setShowTenantDropdown(false);
      return;
    }

    const requestSeq = tenantSearchRequestSeqRef.current + 1;
    tenantSearchRequestSeqRef.current = requestSeq;
    tenantSearchDebounceRef.current = setTimeout(() => {
      searchTenants(normalizedValue, requestSeq);
    }, ASSIGN_SEARCH_DEBOUNCE_MS);
  };

  const selectLandlord = (landlord) => {
    if (landlordSearchDebounceRef.current) {
      clearTimeout(landlordSearchDebounceRef.current);
      landlordSearchDebounceRef.current = null;
    }
    landlordSearchRequestSeqRef.current += 1;
    setSelectedLandlord(landlord);
    setSelectedUserId(landlord._id);
    setLandlordSearch(landlord.idNumber);
    setShowLandlordDropdown(false);
    fetchUsersPayScores([landlord]);
  };

  const selectTenant = (tenant) => {
    if (tenantSearchDebounceRef.current) {
      clearTimeout(tenantSearchDebounceRef.current);
      tenantSearchDebounceRef.current = null;
    }
    tenantSearchRequestSeqRef.current += 1;
    setSelectedTenant(tenant);
    setSelectedUserId(tenant._id);
    setTenantSearch(tenant.idNumber);
    setShowTenantDropdown(false);
    fetchUsersPayScores([tenant]);
  };

  const unassignTenant = async (unitId, tenantId) => {
    if (!window.confirm('Are you sure you want to unassign this tenant?')) {
      return;
    }

    try {
      await api.delete(`/frp/units/${unitId}/unassign-tenant/${tenantId}`);
      fetchUnits(selectedProperty._id);
      alert('Tenant unassigned successfully!');
    } catch (error) {
      alert('Error unassigning tenant: ' + error.response?.data?.error);
    }
  };

  const payRent = async (unit) => {
    setSelectedPaymentUnit(unit);
    setPaymentAmount("0");
    setPaymentDescription(`Payment; ${unit.property?.name}; ${unit.label}`);
    setPaymentResult(null);
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    if (isProcessingPayment) return;
    setShowPaymentModal(false);
    setPaymentAmount("");
    setPaymentDescription("");
    setSelectedPaymentUnit(null);
    setPaymentResult(null);
  };

  const processPayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      setPaymentResult({ success: false, error: "Please enter a valid payment amount." });
      return;
    }

    if (wallet && amount > wallet.balance) {
      setPaymentResult({
        success: false,
        error: "Insufficient funds — payment would exceed your wallet balance.",
        currentBalance: wallet.balance,
        requiredAmount: amount,
        shortfall: amount - wallet.balance,
      });
      return;
    }

    setIsProcessingPayment(true);
    const paymentRequestStorageKey = `recordlogs.rent-payment.${user?._id || user?.id || "user"}`;
    const paymentRequestKey = getStableRequestKey({
      storageKey: paymentRequestStorageKey,
      fingerprint: buildRequestFingerprint({
        unitId: selectedPaymentUnit._id,
        amount: amount.toFixed(2),
        description: paymentDescription.trim(),
      }),
      prefix: "tenant-rent",
    });
    try {
      const response = await api.post("/frp/payments", {
        unitId: selectedPaymentUnit._id,
        amount: amount,
        description: paymentDescription,
        idempotencyKey: paymentRequestKey,
      });
      clearStableRequestKey({
        storageKey: paymentRequestStorageKey,
        requestKey: paymentRequestKey,
      });

      fetchWallet();
      fetchMyUnits();
      if (showWalletTransactionsModal) {
        fetchWalletTransactions();
      }

      setPaymentResult({
        success: true,
        amount,
        newBalance: response.data.walletTransactions.tenantBalance,
        managerReceived: response.data.walletTransactions.managerReceived,
        landlordReceived: response.data.walletTransactions.landlordReceived,
      });
    } catch (error) {
      if (error.response?.data?.shortfall) {
        setPaymentResult({
          success: false,
          error: "Insufficient funds.",
          currentBalance: error.response.data.currentBalance,
          requiredAmount: error.response.data.requiredAmount,
          shortfall: error.response.data.shortfall,
        });
      } else {
        setPaymentResult({
          success: false,
          error: error.response?.data?.error || "Payment failed. Please try again.",
        });
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };
  // eslint-disable-next-line no-unused-vars
  const createInvoice = (unit, tenant = null) => {
    setSelectedInvoiceUnit(unit);
    setSelectedInvoiceTenant(tenant);
    setInvoiceAmount(unit.rentAmount.toString());
    setInvoiceDescription(`Rent Invoice`);
    setInvoiceDueDate("");
    setShowInvoiceModal(true);
  };

  const openWaiveBillModal = (unit, tenant = null, invoice = null) => {
    setSelectedWaiveBillUnit(unit);
    setSelectedWaiveBillTenant(tenant);
    setSelectedWaiveBillInvoice(invoice);
    setWaiveBillAmount("");
    setWaiveBillDescription("");
    setShowWaiveBillModal(true);
  };

  const openUtilityWaiveModal = async (invoice) => {
    if (!invoice?._id) {
      alert("No utility invoice is available to waive right now.");
      return;
    }

    if (invoice?.source === "utility_balance") {
      try {
        const tenantId = getSelectedUtilityTenantId();
        const unitId = normalizeId(selectedWaterUnit?._id);
        const propertyId = normalizeId(
          selectedWaterUnit?.property?._id || selectedWaterUnit?.property
        );

        const buildStatementPath = (utilityType) => {
          const normalizedUtilityType = utilityType === "all" ? "" : utilityType;
          const params = new URLSearchParams();
          if (normalizedUtilityType) params.append("utilityType", normalizedUtilityType);
          if (tenantId) {
            params.append("tenantId", tenantId);
            if (unitId) params.append("unitId", unitId);
            if (propertyId) params.append("propertyId", propertyId);
            return `/utilities/tenant-statement?${params.toString()}`;
          }
          return `/utilities/my-statement${params.toString() ? `?${params.toString()}` : ""}`;
        };

        const categoryLabels = {
          water: "Water",
          other: "Other"
        };

        const categoryRequests = ["water", "other"].map(async (value) => {
          const response = await api.get(buildStatementPath(value));
          return {
            value,
            label: categoryLabels[value],
            utilityType: value,
            outstandingAmount: Number(response?.data?.statement?.summary?.outstandingAmount || 0)
          };
        });

        const categoryResults = await Promise.all(categoryRequests);
        setUtilityWaiveCategoryOptions(categoryResults);
      } catch (error) {
        setUtilityWaiveCategoryOptions([
          {
            value: selectedUtilityType === "all" ? "water" : selectedUtilityType,
            label:
              selectedUtilityType === "all"
                ? "Water"
                : selectedUtilityType === "water"
                ? "Water"
                : selectedUtilityType === "other"
                  ? "Other"
                  : "Water",
            utilityType: selectedUtilityType === "all" ? "water" : selectedUtilityType,
            outstandingAmount: Number(invoice?.outstandingAmount || 0)
          }
        ]);
      }
    } else {
      setUtilityWaiveCategoryOptions([]);
    }

    setSelectedUtilityWaiveInvoice(invoice);
    setShowUtilityWaiveModal(true);
  };

  const closeUtilityWaiveModal = () => {
    setShowUtilityWaiveModal(false);
    setSelectedUtilityWaiveInvoice(null);
    setUtilityWaiveCategoryOptions([]);
  };

  const resolveUtilityInvoiceActionTarget = (row) => {
    if (!row?.invoiceId || row?.type !== "invoice") return null;
    const utilityInvoices = Array.isArray(utilityStatement?.invoices) ? utilityStatement.invoices : [];
    return utilityInvoices.find((invoice) => String(invoice?._id || "") === String(row.invoiceId || "")) || null;
  };

  const canMutateUtilityInvoice = (invoice) =>
    Boolean(invoice?._id) &&
    getUtilityInvoiceSettlement(invoice).totalSettledAmount <= 0;

  const openUtilityEditModal = (invoice) => {
    if (!invoice?._id) {
      alert("No utility invoice is available to edit right now.");
      return;
    }

    setSelectedUtilityEditInvoice(invoice);
    setShowUtilityEditModal(true);
  };

  const closeUtilityEditModal = () => {
    setShowUtilityEditModal(false);
    setSelectedUtilityEditInvoice(null);
  };

  const getSelectedUtilityTenantId = () =>
    normalizeId(
      waterMeterState?.tenant?._id ||
      selectedWaterUnit?.tenant?._id ||
      selectedWaterUnit?.tenant
    );

  const closeWaterMeterModal = () => {
    utilityStatementRequestRef.current += 1;
    waterPurchaseRequestRef.current = { fingerprint: "", requestId: "" };
    setShowWaterMeterModal(false);
    setSelectedWaterUnit(null);
    setWaterMeterState(null);
    setWaterMeterWalletBalance(0);
    setWaterUnitTransactions([]);
    setSelectedUtilityType("water");
    setUtilityStatement(null);
  };

  const loadUtilityStatement = async (utilityType = selectedUtilityType) => {
    const requestId = utilityStatementRequestRef.current + 1;
    utilityStatementRequestRef.current = requestId;
    try {
      setUtilityStatementLoading(true);
      setUtilityStatement(null);
      const normalizedUtilityType = utilityType === "all" ? "" : utilityType;
      const params = new URLSearchParams();
      if (normalizedUtilityType) params.append("utilityType", normalizedUtilityType);

      const tenantId = getSelectedUtilityTenantId();
      const unitId = normalizeId(selectedWaterUnit?._id);
      const propertyId = normalizeId(
        selectedWaterUnit?.property?._id || selectedWaterUnit?.property
      );

      const path = tenantId
        ? `/utilities/tenant-statement?${new URLSearchParams({
            ...(normalizedUtilityType ? { utilityType: normalizedUtilityType } : {}),
            tenantId,
            ...(unitId ? { unitId } : {}),
            ...(propertyId ? { propertyId } : {})
          }).toString()}`
        : `/utilities/my-statement${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await api.get(path);
      if (utilityStatementRequestRef.current !== requestId) return;
      setUtilityStatement(response?.data?.statement || null);
    } catch (error) {
      if (utilityStatementRequestRef.current !== requestId) return;
      setUtilityStatement(null);
      console.error("Error loading utility statement:", error);
    } finally {
      if (utilityStatementRequestRef.current === requestId) {
        setUtilityStatementLoading(false);
      }
    }
  };

  const getDisplayedUtilityOutstandingAmount = () =>
    getUtilityDebtSummary(utilityStatement?.summary || {}, utilityStatement?.invoices || []).amountDue;

  useEffect(() => {
    if (!showWaterMeterModal) return;
    loadUtilityStatement(selectedUtilityType);
  }, [showWaterMeterModal, selectedUtilityType, waterMeterState?.tenant?._id, selectedWaterUnit?._id]);

  const getWaterPreview = () => {
    const rate = Number(waterMeterState?.unit?.property?.waterUnitCharge || selectedWaterUnit?.property?.waterUnitCharge || 0);
    return {
      rate
    };
  };

  const getMinimumWaterPurchaseAmount = () => {
    const rate = Number(
      waterMeterState?.unit?.property?.waterUnitCharge ||
      selectedWaterUnit?.property?.waterUnitCharge ||
      0
    );
    return Number.isFinite(rate) && rate > 0 ? rate : 0;
  };

  const getEditableMeterReadingParts = (readingValue) => {
    const normalized = String(readingValue || "").trim();
    if (!normalized) {
      return {
        wholePart: "",
        decimalPart: "",
      };
    }

    const [wholeRaw, decimalRaw = ""] = normalized.split(".");
    const wholeDigits = wholeRaw.replace(/[^\d]/g, "").slice(-4);
    const decimalDigits = decimalRaw.replace(/[^\d]/g, "").slice(0, 3);

    return {
      wholePart: wholeDigits.replace(/^0+(?=\d)/, ""),
      decimalPart: decimalDigits,
    };
  };

  const getWaterRemainingUnits = () => {
    const remainingUnits = Number(waterMeterState?.meterState?.remainingUnits || 0);
    return Number.isFinite(remainingUnits) ? remainingUnits : 0;
  };

  const getWaterRemainingBalance = () => {
    const remainingBalance = Number(waterMeterState?.meterState?.remainingBalance || 0);
    return Number.isFinite(remainingBalance) ? remainingBalance : 0;
  };

  const getWaterMeterDisplayParts = (readingValue) => {
    const parsedReading = Number(readingValue);
    if (!Number.isFinite(parsedReading) || parsedReading < 0) {
      return {
        wholePart: "0000",
        decimalPart: "000",
      };
    }

    const [wholePart, decimalPart = ""] = parsedReading.toFixed(3).split(".");
    return {
      wholePart: wholePart.slice(-4).padStart(4, "0"),
      decimalPart: decimalPart.padEnd(3, "0").slice(0, 3),
    };
  };

  const buildWaterReadingValue = (wholePart, decimalPart) => {
    const normalizedWhole = String(wholePart || "").replace(/[^\d]/g, "").slice(-4);
    const normalizedDecimal = String(decimalPart || "").replace(/[^\d]/g, "").slice(0, 3);

    if (!normalizedWhole && !normalizedDecimal) {
      return "";
    }

    if (!normalizedDecimal) {
      return normalizedWhole || "0";
    }

    return `${normalizedWhole || "0"}.${normalizedDecimal}`;
  };

  const updateWaterMeterForm = (field, value) => {
    setWaterMeterForm((previousForm) => {
      const nextForm = { ...previousForm };

      if (field === "currentReadingWhole") {
        nextForm.currentReadingWhole = String(value || "").replace(/[^\d]/g, "").slice(-4);
        nextForm.currentReading = buildWaterReadingValue(
          nextForm.currentReadingWhole,
          nextForm.currentReadingDecimal
        );
        return nextForm;
      }

      if (field === "currentReadingDecimal") {
        nextForm.currentReadingDecimal = String(value || "").replace(/[^\d]/g, "").slice(0, 3);
        nextForm.currentReading = buildWaterReadingValue(
          nextForm.currentReadingWhole,
          nextForm.currentReadingDecimal
        );
        return nextForm;
      }

      if (field === "currentReading") {
        const normalizedReading = String(value || "").replace(/[^0-9.]/g, "");
        const editableParts = getEditableMeterReadingParts(normalizedReading);
        nextForm.currentReading = normalizedReading;
        nextForm.currentReadingWhole = editableParts.wholePart;
        nextForm.currentReadingDecimal = editableParts.decimalPart;
        return nextForm;
      }

      nextForm[field] = value;
      return nextForm;
    });
  };

  const renderWaterMeterDisplay = ({ readingValue, editable = false, label }) => {
    const displayParts = editable
      ? {
          wholePart: String(waterMeterForm.currentReadingWhole || "").padStart(4, "0").slice(-4),
          decimalPart: String(waterMeterForm.currentReadingDecimal || "").padEnd(3, "0").slice(0, 3),
        }
      : getWaterMeterDisplayParts(readingValue);

    return (
      <div
        className="inline-flex rounded-xl border-2 border-slate-500 bg-slate-600 p-1.5 shadow-[inset_0_10px_16px_rgba(255,255,255,0.08),0_6px_14px_rgba(15,23,42,0.16)]"
        aria-label={label}
      >
        <div className="flex items-center gap-1 rounded-lg border-[3px] border-slate-800 bg-black px-1.5 py-1.5 shadow-[inset_0_2px_0_rgba(255,255,255,0.08)]">
          {editable ? (
            <>
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={waterMeterForm.currentReadingWhole}
                onChange={(e) => updateWaterMeterForm("currentReadingWhole", e.target.value)}
                placeholder="0000"
                aria-label={`${label} whole digits`}
                className="h-10 w-24 rounded-md border border-slate-300 bg-white px-2 text-center font-mono text-xl font-black tracking-[0.18em] text-slate-950 shadow-[inset_0_8px_14px_rgba(15,23,42,0.08)] outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
              />
              <span className="text-xl font-black text-white">.</span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={3}
                value={waterMeterForm.currentReadingDecimal}
                onChange={(e) => updateWaterMeterForm("currentReadingDecimal", e.target.value)}
                placeholder="000"
                aria-label={`${label} decimal digits`}
                className="h-10 w-[4.5rem] rounded-md border border-red-700 bg-red-600 px-2 text-center font-mono text-xl font-black tracking-[0.18em] text-white shadow-[inset_0_8px_14px_rgba(127,29,29,0.28)] outline-none transition focus:border-red-300 focus:ring-2 focus:ring-red-100"
              />
            </>
          ) : (
            <>
              <div className="flex items-center gap-1">
                {displayParts.wholePart.split("").map((digit, index) => (
                  <div
                    key={`${label}-whole-${index}`}
                    className="flex h-10 w-7 items-center justify-center rounded-md border border-slate-300 bg-white font-mono text-xl font-black text-slate-950 shadow-[inset_0_8px_14px_rgba(15,23,42,0.08)]"
                  >
                    {digit}
                  </div>
                ))}
              </div>
              <span className="text-xl font-black text-white">.</span>
              <div className="flex items-center gap-1">
                {displayParts.decimalPart.split("").map((digit, index) => (
                  <div
                    key={`${label}-decimal-${index}`}
                    className="flex h-10 w-7 items-center justify-center rounded-md border border-red-700 bg-red-600 font-mono text-xl font-black text-white shadow-[inset_0_8px_14px_rgba(127,29,29,0.28)]"
                  >
                    {digit}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const syncWaterMeterModalData = (unit, state, unitLedgerResponse, tenantWalletBalance = 0) => {
    const savedCutOffReading = state?.meterState?.cutOffReading;
    const savedCurrentReading = state?.meterState?.currentReading;

    setSelectedWaterUnit(unit);
    setWaterMeterState(state);
    setWaterMeterWalletBalance(tenantWalletBalance);
    setWaterUnitTransactions(Array.isArray(unitLedgerResponse?.data?.rows) ? unitLedgerResponse.data.rows : []);
    setShowMeterReplacementForm(false);
    setMeterReplacementConfirm(null);
    setMeterReplacementForm({
      oldMeterFinalReading: savedCurrentReading == null ? "" : String(savedCurrentReading),
      newMeterCurrentReading: "",
      replacementDate: formatDateInputValue(new Date()),
      note: ""
    });
    setShowMeterCorrectionForm(false);
    setMeterCorrectionConfirm(null);
    setMeterCorrectionForm({
      currentReading: savedCurrentReading == null ? "" : String(savedCurrentReading),
      cutOffReading: savedCutOffReading == null ? "" : String(savedCutOffReading),
      correctionDate: formatDateInputValue(new Date()),
      reason: ""
    });
    setWaterMeterForm((previousForm) => {
      const readingValue = savedCurrentReading != null
        ? String(savedCurrentReading)
        : savedCutOffReading != null
          ? String(savedCutOffReading)
          : "";
      const editableParts = getEditableMeterReadingParts(readingValue);

      return {
        currentReading: readingValue,
        currentReadingWhole: editableParts.wholePart,
        currentReadingDecimal: editableParts.decimalPart,
        amountPaid: "",
        readingDate: previousForm?.readingDate || formatDateInputValue(new Date())
      };
    });
  };

  const loadWaterMeterModalData = async (unit, { openModal = false, readingDateOverride = null } = {}) => {
    setFormLoading(true);
    setWaterHistoryLoading(true);
    try {
      const todayReadingDate = readingDateOverride || formatDateInputValue(new Date());
      const [stateResult, unitLedgerResult] = await Promise.allSettled([
        api.get(`/frp/units/${unit._id}/water-meter-state`),
        api.get(`/frp/units/${unit._id}/water-transactions`)
      ]);

      if (stateResult.status !== "fulfilled") {
        throw stateResult.reason;
      }

      const state = stateResult.value?.data || {};
      const unitLedgerResponse = unitLedgerResult.status === "fulfilled"
        ? unitLedgerResult.value
        : { data: { rows: [] } };
      const tenantWalletBalance = Number(state?.walletBalance || 0);

      syncWaterMeterModalData(unit, state, unitLedgerResponse, tenantWalletBalance);
      setWaterMeterForm((previousForm) => ({
        ...previousForm,
        readingDate: todayReadingDate
      }));

      if (openModal) {
        setShowWaterMeterModal(true);
        setShowUnitDetailsModal(false);
      }

      const historyErrors = [unitLedgerResult]
        .filter((result) => result.status === "rejected")
        .map((result) => result.reason?.response?.data?.error || result.reason?.message || "Unknown error");
      if (historyErrors.length > 0) {
        alert(`Water meter opened, but some history data could not be loaded yet: ${historyErrors.join(" | ")}`);
      }
    } finally {
      setWaterHistoryLoading(false);
      setFormLoading(false);
    }
  };

  const openWaterMeterModal = async (unit) => {
    try {
      setSelectedUtilityType("water");
      await loadWaterMeterModalData(unit, {
        openModal: true,
        readingDateOverride: formatDateInputValue(new Date())
      });
    } catch (error) {
      alert("Error loading water meter state: " + (error.response?.data?.error || error.message));
    }
  };

  const submitWaterMeterReading = async () => {
    try {
      if (!selectedWaterUnit?._id) return;

      const currentReading = buildWaterReadingValue(
        waterMeterForm.currentReadingWhole,
        waterMeterForm.currentReadingDecimal
      );

      if (!currentReading) {
        alert("Please enter the current meter reading.");
        return;
      }

      setFormLoading(true);
      const response = await api.post(`/frp/units/${selectedWaterUnit._id}/water-meter-reading`, {
        currentReading,
        readingDate: waterMeterForm.readingDate || formatDateInputValue(new Date())
      });

      await loadWaterMeterModalData(selectedWaterUnit, {
        readingDateOverride: waterMeterForm.readingDate || formatDateInputValue(new Date())
      });
      alert(response?.data?.message || "Current water reading updated successfully.");
    } catch (error) {
      alert("Error updating current water reading: " + (error.response?.data?.error || error.message));
    } finally {
      setFormLoading(false);
    }
  };

  const requestMeterReplacement = () => {
    const oldFinal = Number(meterReplacementForm.oldMeterFinalReading);
    const newCurrent = Number(meterReplacementForm.newMeterCurrentReading);
    const savedCurrent = Number(waterMeterState?.meterState?.currentReading || 0);
    if (!Number.isFinite(oldFinal) || oldFinal < savedCurrent) {
      alert(`Old meter final reading cannot be less than ${savedCurrent.toFixed(4)}.`);
      return;
    }
    if (!Number.isFinite(newCurrent) || newCurrent < 0 || meterReplacementForm.newMeterCurrentReading === "") {
      alert("Please enter the new meter current reading.");
      return;
    }
    setMeterReplacementConfirm({ oldFinal, newCurrent });
  };

  const submitMeterReplacement = async () => {
    if (!selectedWaterUnit?._id || !meterReplacementConfirm) return;
    try {
      setFormLoading(true);
      const response = await api.post(`/frp/units/${selectedWaterUnit._id}/water-meter-replacement`, {
        oldMeterFinalReading: meterReplacementConfirm.oldFinal,
        newMeterCurrentReading: meterReplacementConfirm.newCurrent,
        replacementDate: meterReplacementForm.replacementDate || formatDateInputValue(new Date()),
        note: meterReplacementForm.note
      });
      setMeterReplacementConfirm(null);
      await loadWaterMeterModalData(selectedWaterUnit, {
        readingDateOverride: meterReplacementForm.replacementDate || formatDateInputValue(new Date())
      });
      alert(response?.data?.message || "Meter replaced successfully.");
    } catch (error) {
      alert("Error replacing water meter: " + (error.response?.data?.error || error.message));
    } finally {
      setFormLoading(false);
    }
  };

  const requestMeterCorrection = () => {
    const currentReading = Number(meterCorrectionForm.currentReading);
    const cutOffReading = Number(meterCorrectionForm.cutOffReading);
    const savedCurrent = Number(waterMeterState?.meterState?.currentReading || 0);
    const savedCutOff = Number(waterMeterState?.meterState?.cutOffReading || 0);
    const reason = String(meterCorrectionForm.reason || "").trim();
    if (!Number.isFinite(currentReading) || currentReading < 0 || meterCorrectionForm.currentReading === "") {
      alert("Please enter a valid corrected meter reading.");
      return;
    }
    if (!Number.isFinite(cutOffReading) || cutOffReading < 0 || meterCorrectionForm.cutOffReading === "") {
      alert("Please enter a valid corrected cut-off reading.");
      return;
    }
    if (currentReading === savedCurrent && cutOffReading === savedCutOff) {
      alert("Enter a different meter reading or cut-off reading.");
      return;
    }
    if (reason.length < 3) {
      alert("Please enter a clear reason for the correction.");
      return;
    }
    const rate = Number(waterMeterState?.meterState?.rate || 0);
    const beforeUnits = Number((savedCutOff - savedCurrent).toFixed(4));
    const afterUnits = Number((cutOffReading - currentReading).toFixed(4));
    setMeterCorrectionConfirm({
      currentReading,
      cutOffReading,
      savedCurrent,
      savedCutOff,
      beforeUnits,
      afterUnits,
      beforeValue: Number((beforeUnits * rate).toFixed(2)),
      afterValue: Number((afterUnits * rate).toFixed(2))
    });
  };

  const submitMeterCorrection = async () => {
    if (!selectedWaterUnit?._id || !meterCorrectionConfirm) return;
    try {
      setFormLoading(true);
      const response = await api.post(`/frp/units/${selectedWaterUnit._id}/water-meter-correction`, {
        currentReading: meterCorrectionConfirm.currentReading,
        cutOffReading: meterCorrectionConfirm.cutOffReading,
        correctionDate: meterCorrectionForm.correctionDate || formatDateInputValue(new Date()),
        reason: meterCorrectionForm.reason
      });
      setMeterCorrectionConfirm(null);
      await loadWaterMeterModalData(selectedWaterUnit, {
        readingDateOverride: meterCorrectionForm.correctionDate || formatDateInputValue(new Date())
      });
      const warning = response?.data?.warning ? ` ${response.data.warning}` : "";
      alert((response?.data?.message || "Meter details corrected successfully.") + warning);
    } catch (error) {
      alert("Error correcting meter details: " + (error.response?.data?.error || error.message));
    } finally {
      setFormLoading(false);
    }
  };

  const submitWaterPurchase = async () => {
    try {
      if (!selectedWaterUnit?._id) return;

      const currentReading = buildWaterReadingValue(
        waterMeterForm.currentReadingWhole,
        waterMeterForm.currentReadingDecimal
      );

      if (!currentReading) {
        alert("Please enter the current meter reading.");
        return;
      }

      if (!(Number(waterMeterForm.amountPaid) > 0)) {
        alert("Please enter a valid water payment amount.");
        return;
      }
      const purchaseValidation = getWaterPurchaseValidation({
        amountPaid: waterMeterForm.amountPaid,
        outstandingDebtAmount: waterMeterState?.meterState?.outstandingDebtAmount,
        rate: getMinimumWaterPurchaseAmount(),
      });
      if (!purchaseValidation.isValid) {
        alert(purchaseValidation.message || "Please enter a valid water payment amount.");
        return;
      }

      setFormLoading(true);
      const readingDate = waterMeterForm.readingDate || formatDateInputValue(new Date());
      const purchasePayload = {
        currentReading,
        amountPaid: waterMeterForm.amountPaid,
        readingDate
      };
      waterPurchaseRequestRef.current = getStableWaterPurchaseRequest(
        waterPurchaseRequestRef.current,
        {
          unitId: String(selectedWaterUnit._id),
          ...purchasePayload,
        },
      );
      const response = await api.post(`/frp/units/${selectedWaterUnit._id}/water-meter-capture`, {
        ...purchasePayload,
        requestId: waterPurchaseRequestRef.current.requestId,
      });

      await loadWaterMeterModalData(selectedWaterUnit, {
        readingDateOverride: readingDate
      });
      waterPurchaseRequestRef.current = { fingerprint: "", requestId: "" };
      setWaterMeterForm((previousForm) => ({
        ...previousForm,
        amountPaid: ""
      }));
      alert(response?.data?.message || "Water payment captured successfully.");
    } catch (error) {
      const backendError = error.response?.data?.error || error.message;
      alert(
        typeof backendError === "string" && backendError.startsWith("Water payment failed")
          ? backendError
          : "Error capturing water payment: " + backendError
      );
    } finally {
      setFormLoading(false);
    }
  };

  const openPropertyWaterStatements = async (property) => {
    try {
      const propertyId = property?._id || property;
      if (!propertyId) return;
      setPropertyWaterLoading(true);
      const { start, end } = getCurrentMonthRange();
      setPropertyWaterStartDate(start);
      setPropertyWaterEndDate(end);
      setPropertyWaterSearch("");
      const response = await api.get(`/frp/properties/${propertyId}/water-transactions`);
      setWaterPropertyTransactions(Array.isArray(response?.data?.rows) ? response.data.rows : []);
      setWaterPropertyLatestByUnit(Array.isArray(response?.data?.latestByUnit) ? response.data.latestByUnit : []);
      setWaterPropertySummary(response?.data?.summary || null);
      setShowPropertyWaterStatementsModal(true);
    } catch (error) {
      alert("Error loading property water statements: " + (error.response?.data?.error || error.message));
    } finally {
      setPropertyWaterLoading(false);
    }
  };

  const getWaiveBillCurrentBalance = () => {
    const tenantBalance = Number(selectedWaiveBillTenant?.currentBalance);
    if (Number.isFinite(tenantBalance)) return tenantBalance;

    const unitCurrentBalance = Number(selectedWaiveBillUnit?.currentBalance);
    if (Number.isFinite(unitCurrentBalance)) return unitCurrentBalance;

    const mappedUnitBalance = Number(managerUnitBalances?.[selectedWaiveBillUnit?._id]);
    if (Number.isFinite(mappedUnitBalance)) return mappedUnitBalance;

    const unitBalanceField = Number(selectedWaiveBillUnit?.balance);
    if (Number.isFinite(unitBalanceField)) return unitBalanceField;

    return 0;
  };

  const processInvoiceCreation = async () => {
    try {
      const amount = parseFloat(invoiceAmount);
      if (!amount || amount <= 0) {
        alert("Please enter a valid invoice amount");
        return;
      }

      setFormLoading(true);

      const payload = {
        unitId: selectedInvoiceUnit._id,
        amount: amount,
        description: invoiceDescription,
        dueDate: invoiceDueDate || null
      };
      if (selectedInvoiceTenant?.tenantId) {
        payload.tenantId = selectedInvoiceTenant.tenantId;
      }

      await api.post("/frp/invoices", payload);

      setShowInvoiceModal(false);
      setInvoiceAmount("");
      setInvoiceDescription("");
      setInvoiceDueDate("");
      setSelectedInvoiceTenant(null);

      // Refresh the units list to show updated balance
      const propertyId = selectedProperty?._id || selectedInvoiceUnit?.property?._id || selectedInvoiceUnit?.property;
      if (propertyId) {
        fetchUnits(propertyId);
      }

      setSelectedInvoiceUnit(null);

      const targetTenantName = selectedInvoiceTenant?.tenantName || selectedInvoiceUnit?.tenant?.name;
      alert(`Invoice for ${formatMoney(amount)} created successfully${targetTenantName ? ` for ${targetTenantName}` : ''}!`);
    } catch (error) {
      alert("Error creating invoice: " + (error.response?.data?.error || error.message));
    } finally {
      setFormLoading(false);
    }
  };

  // Send a utility bill (trash / other) for the unit's active tenant from the
  // Utilities modal. Mirrors the UT Bills flow: the bill autopays from the
  // tenant's UT Bills wallet when funded.
  const submitUtilityBill = async () => {
    if (utilityBillSubmitting) return;
    const amount = parseFloat(utilityBillForm.amount);
    if (!amount || amount <= 0) {
      alert("Please enter a valid bill amount");
      return;
    }
    if (!selectedWaterUnit?._id) return;

    const payloadUtilityType = selectedUtilityType === "water" ? "water" : "other";
    const utilityLabel = payloadUtilityType === "water" ? "Water" : "Utility";
    const propertyName =
      waterMeterState?.unit?.property?.name || selectedWaterUnit?.property?.name || "Property";

    try {
      setUtilityBillSubmitting(true);
      const payload = {
        unitId: selectedWaterUnit._id,
        amount,
        description:
          (utilityBillForm.description && utilityBillForm.description.trim()) ||
          `${utilityLabel} bill for ${propertyName} - Unit ${selectedWaterUnit.label || ""}`.trim(),
        dueDate: utilityBillForm.dueDate || null,
        category: "utility",
        utilityType: payloadUtilityType,
      };

      await api.post("/frp/invoices", payload);
      setUtilityBillForm({ amount: "", description: "", dueDate: "" });
      await loadUtilityStatement(selectedUtilityType);

      const propertyId = selectedProperty?._id || selectedWaterUnit?.property?._id || selectedWaterUnit?.property;
      if (propertyId) fetchUnits(propertyId);

      alert(`${utilityLabel} bill of ${formatMoney(amount)} sent successfully!`);
    } catch (error) {
      alert("Error sending bill: " + (error.response?.data?.error || error.message));
    } finally {
      setUtilityBillSubmitting(false);
    }
  };

  const processWaiveBill = async () => {
    try {
      const amount = parseFloat(waiveBillAmount);
      if (!amount || amount <= 0) {
        alert("Please enter a valid waive amount");
        return;
      }

      setFormLoading(true);

      // Create a negative invoice to waive the bill
      const baseDescription = (waiveBillDescription && waiveBillDescription.trim())
        ? waiveBillDescription.trim()
        : `Bill waiver for ${selectedWaiveBillUnit.property?.name} - Unit ${selectedWaiveBillUnit.label}`;
      const descriptionWithUser = `${baseDescription} (Waived by - ${user?.name || 'Unknown'})`;

      if (selectedWaiveBillInvoice?.invoiceId) {
        if (selectedWaiveBillInvoice?.invoiceModel === "UtilityBillInvoice" && selectedWaiveBillInvoice?.providerId) {
          await api.post(
            `/ut-bills/providers/${encodeURIComponent(selectedWaiveBillInvoice.providerId)}/invoices/${encodeURIComponent(selectedWaiveBillInvoice.invoiceId)}/waive`,
            {
              amount,
              description: descriptionWithUser
            }
          );
        } else {
          await api.post(`/utilities/invoices/${selectedWaiveBillInvoice.invoiceId}/waive`, {
            amount,
            description: descriptionWithUser
          });
        }
      } else {
        const payload = {
          unitId: selectedWaiveBillUnit._id,
          amount: -amount, // Negative amount for waiving
          description: descriptionWithUser,
          dueDate: null
        };
        if (selectedWaiveBillTenant?.tenantId) {
          payload.tenantId = selectedWaiveBillTenant.tenantId;
        }

        await api.post("/frp/invoices", payload);
      }

      setShowWaiveBillModal(false);
      setWaiveBillAmount("");
      setWaiveBillDescription("");
      setSelectedWaiveBillTenant(null);
      setSelectedWaiveBillInvoice(null);

      // Refresh the units list to show updated balance
      const propertyId = selectedProperty?._id || selectedWaiveBillUnit?.property?._id || selectedWaiveBillUnit?.property;
      if (propertyId) {
        fetchUnits(propertyId);
      }

      if (showWaterMeterModal && selectedWaterUnit?._id && selectedWaiveBillUnit?._id === selectedWaterUnit._id) {
        await loadWaterMeterModalData(selectedWaterUnit, {
          openModal: false,
          readingDateOverride: waterMeterForm.readingDate || formatDateInputValue(new Date())
        });
      }

      setSelectedWaiveBillUnit(null);

      const targetTenantName = selectedWaiveBillTenant?.tenantName || selectedWaiveBillUnit?.tenant?.name;
      alert(`Bill waiver for ${formatMoney(amount)} created successfully${targetTenantName ? ` for ${targetTenantName}` : ''}!`);
    } catch (error) {
      alert("Error creating bill waiver: " + (error.response?.data?.error || error.message));
    } finally {
      setFormLoading(false);
    }
  };

  const processUtilityInvoiceWaive = async (providerId, invoiceId, payload, overrideInvoice = null) => {
    const targetInvoice = overrideInvoice || selectedUtilityWaiveInvoice;

    if (targetInvoice?.source === "utility_balance") {
      const tenantId = getSelectedUtilityTenantId();
      const unitId = normalizeId(selectedWaterUnit?._id);
      const propertyId = normalizeId(
        selectedWaterUnit?.property?._id || selectedWaterUnit?.property
      );
      const normalizedUtilityType =
        targetInvoice?.utilityType === undefined
          ? (selectedUtilityType === "all" ? null : selectedUtilityType)
          : targetInvoice.utilityType;

      await api.post("/utilities/tenant-waive", {
        tenantId,
        unitId: unitId || null,
        propertyId: propertyId || null,
        utilityType: normalizedUtilityType,
        ...payload
      });
    } else if (targetInvoice?.source === "ut_bill") {
      if (!providerId) {
        throw new Error("Missing utility provider for this invoice.");
      }

      await api.post(
        `/ut-bills/providers/${encodeURIComponent(providerId)}/invoices/${encodeURIComponent(invoiceId)}/waive`,
        payload
      );
    } else {
      await api.post(`/utilities/invoices/${encodeURIComponent(invoiceId)}/waive`, payload);
    }

    await loadUtilityStatement(selectedUtilityType);

    if (selectedUtilityType === "water" && selectedWaterUnit?._id) {
      await loadWaterMeterModalData(selectedWaterUnit, {
        readingDateOverride: waterMeterForm.readingDate || formatDateInputValue(new Date())
      });
    }
  };

  const processUtilityInvoiceEdit = async (payload, overrideInvoice = null) => {
    const targetInvoice = overrideInvoice || selectedUtilityEditInvoice;
    if (!targetInvoice?._id) {
      throw new Error("Utility invoice not found.");
    }

    if (targetInvoice?.source === "ut_bill") {
      if (!targetInvoice?.providerId) {
        throw new Error("Missing utility provider for this invoice.");
      }
      await api.put(
        `/ut-bills/providers/${encodeURIComponent(targetInvoice.providerId)}/invoices/${encodeURIComponent(targetInvoice._id)}`,
        payload
      );
    } else {
      await api.put(`/utilities/invoices/${encodeURIComponent(targetInvoice._id)}`, payload);
    }

    await loadUtilityStatement(selectedUtilityType);

    if (selectedUtilityType === "water" && selectedWaterUnit?._id) {
      await loadWaterMeterModalData(selectedWaterUnit, {
        readingDateOverride: waterMeterForm.readingDate || formatDateInputValue(new Date())
      });
    }
  };

  const processUtilityInvoiceDelete = async (invoice) => {
    if (!invoice?._id) {
      alert("Utility invoice not found.");
      return;
    }

    if (!window.confirm("Delete this utility invoice? This cannot be undone.")) {
      return;
    }

    try {
      if (invoice?.source === "ut_bill") {
        if (!invoice?.providerId) {
          throw new Error("Missing utility provider for this invoice.");
        }
        await api.delete(
          `/ut-bills/providers/${encodeURIComponent(invoice.providerId)}/invoices/${encodeURIComponent(invoice._id)}`
        );
      } else {
        await api.delete(`/utilities/invoices/${encodeURIComponent(invoice._id)}`);
      }

      await loadUtilityStatement(selectedUtilityType);

      if (selectedUtilityType === "water" && selectedWaterUnit?._id) {
        await loadWaterMeterModalData(selectedWaterUnit, {
          readingDateOverride: waterMeterForm.readingDate || formatDateInputValue(new Date())
        });
      }

      alert("Utility invoice deleted successfully.");
    } catch (error) {
      alert("Error deleting utility invoice: " + (error.response?.data?.error || error.message));
    }
  };

  const triggerTenantUtilitySettlement = async () => {
    if (!selectedWaterUnit) return;
    askUtilitySettlementForUnit(selectedWaterUnit, {
      refreshModal: true,
      successMessage: true,
      buttonScope: "modal"
    });
  };

  const fetchTransactionStatement = async (unit, startDate = "", endDate = "", tenantId = null) => {
    try {
      setStatementLoading(true);

      // Build query parameters only if user provided filters; default shows all
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      // Reflect UI filter state (blank by default)
      setStatementStartDate(startDate || "");
      setStatementEndDate(endDate || "");

      const queryString = params.toString();

      // Determine endpoint. If a tenantId is provided, always fetch the tenant-view statement
      // so managers/landlords see exactly what the tenant sees.
      let url;

      if (tenantId) {
        url = `/frp/units/${unit._id}/tenant-statements/${tenantId}${queryString ? `?${queryString}` : ''}`;
        setSelectedTenantForHistory(tenantId);
        console.log('Using tenant-statements (explicit tenant) endpoint:', url);
      } else {
        // Determine endpoint based on the role the user is currently viewing as.
        // If the user is viewing as a tenant, always use the tenant-specific endpoint
        // and let the backend enforce access control. This avoids backend redirects
        // that drop the `/api` prefix in production.
        if (activeRole === 'tenant') {
          url = `/frp/units/${unit._id}/tenant-statements${queryString ? `?${queryString}` : ''}`;
          console.log('Using tenant-statements endpoint (activeRole=tenant):', url);
        } else {
          // For managers/landlords, use the management/landlord endpoint
          url = `/frp/units/${unit._id}/transaction-statement${queryString ? `?${queryString}` : ''}`;
          console.log('Using transaction-statement endpoint (non-tenant role):', url);
        }
      }

      const response = await api.get(url);
      setTransactionStatement(response.data.statement);
      setSelectedUnit(unit);
      setShowTransactionStatement(true);
    } catch (error) {
      // If tenant endpoint fails with access denied, try management endpoint as fallback
      if (error.response?.status === 403 && error.response?.data?.error?.includes('tenant access only')) {
        try {
          const params = new URLSearchParams();
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          const queryString = params.toString();

          const fallbackUrl = `/frp/units/${unit._id}/transaction-statement${queryString ? `?${queryString}` : ''}`;
          const response = await api.get(fallbackUrl);
          setTransactionStatement(response.data.statement);
          setSelectedUnit(unit);
          setShowTransactionStatement(true);
          return; // Success with fallback
        } catch (fallbackError) {
          // If fallback also fails, show the original error
          alert("Error fetching transaction statement: " + (error.response?.data?.error || error.message));
        }
      } else {
        alert("Error fetching transaction statement: " + (error.response?.data?.error || error.message));
      }
    } finally {
      setStatementLoading(false);
    }
  };

  const fetchTenantMonthlyStatus = async (unit, startDate = "", endDate = "", tenantId = null, showModal = true) => {
    try {
      setTenantMonthlyStatusLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const queryString = params.toString();
      let url = "";
      if (tenantId) {
        url = `/frp/units/${unit._id}/tenant-statements/${tenantId}${queryString ? `?${queryString}` : ""}`;
      } else if (activeRole === "tenant") {
        url = `/frp/units/${unit._id}/tenant-statements${queryString ? `?${queryString}` : ""}`;
      } else {
        // Fallback for non-tenant views when no tenant id is available.
        url = `/frp/units/${unit._id}/transaction-statement${queryString ? `?${queryString}` : ""}`;
      }

      const response = await api.get(url);
      const statement = response?.data?.statement || {};
      const monthly = buildTenantMonthlyInvoiceSummary(statement);
      const realPaidTransactions = Array.isArray(monthly?.paidTransactions) ? monthly.paidTransactions : [];
      const autoCreditApplications = Array.isArray(statement?.autoCreditApplications)
        ? statement.autoCreditApplications
        : [];
      const autoCreditRows = autoCreditApplications
        .map((row) => {
          const invoiceDate = row?.date ? new Date(row.date) : null;
          const monthLabel = invoiceDate && !Number.isNaN(invoiceDate.getTime())
            ? invoiceDate.toLocaleString("en-US", { month: "short", year: "numeric" })
            : "-";
          return {
            date: row?.date || null,
            invoicedAt: monthLabel,
            amount: Number(row?.amountApplied || 0),
            totalBalance: Number(row?.totalBalance || 0),
            description: row?.description || 'Invoice',
          };
        })
        .filter((row) => Number(row.amount || 0) > 0);
      const paidTransactions = [...realPaidTransactions, ...autoCreditRows].sort(
        (a, b) => new Date(b?.date || 0) - new Date(a?.date || 0)
      );
      const monthlySummary = monthly?.summary || {};
      const statementCurrentBalance = Number(statement?.summary?.currentBalance);
      const netBalance = Number.isFinite(statementCurrentBalance)
        ? statementCurrentBalance
        : Number(monthlySummary?.netBalance ?? (
          Number(monthlySummary?.totalOutstanding || 0) - Number(monthlySummary?.unappliedCredit || 0)
        ));
      const fallbackTenantName = statement.tenantName
        || (Array.isArray(statement.tenantNames) ? statement.tenantNames.join(", ") : "")
        || (activeRole === "tenant" ? (user?.name || "Tenant") : "Tenant");

      setTenantMonthlyStatus({
        propertyName: statement.propertyName || unit?.property?.name || "Property",
        unitLabel: statement.unitLabel || unit?.label || "-",
        tenantName: fallbackTenantName,
        dateFilter: {
          filtered: Boolean(startDate || endDate),
          startDate: startDate || "",
          endDate: endDate || "",
        },
        ...monthly,
        summary: {
          ...monthlySummary,
          netBalance,
        },
        paidTransactions,
      });
      setTenantMonthlyStatusVisibleInvoices(TENANT_MONTHLY_STATUS_LIMIT);
      setSelectedUnit(unit);
      if (showModal) setShowTenantMonthlyStatus(true);
    } catch (error) {
      alert("Error fetching monthly status: " + (error.response?.data?.error || error.message));
    } finally {
      setTenantMonthlyStatusLoading(false);
    }
  };

  // New function to fetch tenant history (Manager/Landlord only)
  const fetchTenantHistory = async (unit) => {
    try {
      setStatementLoading(true);
      const response = await api.get(`/frp/units/${unit._id}/tenant-history`);
      setTenantHistory(response.data);
      setSelectedUnit(unit);
      setShowTenantHistory(true);
    } catch (error) {
      alert("Error fetching tenant history: " + (error.response?.data?.error || error.message));
    } finally {
      setStatementLoading(false);
    }
  };

  // Function to fetch filtered transaction statement
  const fetchFilteredStatement = () => {
    if (selectedUnit) {
      fetchTransactionStatement(selectedUnit, statementStartDate, statementEndDate, selectedTenantForHistory);
    }
  };

  // Function to clear date filters
  const clearDateFilters = () => {
    setStatementStartDate("");
    setStatementEndDate("");
    if (selectedUnit) {
      fetchTransactionStatement(selectedUnit, "", "", selectedTenantForHistory);
    }
  };

  const getTenantMonthlyStatusVisibleInvoices = (status = tenantMonthlyStatus) => {
    const rows = Array.isArray(status?.invoiceRows) ? status.invoiceRows : [];
    return rows.slice(0, tenantMonthlyStatusVisibleInvoices);
  };
  const getPreferredStatementTenantId = (unit) => {
    if (!unit) return null;

    const directTenantId = normalizeId(unit?.tenant?._id || unit?.tenant);
    if (directTenantId) return directTenantId;

    const activeHistory = Array.isArray(unit?.tenantHistory)
      ? unit.tenantHistory.find((th) => th?.tenant && !th?.endDate)
      : null;
    const activeHistoryTenantId = normalizeId(activeHistory?.tenant?._id || activeHistory?.tenant);
    if (activeHistoryTenantId) return activeHistoryTenantId;

    if (Array.isArray(unit?.tenantHistory) && unit.tenantHistory.length > 0) {
      const latestHistory = [...unit.tenantHistory]
        .filter((th) => th?.tenant)
        .sort((a, b) => new Date(b?.startDate || 0) - new Date(a?.startDate || 0))[0];
      const latestHistoryTenantId = normalizeId(latestHistory?.tenant?._id || latestHistory?.tenant);
      if (latestHistoryTenantId) return latestHistoryTenantId;
    }

    if (Array.isArray(unit?.tenants) && unit.tenants.length > 0) {
      const latestTenantEntry = [...unit.tenants]
        .filter((t) => t?.user)
        .sort((a, b) => new Date(b?.assignedDate || 0) - new Date(a?.assignedDate || 0))[0];
      const tenantArrayId = normalizeId(latestTenantEntry?.user?._id || latestTenantEntry?.user);
      if (tenantArrayId) return tenantArrayId;
    }

    return null;
  };

  // Every UT Pay button asks first: the action spends the tenant's utility
  // wallet against their bills and cannot be undone. The pending unit is kept
  // here so one confirmation box serves the table, the cards and the modal.
  const [utPayConfirm, setUtPayConfirm] = useState(null);

  const askUtilitySettlementForUnit = (unit, options = {}) => {
    if (!unit) return;
    setUtPayConfirm({ unit, options });
  };

  const triggerUtilitySettlementForUnit = async (unit, options = {}) => {
    const {
      refreshModal = false,
      successMessage = true,
      buttonScope = "unit",
    } = options;

    const tenantId = getPreferredStatementTenantId(unit);
    const unitId = normalizeId(unit?._id);
    const propertyId = normalizeId(unit?.property?._id || unit?.property);

    if (!tenantId) {
      alert("No tenant is linked to this unit yet.");
      return null;
    }

    if (!unitId && !propertyId) {
      alert("Unit or property context is missing for this utility payment.");
      return null;
    }

    if (buttonScope === "modal") {
      if (utilitySettlementSubmitting) return null;
      setUtilitySettlementSubmitting(true);
    } else if (unitId) {
      setUtilitySettlementLoadingByUnit((current) => ({ ...current, [unitId]: true }));
    }

    try {
      const response = await api.post("/utilities/tenant-settle", {
        tenantId,
        unitId: unitId || null,
        propertyId: propertyId || null
      });

      if (refreshModal && selectedUtilityType) {
        await loadUtilityStatement(selectedUtilityType);
      }

      if (refreshModal && selectedWaterUnit?._id) {
        await loadWaterMeterModalData(selectedWaterUnit, {
          openModal: false,
          readingDateOverride: waterMeterForm.readingDate || formatDateInputValue(new Date())
        });
      }

      if (propertyId) {
        fetchUnits(propertyId);
      }

      const settlement = response?.data?.settlement || {};
      const totalPaid = Number(settlement?.totalPaid || settlement?.appliedAmount || 0);
      const paymentsProcessed = Number(settlement?.paymentsProcessed || 0);
      const remainingBalance = Number(settlement?.remainingBalance || 0);

      if (successMessage) {
        if (settlement?.triggered && totalPaid > 0) {
          alert(
            `Utility payments processed successfully. Applied ${formatMoney(totalPaid)} across ${paymentsProcessed} invoice${paymentsProcessed === 1 ? "" : "s"}. Remaining UT wallet balance: ${formatMoney(remainingBalance)}.`
          );
        } else {
          alert(
            response?.data?.message ||
            `No utility payments were processed. Remaining UT wallet balance: ${formatMoney(remainingBalance)}.`
          );
        }
      }

      return response?.data || null;
    } catch (error) {
      alert("Error triggering utility payments: " + (error.response?.data?.error || error.message));
      return null;
    } finally {
      if (buttonScope === "modal") {
        setUtilitySettlementSubmitting(false);
      } else if (unitId) {
        setUtilitySettlementLoadingByUnit((current) => {
          const next = { ...current };
          delete next[unitId];
          return next;
        });
      }
    }
  };
  const getUnitCurrentTenants = (unit) => {
    if (!unit) return [];

    const activeHistory = Array.isArray(unit?.tenantHistory)
      ? unit.tenantHistory
          .filter((entry) => entry?.tenant && !entry?.endDate)
          .sort((a, b) => new Date(b?.startDate || 0) - new Date(a?.startDate || 0))
      : [];

    if (activeHistory.length > 0) {
      return activeHistory.map((entry, index) => ({
        key: `history-${normalizeId(entry?.tenant?._id || entry?.tenant) || index}`,
        user: entry.tenant,
        assignedDate: entry.startDate || entry.assignedDate || null,
        role: entry.role || "tenant",
      }));
    }

    const tenants = Array.isArray(unit?.tenants)
      ? unit.tenants.filter((entry) => entry?.user)
      : [];

    if (tenants.length === 0) return [];

    const latestAssignedTime = tenants.reduce((latest, entry) => {
      const assignedTime = new Date(entry?.assignedDate || 0).getTime();
      if (!Number.isFinite(assignedTime)) return latest;
      return assignedTime > latest ? assignedTime : latest;
    }, Number.NEGATIVE_INFINITY);

    if (Number.isFinite(latestAssignedTime)) {
      return tenants
        .filter((entry) => new Date(entry?.assignedDate || 0).getTime() === latestAssignedTime)
        .map((entry, index) => ({
          key: `tenant-${normalizeId(entry?.user?._id || entry?.user) || index}`,
          user: entry.user,
          assignedDate: entry.assignedDate || null,
          role: entry.role || "tenant",
        }));
    }

    const fallbackTenant = tenants[tenants.length - 1];
    if (!fallbackTenant) return [];

    return [
      {
        key: `tenant-${normalizeId(fallbackTenant?.user?._id || fallbackTenant?.user) || "fallback"}`,
        user: fallbackTenant.user,
        assignedDate: fallbackTenant.assignedDate || null,
        role: fallbackTenant.role || "tenant",
      },
    ];
  };
  const getUnitMoveInDate = (unit) => {
    if (!unit) return null;

    const activeHistory = Array.isArray(unit?.tenantHistory)
      ? [...unit.tenantHistory]
          .filter((entry) => entry?.tenant && !entry?.endDate && entry?.startDate)
          .sort((a, b) => new Date(b?.startDate || 0) - new Date(a?.startDate || 0))
      : [];

    if (activeHistory.length > 0) {
      return activeHistory[0].startDate;
    }

    const currentTenants = getUnitCurrentTenants(unit);
    const currentAssignedDate = currentTenants.find((entry) => entry?.assignedDate)?.assignedDate;
    if (currentAssignedDate) {
      return currentAssignedDate;
    }

    return unit?.fromDate || unit?.moveInDate || null;
  };
  const getUnitStatusPillClasses = (status) => {
    switch (status) {
      case "Occupied":
        return "bg-emerald-100 text-emerald-900 border border-emerald-200";
      case "Reserved":
        return "bg-amber-100 text-amber-900 border border-amber-200";
      case "Vacant":
        return "bg-rose-100 text-rose-900 border border-rose-200";
      default:
        return "bg-slate-100 text-slate-700 border border-slate-200";
    }
  };
  const formatStatusDate = (value) => {
    const formatted = formatDateWithTime(value, true);
    if (!formatted) return "";
    return formatted.split(" ")[0].replace(/\//g, " ");
  };

  const downloadTenantInvoicesAsPDF = () => {
    const invoiceRows = Array.isArray(tenantMonthlyStatus?.invoiceRows)
      ? tenantMonthlyStatus.invoiceRows
      : [];
    if (!tenantMonthlyStatus || invoiceRows.length === 0) {
      alert("No invoice data to export");
      return;
    }

    const doc = new jsPDF();
    const margin = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - margin * 2;
    const columns = [
      { label: "Date", width: 25, align: "left" },
      { label: "Description", width: 62, align: "left" },
      { label: "Invoice", width: 27, align: "right" },
      { label: "Paid", width: 27, align: "right" },
      { label: "Balance", width: 27, align: "right" },
      { label: "Cleared", width: contentWidth - 168, align: "left" },
    ];
    const rowHeight = 8;

    const drawTableHeader = (y) => {
      doc.setFillColor(226, 232, 240);
      doc.rect(margin, y, contentWidth, rowHeight, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      let x = margin;
      columns.forEach((column) => {
        const textX = column.align === "right" ? x + column.width - 2 : x + 2;
        doc.text(column.label, textX, y + 5.5, { align: column.align });
        x += column.width;
      });
      return y + rowHeight;
    };

    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    const tenantName = String(tenantMonthlyStatus.tenantName || "Tenant");
    doc.text(`${tenantName} - Rental Invoices`, margin, 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Property: ${tenantMonthlyStatus.propertyName || "-"}`, margin, 27);
    doc.text(`Unit: ${tenantMonthlyStatus.unitLabel || "-"}`, margin, 33);
    doc.text(`Tenant: ${tenantName}`, margin, 39);
    doc.text(`Net Balance: ${formatMoney(tenantMonthlyStatus.summary?.netBalance || 0)}`, margin, 47);
    doc.text(`Outstanding: ${formatMoney(tenantMonthlyStatus.summary?.totalOutstanding || 0)}`, margin + 70, 47);

    let y = drawTableHeader(54);
    invoiceRows.forEach((row) => {
      if (y + rowHeight > pageHeight - margin) {
        doc.addPage();
        y = drawTableHeader(margin);
      }
      const values = [
        formatStatusDate(row.date) || "-",
        String(row.description || "Invoice"),
        formatMoney(row.invoiceAmount),
        formatMoney(row.paidAmount),
        formatMoney(row.balance),
        formatStatusDate(row.clearedDate) || "-",
      ];
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      let x = margin;
      columns.forEach((column, index) => {
        const value = index === 1 && values[index].length > 35
          ? `${values[index].slice(0, 32)}...`
          : values[index];
        const textX = column.align === "right" ? x + column.width - 2 : x + 2;
        doc.text(value, textX, y + 5.5, { align: column.align });
        x += column.width;
      });
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, y + rowHeight, margin + contentWidth, y + rowHeight);
      y += rowHeight;
    });

    const safeUnit = String(tenantMonthlyStatus.unitLabel || "unit").replace(/[^a-z0-9_-]+/gi, "-");
    const safeTenant = tenantName.replace(/[^a-z0-9_-]+/gi, "-");
    doc.save(`${safeTenant}-rental-invoices-${safeUnit}.pdf`);
  };

  const downloadTenantInvoicePaymentsAsPDF = () => {
    if (!tenantMonthlyStatus || !selectedTenantInvoiceRow) return;

    const invoiceId = selectedTenantInvoiceRow.invoiceId
      ? String(selectedTenantInvoiceRow.invoiceId)
      : "";
    const rows = invoiceId && tenantMonthlyStatus.invoicePayments
      ? tenantMonthlyStatus.invoicePayments[invoiceId]
      : [];
    const paymentRows = Array.isArray(rows) ? rows : [];
    const tenantName = String(tenantMonthlyStatus.tenantName || "Tenant");
    const doc = new jsPDF();
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - margin * 2;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Invoice Payments", margin, 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Tenant: ${tenantName}`, margin, 27);
    doc.text(`Property: ${tenantMonthlyStatus.propertyName || "-"}`, margin, 34);
    doc.text(`Unit: ${tenantMonthlyStatus.unitLabel || "-"}`, margin, 41);
    doc.text(`Invoice date: ${formatStatusDate(selectedTenantInvoiceRow.date) || "-"}`, margin, 48);
    doc.text(`Description: ${selectedTenantInvoiceRow.description || "Invoice"}`, margin, 55);
    doc.text(`Invoice: ${formatMoney(selectedTenantInvoiceRow.invoiceAmount || 0)}`, margin, 64);
    doc.text(`Paid: ${formatMoney(selectedTenantInvoiceRow.paidAmount || 0)}`, margin + 65, 64);
    doc.text(`Balance: ${formatMoney(selectedTenantInvoiceRow.balance || 0)}`, margin + 125, 64);

    const columns = [
      { label: "Date", width: 42, align: "left" },
      { label: "Type", width: 30, align: "left" },
      { label: "Description", width: 75, align: "left" },
      { label: "Paid", width: contentWidth - 147, align: "right" },
    ];
    const rowHeight = 9;
    const drawHeader = (startY) => {
      doc.setFillColor(226, 232, 240);
      doc.rect(margin, startY, contentWidth, rowHeight, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      let x = margin;
      columns.forEach((column) => {
        doc.text(column.label, column.align === "right" ? x + column.width - 2 : x + 2, startY + 6, {
          align: column.align,
        });
        x += column.width;
      });
      return startY + rowHeight;
    };

    let y = drawHeader(72);
    if (paymentRows.length === 0) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text("No payments found for this invoice.", margin + 2, y + 7);
    } else {
      paymentRows.forEach((row) => {
        if (y + rowHeight > pageHeight - margin) {
          doc.addPage();
          y = drawHeader(margin);
        }
        const type = String(row?.type || "").toLowerCase();
        const typeLabel = type === "payment" ? "Payment" : type === "waiver" ? "Waiver" : "Credit";
        const values = [
          formatDateWithTime(row?.date, true) || "-",
          typeLabel,
          String(row?.description || "-"),
          formatMoney(row?.amountApplied || 0),
        ];
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7.5);
        let x = margin;
        columns.forEach((column, index) => {
          const value = index === 2 && values[index].length > 42
            ? `${values[index].slice(0, 39)}...`
            : values[index];
          doc.text(value, column.align === "right" ? x + column.width - 2 : x + 2, y + 6, {
            align: column.align,
          });
          x += column.width;
        });
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, y + rowHeight, margin + contentWidth, y + rowHeight);
        y += rowHeight;
      });
    }

    const safeTenant = tenantName.replace(/[^a-z0-9_-]+/gi, "-");
    const safeInvoice = invoiceId.replace(/[^a-z0-9_-]+/gi, "-") || "invoice";
    doc.save(`${safeTenant}-invoice-payments-${safeInvoice}.pdf`);
  };

  const getTenantAssignmentWalletTotals = (unit) => {
    const deposit = Number(unit?.deposit || 0);
    const rent = Number(unit?.rentAmount || 0);
    const safeDeposit = Number.isFinite(deposit) ? deposit : 0;
    const safeRent = Number.isFinite(rent) ? rent : 0;
    return {
      deposit: safeDeposit,
      rent: safeRent,
      total: safeDeposit + safeRent,
    };
  };

  // Function to download statement as PDF
  const downloadStatementAsPDF = () => {
    if (!transactionStatement || !transactionStatement.transactions?.length) {
      alert("No transaction data to export");
      return;
    }

    const doc = new jsPDF();

    // Helpers
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 15;
    const usableW = pageW - margin * 2;
    const topY = 20;
    const bottomMargin = 20;

    // Column layout (responsive to page width)
    // widths sum should be <= usableW
    const col = {
      date: 24,
      invoice: 28,
      payment: 28,
      balance: 32,
    };
    // Description takes the remaining space
    col.description = usableW - (col.date + col.invoice + col.payment + col.balance);

    const x = {
      date: margin,
      invoice: margin + col.date,
      payment: margin + col.date + col.invoice,
      description: margin + col.date + col.invoice + col.payment,
      // balance will be right-aligned to margin + usableW
      balanceRight: margin + usableW,
    };

    // Fonts
    doc.setFont('helvetica');

    // Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Transaction Statement', margin, topY);

    // Property and Unit Info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');

    let infoY = topY + 15;
    doc.text(`Property: ${transactionStatement.propertyName}`, margin, infoY); infoY += 10;
    doc.text(`Unit: ${transactionStatement.unitLabel}`, margin, infoY); infoY += 10;

    const tenantText = transactionStatement.tenantNames?.length
      ? transactionStatement.tenantNames.join(', ')
      : 'N/A';
    // Wrap tenant line if needed
    const tenantLines = doc.splitTextToSize(`Tenant: ${tenantText}`, usableW);
    doc.text(tenantLines, margin, infoY);
    infoY += 10 + (tenantLines.length - 1) * 6;

    // Date range if filtered
    if (transactionStatement.dateFilter?.filtered) {
      const { startDate, endDate } = transactionStatement.dateFilter;
      const dateRange = startDate && endDate
        ? `${startDate} to ${endDate}`
        : startDate
          ? `From ${startDate}`
          : `Until ${endDate}`;
      const drLines = doc.splitTextToSize(`Date Range: ${dateRange}`, usableW);
      doc.text(drLines, margin, infoY);
      infoY += 10 + (drLines.length - 1) * 6;
    }

    // Summary information
    if (transactionStatement.summary) {
      const { currentBalance } = transactionStatement.summary;
      doc.text(`Current Balance: ${formatMoney(currentBalance)}`, margin, infoY + 10);
      infoY += 40; // give some breathing room before table
    } else {
      infoY += 10;
    }

    // Table header drawer
    const drawHeader = (y) => {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Date', x.date, y);
      doc.text('Invoice', x.invoice, y);
      doc.text('Payment', x.payment, y);
      doc.text('Description', x.description, y);
      doc.text('Balance', x.balanceRight, y, { align: 'right' });
      doc.line(margin, y + 2, margin + usableW, y + 2);
    };

    let yPos = Math.max(infoY, 85);
    drawHeader(yPos);
    yPos += 10;
    // Table rows
    doc.setFont('helvetica', 'normal');
    const lineGap = 6; // line height for wrapped text
    const rowPaddingY = 2;

    transactionStatement.transactions.forEach((t, idx) => {
      const date = formatDateLong(t.date);
      const invoice = t.invoiceAmount !== 0 ? `${formatMoney(t.invoiceAmount)}` : '';
      const payment = t.paymentAmount > 0 ? `${formatMoney(t.paymentAmount)}` : '';
      const descriptionRaw = t.description || '';
      // Wrap description to column width
      const descLines = doc.splitTextToSize(descriptionRaw, col.description - 2); // small inner padding
      const rowHeight = Math.max(lineGap, descLines.length * lineGap) + rowPaddingY * 2;

      // Page break if needed (keep footer space)
      if (yPos + rowHeight > pageH - bottomMargin) {
        doc.addPage();
        yPos = topY;
        drawHeader(yPos);
        yPos += 10;
      }

      // Draw row
      // Date / Invoice / Payment at single line
      doc.text(date, x.date, yPos + rowPaddingY + lineGap - 2);
      if (invoice) doc.text(invoice, x.invoice, yPos + rowPaddingY + lineGap - 2);
      if (payment) doc.text(payment, x.payment, yPos + rowPaddingY + lineGap - 2);

      // Description (multi-line)
      doc.text(descLines, x.description, yPos + rowPaddingY + lineGap - 2);

      // Balance (right aligned)
      const balanceStr = `${formatMoney(t.runningBalance)}`;
      doc.text(balanceStr, x.balanceRight, yPos + rowPaddingY + lineGap - 2, { align: 'right' });

      yPos += rowHeight;
    });

    // Footer
    const footerY = Math.min(pageH - 8, yPos + 20);
    if (footerY > pageH - bottomMargin) {
      doc.addPage();
    }
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    const now = new Date();
    doc.text(
      `Generated on ${formatDateLong(now)} at ${formatTimeOnly(now)}`,
      margin,
      pageH - 14
    );
    doc.text('This document is read-only and cannot be modified.', margin, pageH - 8);

    // Generate filename and save
    const safe = (s) => String(s).replace(/[/\\?%*:|"<>]/g, '-');
    const todayStr = formatDateInputValue(new Date());
    const fileName = `Statement_${safe(transactionStatement.propertyName)}_${safe(transactionStatement.unitLabel)}_${todayStr}.pdf`;
    doc.save(fileName);
  };
  const vacateUnit = async (e) => {
    e.preventDefault();
    setVacateLoading(true);

    // Validate form
    if (!vacateForm.vacateDate) {
      alert("Please enter a vacate date");
      setVacateLoading(false);
      return;
    }

    if (vacateForm.confirmVacate !== "VACATE") {
      alert('Please type "VACATE" to confirm the action');
      setVacateLoading(false);
      return;
    }

    try {
      const response = await api.put(`/frp/units/${selectedUnit._id}/vacate`, vacateForm);

      // Refresh units list
      if (selectedProperty) {
        fetchUnits(selectedProperty._id);
      }
      fetchMyUnits();

      // Reset form and close modal
      setVacateForm({ vacateDate: "", confirmVacate: "" });
      setShowVacateForm(false);

      // Backend currently returns `removedTenant` (singular). Fall back gracefully if renamed.
      const removedCount = response.data.removedTenants ?? response.data.removedTenant ?? 1;
      alert(`Unit vacated successfully! ${removedCount} tenant(s) removed. ${response.data.details}`);
    } catch (error) {
      alert("Error vacating unit: " + (error.response?.data?.error || error.message));
    } finally {
      setVacateLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const selectProperty = (property) => {
    setSelectedProperty(property);
    setActiveTab("units");
    fetchUnits(property._id);
  };

  // Filter properties based on selected company and search term
  const filteredProperties = properties.filter(property => {
    // First filter by selected company
    const belongsToCompany = property.managementCompany === selectedCompany?._id ||
      (property.managementCompany && property.managementCompany._id === selectedCompany?._id);

    if (!belongsToCompany) return false;

    // Then filter by search term if there is one
    if (!propertySearch) return true;

    const searchTerm = propertySearch.toLowerCase();
    return (
      property.name.toLowerCase().includes(searchTerm) ||
      (property.address && property.address.toLowerCase().includes(searchTerm))
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 font-sans" aria-busy="true" aria-label="Loading rentals">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-4">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
            <Skeleton className="h-9 w-20 bg-white/25" rounded="rounded-lg" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-5 w-40 bg-white/25" />
              <Skeleton className="h-3 w-28 bg-white/20" />
            </div>
            <Skeleton className="h-10 w-24 bg-white/25" rounded="rounded-xl" />
          </div>
        </div>
        <div className="mx-auto max-w-7xl space-y-4 px-4 py-6">
          <SkeletonStatGrid count={4} />
          <SkeletonTable rows={6} columns={5} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Header */}
      <header className="bg-green-900 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">

          {/* ── Left: back + brand ── */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-1.5 shrink-0 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back</span>
            </button>

            <div className="flex items-center gap-2 min-w-0">
              <img
                src="/logofinalrounded.png"
                alt="RecordLOGS"
                className="w-8 h-8 rounded-lg object-cover shrink-0"
              />
              <span className="text-white font-bold text-lg leading-none">FRP</span>
            </div>
          </div>

          {/* ── Right: pills + avatar + menu ── */}
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Desktop info pills */}
            <div className="hidden md:flex items-center gap-2 mr-2">
              <span className="bg-white/10 text-green-100 text-xs font-medium px-2.5 py-1 rounded-full">
                {getUserRoleDisplay(user)}
              </span>
              {wallet && (
                <span className="bg-white/10 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                  {wallet.balance != null ? formatMoney(wallet.balance) : "0.00"}
                </span>
              )}
            </div>

            {/* Avatar + name */}
            <button
              onClick={handleProfileClick}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-green-800 transition-colors"
            >
              {user?.profilePhoto?.url ? (
                <img
                  src={user.profilePhoto.url}
                  alt="Profile"
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {getInitials(user?.name)}
                </div>
              )}
              <span className="hidden sm:block text-white text-sm font-medium leading-none">
                {getShortName(user?.name)}
              </span>
            </button>

            {/* Menu toggle */}
            <div className="relative dropdown-container">
              <button
                onClick={toggleMenu}
                className="flex items-center justify-center w-9 h-9 rounded-lg text-white hover:bg-green-800 transition-colors"
              >
                <Icon name="list" size="1.25rem" />
              </button>

              <div className={`absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 transition-all duration-150 z-20 ${isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                <div className="p-1.5">
                  <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">Register</p>
                  <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-2">
                    <Icon name="people" size="1rem" />
                    Group
                  </button>
                  <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-2">
                    <Icon name="car-front" size="1rem" />
                    Vehicle
                  </button>
                  <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg flex items-center gap-2">
                    <Icon name="mortarboard" size="1rem" />
                    School
                  </button>
                  <div className="h-px bg-gray-100 my-1" />
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2"
                  >
                    <Icon name="box-arrow-right" size="1rem" />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </header>
      {/* Main Content */}
      <main className={`max-w-6xl mx-auto p-4 sm:p-6 lg:p-8${activeRole === 'tenant' ? ' bg-emerald-50/40 min-h-screen' : ''}`}>
        {/* Welcome Section */}
        {user && getUserRoles(user).length > 0 && !activeRole && (
          <div className="mb-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mb-4 shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Rentals</h2>
              <p className="mt-1 text-sm text-gray-500">Welcome back{user?.name ? `, ${getShortName(user.name)}` : ""} — pick a workspace to continue</p>
            </div>
          </div>
        )}

        {/* Role Selection Buttons - Always show for all users */}
        {user && getUserRoles(user).length > 0 && !activeRole && (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 sm:p-6 rounded-2xl border border-green-100 shadow-sm">
              {/* FRP PaySCORE for Tenants only, but only if they have units */}
              {hasAnyTenantAccess(user) && Array.isArray(myUnits) && myUnits.length > 0 && (
                <div className="mb-8 flex flex-col items-center">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-sm border border-green-100/50 flex flex-col items-center w-full max-w-sm mx-auto">
                    <PayScoreGauge
                      score={payScore?.modules?.frp?.score}
                      label="FRP PaySCORE"
                      status={payScore?.modules?.frp?.status}
                    />
                    <p className="mt-3 text-[10px] text-gray-500 text-center font-medium">
                      Based on your rent payment performance.
                    </p>
                    <p className="mt-3 text-[10px] text-gray-500 text-center font-medium">
                      Over the LAST 36 Payment Cycles (Sometimes up to 84 Payment Cycles)
                    </p>
                  </div>
                </div>
              )}

              <div className="mb-4 text-center">
                <h3 className="text-lg font-bold text-gray-900">Choose your role</h3>
                <p className="mt-1 text-xs text-gray-500">Select how you want to use FOR Rent right now</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {getUserRoles(user).map((role) => {
                  const meta = ROLE_VIEW_META[role] || DEFAULT_ROLE_VIEW_META;
                  return (
                    <button
                      key={role}
                      onClick={() => setActiveRole(role)}
                      {...(role === "manager" ? { "data-tutorial": "role-pm-btn" } : {})}
                      className={`group relative flex items-center gap-3 overflow-hidden rounded-2xl border border-gray-200 bg-white p-3.5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${meta.hover}`}
                    >
                      <span className={`absolute inset-y-0 left-0 w-1 bg-gradient-to-b ${meta.accent}`} />
                      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${meta.iconWrap}`}>
                        <Icon name={meta.icon} size="1.4rem" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-bold text-gray-900">{getRoleDisplayName(role)}</span>
                        <span className="mt-0.5 block text-[11px] leading-tight text-gray-500">{meta.description}</span>
                      </span>
                      <Icon
                        name="arrow-right-circle-fill"
                        size="1.15rem"
                        className={`shrink-0 -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100 ${meta.arrow}`}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Active Role Display - Show when role is selected */}
        {activeRole && (
          <div className="mb-8">
            <div className="relative overflow-hidden bg-white p-3 sm:p-4 rounded-2xl border border-gray-200 shadow-sm">
              {(() => {
                const m = ROLE_VIEW_META[activeRole] || DEFAULT_ROLE_VIEW_META;
                return (
                  <>
                    <span className={`absolute inset-y-0 left-0 w-1 bg-gradient-to-b ${m.accent}`} />
                    <div className="flex flex-row items-center justify-between gap-3 pl-2">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className={`flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xl ${m.iconWrap}`}>
                          <Icon name={m.icon} size="1.3rem" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 leading-none">Active role</p>
                          <h3 className="mt-0.5 truncate text-base sm:text-lg font-bold text-gray-900 leading-tight">{getRoleDisplayName(activeRole)}</h3>
                          <p className="hidden sm:block text-[11px] text-gray-500 leading-tight">{m.description}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setActiveRole(null)}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 sm:px-3.5 sm:py-2 text-xs sm:text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
                      >
                        <Icon name="arrow-left-right" size="0.85rem" />
                        <span>Switch</span>
                      </button>
                    </div>
                  </>
                );
              })()}
              {/* Inline Monthly Summary for Active Role (hidden for tenants) */}
              {canViewMonthlySummary(activeRole) && summaryRole && monthlySummary && (
                <div className="mt-2">
                  <div className="bg-white rounded-xl border border-gray-200 p-1">
                    {/* Month/Year Selector (inline) */}
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 p-2">
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Month</label>
                          <select
                            value={selectedSummaryMonth}
                            onChange={(e) => setSelectedSummaryMonth(parseInt(e.target.value))}
                            className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
                          >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                              <option key={month} value={month}>
                                {getMonthName(month)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
                          <select
                            value={selectedSummaryYear}
                            onChange={(e) => setSelectedSummaryYear(parseInt(e.target.value))}
                            className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
                          >
                            {Array.from({ length: 5 }, (_, i) => getNairobiYear() - i).map(year => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                        </div>
                        <button
                          onClick={() => fetchMonthlySummary(selectedSummaryMonth, selectedSummaryYear)}
                          disabled={monthlySummaryLoading}
                          className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-xs"
                        >
                          {monthlySummaryLoading ? 'Loading...' : 'Update'}
                        </button>
                      </div>
                      <div className="text-xs text-gray-600">Period: {monthlySummary.period}</div>
                    </div>

                    {monthlySummaryLoading ? (
                      /* Same four tiles as the loaded summary. A short "Loading
                         summary..." line here used to collapse the card, which
                         slid the Transactions and Trigger Payment buttons up
                         under the finger that was already reaching for them. */
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" aria-busy="true" aria-label="Loading summary">
                        {[0, 1, 2, 3].map((tile) => (
                          <div key={tile} className="bg-gray-50 p-3 rounded-lg">
                            {tile === 3 ? (
                              <div className="space-y-1.5">
                                {[0, 1, 2, 3].map((line) => (
                                  <div key={line} className="flex items-center justify-between gap-2">
                                    <Skeleton className="h-2.5 w-16" />
                                    <Skeleton className="h-2.5 w-8" />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <>
                                <Skeleton className="h-3 w-24" />
                                <Skeleton className="mt-2 h-5 w-20" />
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (() => {
                      const roleLabel = summaryRole === 'manager' ? 'Management' : summaryRole === 'landlord' ? 'Landlord' : summaryRole === 'tenant' ? 'Tenant' : null;
                      const section = (monthlySummary.sections || []).find(s => s.role === roleLabel);
                      if (!section) {
                        return (
                          <div className="text-sm text-gray-500">No data found for this role in {monthlySummary.period}.</div>
                        );
                      }

                      if (roleLabel === 'Management') {
                        const grossRevenue = section.totalPaymentsProcessed ?? 0;
                        const mgmtCommission = section.totalCommissionEarned ?? 0;
                        const landlordShare = section.totalLandlordShare ?? (grossRevenue - mgmtCommission - (section.totalPlatformFee ?? 0));
                        const totalUnits = section.unitsManaged ?? 0;
                        return (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-600">Gross Revenue</p>
                              <p className="text-base font-bold text-green-600">{formatMoney(grossRevenue)}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-600">Mgmt Commission</p>
                              <p className="text-base font-bold text-green-600">{formatMoney(mgmtCommission)}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-600">Landlord Share</p>
                              <p className="text-base font-bold text-purple-600">{formatMoney(landlordShare)}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <div className="space-y-1 text-xs text-gray-700 leading-tight">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-gray-600">Properties</span>
                                  <span className="font-semibold text-gray-900">{section.propertiesManaged ?? 0}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-gray-600">Total Units</span>
                                  <span className="font-semibold text-gray-900">{section.unitsManaged ?? 0}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-gray-600">Occupied</span>
                                  <span className="font-semibold text-gray-900">{section.occupiedUnits ?? 0}{totalUnits > 0 ? ` (${Math.round(((section.occupiedUnits ?? 0) / totalUnits) * 100)}%)` : ''}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-gray-600">Vacant</span>
                                  <span className="font-semibold text-gray-900">{section.vacantUnits ?? 0}{totalUnits > 0 ? ` (${Math.round(((section.vacantUnits ?? 0) / totalUnits) * 100)}%)` : ''}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      if (roleLabel === 'Landlord') {
                        const grossRevenue = section.totalPaymentsReceived ?? 0;
                        const netRevenue = section.totalIncomeEarned ?? 0;
                        const mgmtCommission = section.totalCommissionPaid ?? 0;
                        const portalFee = grossRevenue - netRevenue - mgmtCommission;
                        return (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-600">Gross Revenue</p>
                              <p className="text-base font-bold text-green-600">{formatMoney(grossRevenue)}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-600">Net Revenue</p>
                              <p className="text-base font-bold text-green-600">{formatMoney(netRevenue)}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-600">Mgmt Commission</p>
                              <p className="text-base font-bold text-red-600">{formatMoney(mgmtCommission)}</p>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <p className="text-xs text-gray-600">Portal Fee</p>
                              <p className="text-base font-bold text-purple-600">{formatMoney(portalFee)}</p>
                            </div>
                          </div>
                        );
                      }

                      // Tenant
                      return (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-xs text-gray-600">Total Paid</p>
                            <p className="text-base font-bold text-red-600">{section.totalPaid != null ? formatMoney(section.totalPaid) : '0.00'}</p>
                            <p className="text-[11px] text-gray-500">{section.paymentsCount} payments</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-xs text-gray-600">Average Payment</p>
                            <p className="text-base font-bold text-green-600">{section.averagePayment}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Management-level Actions under Summary */}
              {activeRole === 'manager' && canManageManagement() && (
                <div className="mt-3 flex flex-row flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setShowManagementTransactionsModal(true);
                      const { start, end } = getCurrentMonthRange();
                      setMgmtStartDate(start);
                      setMgmtEndDate(end);
                      fetchManagementTransactions();
                    }}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg hover:shadow-md hover:shadow-green-500/25 transition-all duration-200 font-medium flex items-center gap-2 w-full justify-center text-xs sm:text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6a2 2 0 012-2h8m-6 8h6m-6-4h6M7 7h.01M4 17h.01" />
                    </svg>
                    Transactions
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Trigger payments from all tenant wallets with unpaid rent?')) return;
                      try {
                        setTriggeringPayments(true);
                        setTriggerSummary(null);
                        const storageKey = `recordlogs.manager-rent-batch.${user?._id || user?.id || "user"}`;
                        const requestKey = getStableRequestKey({
                          storageKey,
                          fingerprint: buildRequestFingerprint({
                            userId: user?._id || user?.id || "user",
                            scope: "all-managed-properties",
                          }),
                          prefix: "manager-rent-batch",
                        });
                        const resp = await api.post('/frp/manager/trigger-payments', {
                          idempotencyKey: requestKey,
                        });
                        if (!resp.data?.retryWithSameRequestKey) {
                          clearStableRequestKey({ storageKey, requestKey });
                        }
                        setTriggerSummary(resp.data);
                        // Refresh transactions after triggering
                        await fetchManagementTransactions();
                        // Refresh units data to update balances in UI
                        if (selectedProperty) {
                          await fetchUnits(selectedProperty._id);
                        }
                        // Refresh monthly summary to update gross revenue
                        await fetchMonthlySummary();
                        alert(`Triggered payments: ${resp.data.summary.paymentsProcessed} processed, Collected ${formatMoney(resp.data.summary.totalCollected)}`);
                      } catch (e) {
                        alert('Error triggering payments: ' + (e.response?.data?.error || e.message));
                      } finally {
                        setTriggeringPayments(false);
                      }
                    }}
                    disabled={triggeringPayments || managementTransactionsLoading}
                    className={`bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg transition-all duration-200 font-medium flex items-center gap-2 w-full justify-center text-xs sm:text-sm ${triggeringPayments ? 'opacity-80 cursor-not-allowed' : 'hover:shadow-md hover:shadow-emerald-500/25'}`}
                  >
                    {triggeringPayments ? 'Processing…' : 'Trigger Payment'}
                  </button>
                </div>
              )}

              {/* Landlord-level Actions under Summary */}
              {activeRole === 'landlord' && (
                <div className="mt-3 flex flex-row flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setShowLandlordTransactionsModal(true);
                      const { start, end } = getCurrentMonthRange();
                      setLdStartDate(start);
                      setLdEndDate(end);
                      fetchLandlordTransactions();
                    }}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg hover:shadow-md hover:shadow-green-500/25 transition-all duration-200 font-medium flex items-center gap-2 w-full justify-center text-xs sm:text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6a2 2 0 012-2h8m-6 8h6m-6-4h6M7 7h.01M4 17h.01" />
                    </svg>
                    Transactions
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* No Roles - Show Management Company Creation */}
        {user && getUserRoles(user).length === 0 && (
          <div className="mb-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mb-6 shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">FOR Rent</h2>
            </div>
          </div>
        )}
        {/* Role-based Content */}
        {activeRole === "manager" && isManager(user) && (
          <>
            {/* Units View - Separate Page */}
            {showUnitsView && selectedProperty ? (
              <div>
                <div className="flex items-center gap-4 mb-8">
                  <button
                    onClick={handleBackToProperties}
                    className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Properties
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Units List - <span className="text-green-600">{selectedProperty.name}</span>
                    </h2>
                    <p className="text-gray-600 mt-1">
                      {canManageProperty(selectedProperty) ? "Manage units and tenant assignments" : "View units and tenants"}
                    </p>
                  </div>
                  <div className="flex flex-row flex-wrap gap-2 sm:gap-3">
                    {canManageProperty(selectedProperty) && (
                      <>
                        <button
                          onClick={() => handleUpdateProperty(selectedProperty)}
                          style={{ width: '100px' }}
                          className="bg-gradient-to-r from-green-500 to-green-600 text-white px-2 py-2 text-xs sm:px-4 sm:py-2 sm:text-sm rounded-md sm:rounded-lg hover:shadow-md sm:hover:shadow-lg hover:shadow-green-500/25 transition-all duration-200 w-auto font-medium flex items-center gap-2 justify-center"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Update Property
                        </button>
                        <button
                          onClick={() => openPropertyWaterStatements(selectedProperty)}
                          style={{ width: '120px' }}
                          className="bg-gradient-to-r from-cyan-500 to-sky-600 text-white px-2 py-2 text-xs sm:px-4 sm:py-2 sm:text-sm rounded-md sm:rounded-lg hover:shadow-md sm:hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200 w-auto font-medium flex items-center gap-2 justify-center"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3c3 4 6 7.2 6 10.2A6 6 0 1112 3z" />
                          </svg>
                          UTILITY-Bills
                        </button>
                        <button
                          onClick={() => setShowUnitForm(true)}
                          data-tutorial="add-unit-btn"
                          style={{ width: '100px' }}
                          className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-2 py-2 text-xs sm:px-4 sm:py-2 sm:text-sm rounded-md sm:rounded-lg hover:shadow-md sm:hover:shadow-lg hover:shadow-green-500/25 transition-all duration-200 w-auto font-medium flex items-center gap-2 justify-center"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Unit
                        </button>
                        <button
                          onClick={() => {
                            setShowPropertyStatementsModal(true);
                            const { start, end } = getCurrentMonthRange();
                            setPropertyStatementStartDate(start);
                            setPropertyStatementEndDate(end);
                            fetchPropertyPayments(selectedProperty._id);
                          }}
                          style={{ width: '100px' }}
                          className="bg-gradient-to-r from-green-500 to-green-600 text-white px-2 py-2 text-xs sm:px-4 sm:py-2 sm:text-sm rounded-md sm:rounded-lg hover:shadow-md sm:hover:shadow-lg hover:shadow-green-500/25 transition-all duration-200 w-auto font-medium flex items-center gap-2 justify-center"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6a2 2 0 012-2h8m-6 8h6m-6-4h6M7 7h.01M4 17h.01" />
                          </svg>
                          Statements
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Search Input for Units */}
                <div className="mb-6">
                  <div className="flex gap-4">
                    {/* Search Input */}
                    <div className="flex-1 relative">
                      <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search by unit number or tenant name..."
                        value={unitsSearch}
                        onChange={(e) => setUnitsSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                      {unitsSearch && (
                        <button
                          onClick={() => setUnitsSearch("")}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Balance Filter Dropdown */}
                    {canManageProperty(selectedProperty) && (
                      <div className="relative">
                        <select
                          value={unitsBalanceFilter}
                          onChange={(e) => setUnitsBalanceFilter(e.target.value)}
                          className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-8 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm font-medium text-gray-700 min-w-[180px]"
                        >
                          <option value="">Sort by Balance</option>
                          <option value="highest">Highest Balance</option>
                          <option value="lowest">Lowest Balance</option>
                        </select>
                        <svg className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {(() => {
                  // Filter units based on search term and sort by balance
                  let filteredUnits = units.filter(unit => {
                    if (!unitsSearch) return true;

                    const searchTerm = unitsSearch.toLowerCase();

                    // Search by unit number/label
                    if (unit.label.toLowerCase().includes(searchTerm)) {
                      return true;
                    }

                    // Search by tenant names and ID numbers (multiple tenants support)
                    if (unit.tenants && unit.tenants.length > 0) {
                      const hasMatch = unit.tenants.some((tenant) => {
                        const user = tenant.user || {};
                        const name = user.name ? user.name.toLowerCase() : "";
                        const idNumber = user.idNumber != null ? String(user.idNumber).toLowerCase() : "";

                        return (
                          (name && name.includes(searchTerm)) ||
                          (idNumber && idNumber.includes(searchTerm))
                        );
                      });
                      if (hasMatch) return true;
                    }

                    // Search by legacy tenant field (name + ID)
                    if (unit.tenant) {
                      const legacyName = unit.tenant.name ? unit.tenant.name.toLowerCase() : "";
                      const legacyId = unit.tenant.idNumber != null ? String(unit.tenant.idNumber).toLowerCase() : "";

                      if (
                        (legacyName && legacyName.includes(searchTerm)) ||
                        (legacyId && legacyId.includes(searchTerm))
                      ) {
                        return true;
                      }
                    }

                    return false;
                  });

                  // Apply sorting: balance when selected, otherwise by unit label
                  filteredUnits = [...filteredUnits].sort((a, b) => {
                    if (unitsBalanceFilter && !managerBalancesLoading) {
                      // Use managerUnitBalances if available, otherwise fall back to unit.balance
                      const getBalance = (unit) => {
                        const computed = managerUnitBalances[unit._id];
                        if (typeof computed === 'number') return computed;
                        // Fall back to unit.balance (may be string from DB)
                        const raw = unit.balance;
                        return typeof raw === 'number' ? raw : (parseFloat(raw) || 0);
                      };
                      const balanceA = getBalance(a);
                      const balanceB = getBalance(b);

                      if (unitsBalanceFilter === "highest") {
                        return balanceB - balanceA; // Highest first
                      } else if (unitsBalanceFilter === "lowest") {
                        return balanceA - balanceB; // Lowest first
                      }
                    }

                    const labelA = a.label || "";
                    const labelB = b.label || "";
                    return labelA.localeCompare(labelB, undefined, { numeric: true, sensitivity: "base" });
                  });

                  if (filteredUnits.length === 0) {
                    return (
                      <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
                        <p className="text-gray-500">
                          {unitsSearch ? `No units found matching "${unitsSearch}"` : "No units found for this property."}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                      {/* Desktop Table View (Medium screens and up) */}
                      <div className="hidden md:block">
                        <table className="w-full">
                          <thead className="bg-gradient-to-r from-emerald-600 to-green-600 text-white">
                            <tr>
                              <th className="text-left px-6 py-3.5 text-xs font-semibold uppercase tracking-wide">Name</th>
                              <th className="text-left px-6 py-3.5 text-xs font-semibold uppercase tracking-wide">Tenant</th>
                              <th className="text-left px-6 py-3.5 text-xs font-semibold uppercase tracking-wide">ID Number</th>
                              <th className="text-left px-6 py-3.5 text-xs font-semibold uppercase tracking-wide">Balance Due</th>
                              <th className="text-left px-6 py-3.5 text-xs font-semibold uppercase tracking-wide">UT-Balance</th>
                              <th className="text-left px-6 py-3.5 text-xs font-semibold uppercase tracking-wide">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {filteredUnits.map((unit) => (
                              <tr
                                key={unit._id}
                                className="hover:bg-gray-50 transition-colors"
                              >
                                <td className="px-6 py-4">
                                  <span className="text-base font-medium text-gray-900">{unit.label}</span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm">
                                    {unit.tenants && unit.tenants.length > 0 ? (
                                      <div className="space-y-1">
                                        {unit.tenants.map((tenant, index) => (
                                          <div key={index} className="text-gray-900">
                                            {tenant.user?.name || tenant.user?.email || "Unknown"}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-red-600 font-medium">Vacant</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  {/* Tenant ID intentionally hidden but still searchable via the search bar */}
                                  <div className="text-sm text-gray-500">-</div>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-gray-900 font-medium">
                                    {(() => {
                                      // Use managerUnitBalances if available, otherwise fall back to unit.balance
                                      let balance = managerUnitBalances[unit._id];
                                      if (typeof balance !== 'number') {
                                        // Fall back to unit.balance (may be string from DB)
                                        const raw = unit.balance;
                                        balance = typeof raw === 'number' ? raw : (parseFloat(raw) || 0);
                                      }
                                      return balance > 0
                                        ? <span className="text-red-600">{formatMoney(balance)}</span>
                                        : balance < 0
                                          ? <span className="text-green-600">-{formatMoney(Math.abs(balance))}</span>
                                          : <span className="text-gray-400">{formatMoney(0)}</span>;
                                    })()}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-gray-900 font-medium">
                                    {(() => {
                                      const utilBalance = managerUnitUtilityBalances[unit._id];
                                      if (typeof utilBalance !== 'number') {
                                        return <span className="text-gray-400">—</span>;
                                      }
                                      return utilBalance > 0
                                        ? <span className="text-red-600">{formatMoney(utilBalance)}</span>
                                        : utilBalance < 0
                                          ? <span className="text-green-600">-{formatMoney(Math.abs(utilBalance))}</span>
                                          : <span className="text-gray-400">{formatMoney(0)}</span>;
                                    })()}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex flex-col gap-2">
                                    {(() => {
                                      const utilityBalance = Number(managerUnitUtilityBalances[unit._id] || 0);
                                      const tenantId = getPreferredStatementTenantId(unit);
                                      const isSettling = Boolean(utilitySettlementLoadingByUnit[unit._id]);

                                      if (!(utilityBalance > 0) || !tenantId) return null;

                                      return (
                                        <button
                                          type="button"
                                          onClick={() => askUtilitySettlementForUnit(unit)}
                                          disabled={isSettling}
                                          className="rounded-lg bg-emerald-100 px-3 py-2 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-200 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                          {isSettling ? "Processing..." : "Trigger UT Pay"}
                                        </button>
                                      );
                                    })()}
                                    <button
                                      onClick={() => {
                                        setSelectedUnit(unit);
                                        setShowUnitDetailsModal(true);
                                      }}
                                      className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg hover:shadow-lg hover:shadow-green-500/25 transition-all duration-200 font-medium"
                                    >
                                      Open/View
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Card View - Fit to Screen (below medium) */}
                      <div className="space-y-2.5 md:hidden">
                        {filteredUnits.map((unit) => {
                          // Resolve active tenant name(s)
                          const activeHistory = Array.isArray(unit.tenantHistory)
                            ? unit.tenantHistory.filter((th) => !th.endDate && th.tenant)
                            : [];
                          let tenantNames = [];
                          if (activeHistory.length > 0) {
                            tenantNames = activeHistory.map((th) => {
                              const tUser = th.tenant || {};
                              return tUser.name || tUser.email?.split('@')[0] || "Unknown";
                            });
                          } else if (unit.tenants && unit.tenants.length > 0) {
                            tenantNames = unit.tenants.map((tenant) => {
                              const tUser = tenant.user || {};
                              return tUser.name || tUser.email?.split('@')[0] || "Unknown";
                            });
                          }
                          const isVacant = tenantNames.length === 0;

                          // Rent balance
                          let balance = managerUnitBalances[unit._id];
                          if (typeof balance !== 'number') {
                            const raw = unit.balance;
                            balance = typeof raw === 'number' ? raw : (parseFloat(raw) || 0);
                          }

                          // Utility balance
                          const utilBalanceRaw = managerUnitUtilityBalances[unit._id];
                          const hasUtil = typeof utilBalanceRaw === 'number';
                          const utilityBalance = Number(managerUnitUtilityBalances[unit._id] || 0);
                          const tenantId = getPreferredStatementTenantId(unit);
                          const isSettling = Boolean(utilitySettlementLoadingByUnit[unit._id]);
                          const showUtPay = utilityBalance > 0 && tenantId;

                          const fmtBal = (val) =>
                            val > 0
                              ? <span className="text-red-600">{formatMoney(val)}</span>
                              : val < 0
                                ? <span className="text-green-600">-{formatMoney(Math.abs(val))}</span>
                                : <span className="text-gray-400">{formatMoney(0)}</span>;

                          return (
                            <div
                              key={unit._id}
                              className={`rounded-xl border p-3 shadow-sm ${
                                isVacant ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-white"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex min-w-0 items-center gap-2">
                                  <span
                                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                                      isVacant ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"
                                    }`}
                                  >
                                    <Icon name={isVacant ? "door-closed" : "door-open"} size="0.9rem" />
                                  </span>
                                  <span className="truncate text-sm font-bold text-gray-900">{unit.label}</span>
                                </div>
                                <span
                                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                    isVacant ? "bg-amber-500 text-white" : "bg-emerald-100 text-emerald-700"
                                  }`}
                                >
                                  {isVacant ? "Vacant" : "Occupied"}
                                </span>
                              </div>

                              {!isVacant && (
                                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-600">
                                  <Icon name="person" size="0.75rem" className="shrink-0 text-gray-400" />
                                  <span className="truncate">{tenantNames.join(', ')}</span>
                                </div>
                              )}

                              <div className="mt-2 grid grid-cols-3 gap-1.5 text-center">
                                <div className="rounded-lg bg-gray-50 px-1 py-1.5">
                                  <p className="text-[9px] font-medium uppercase tracking-wide text-gray-400">Monthly Rate</p>
                                  <p className="mt-0.5 break-words text-[11px] font-bold text-gray-900">{formatMoney(unit.rentAmount || 0)}</p>
                                </div>
                                <div className="rounded-lg bg-gray-50 px-1 py-1.5">
                                  <p className="text-[9px] font-medium uppercase tracking-wide text-gray-400">Rent Balance</p>
                                  <p className="mt-0.5 break-words text-[11px] font-bold">{fmtBal(balance)}</p>
                                </div>
                                <div className="rounded-lg bg-gray-50 px-1 py-1.5">
                                  <p className="text-[9px] font-medium uppercase tracking-wide text-gray-400">UT-Bal</p>
                                  <p className="mt-0.5 break-words text-[11px] font-bold">
                                    {hasUtil ? fmtBal(utilBalanceRaw) : <span className="text-gray-400">—</span>}
                                  </p>
                                </div>
                              </div>

                              <div className="mt-2 flex gap-2">
                                {showUtPay && (
                                  <button
                                    type="button"
                                    onClick={() => askUtilitySettlementForUnit(unit)}
                                    disabled={isSettling}
                                    className="flex-1 rounded-lg bg-emerald-100 px-2 py-2 text-[11px] font-semibold text-emerald-800 transition hover:bg-emerald-200 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    {isSettling ? "Processing..." : "UT Pay"}
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setSelectedUnit(unit);
                                    setShowUnitDetailsModal(true);
                                  }}
                                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-2 py-2 text-[11px] font-semibold text-white transition hover:bg-emerald-700"
                                >
                                  <Icon name="box-arrow-up-right" size="0.8rem" />
                                  View
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : showCompanyDetails && selectedCompany ? (
              <div className="max-w-4xl mx-auto">
                {/* Company Details View */}
                <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={handleBackToCompanies}
                    className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Companies
                  </button>
                </div>

                <div className="bg-green-600 border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm text-white">
                  <div className="flex flex-col gap-4 mb-5 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-center gap-3 w-full min-w-0 sm:flex-1">
                      <div className="p-2 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <h1 className="text-xl sm:text-2xl font-bold">{selectedCompany.companyName}</h1>
                      </div>
                    </div>
                    <div className="flex w-full flex-col gap-2 items-stretch self-start sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end sm:items-center">
                      {canManageCompany(selectedCompany?._id) && (
                        <>
                          <button
                            onClick={() => handleUpdateCompany(selectedCompany)}
                            className="bg-green-500 text-white px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg hover:shadow-md hover:shadow-green-500/25 transition-all duration-200 font-medium flex items-center gap-2 w-full sm:w-auto justify-center text-xs sm:text-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Update
                          </button>
                          <button
                            onClick={() => setStaffModalCompany(selectedCompany)}
                            className="bg-green-500 text-white px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg hover:shadow-md hover:shadow-green-500/25 transition-all duration-200 font-medium flex items-center gap-2 w-full sm:w-auto justify-center text-xs sm:text-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M9 20H4v-2a3 3 0 015.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Team Members
                          </button>
                          {/* Mobile-only: Add Property and Tenancy History in header, stacked */}
                          <button
                            onClick={() => {
                              setPropertyForm({ ...propertyForm, managementCompanyId: selectedCompany._id });
                              setShowPropertyForm(true);
                            }}
                            data-tutorial="add-property-btn"
                            className="bg-green-500 text-white px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg hover:shadow-md hover:shadow-green-500/25 transition-all duration-200 font-medium flex items-center gap-2 w-full sm:w-auto justify-center text-xs sm:text-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Property
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCompanyForTenancy(selectedCompany);
                              fetchCompanyTenants(selectedCompany._id);
                              setShowTenancyHistoryModal(true);
                            }}
                            className="sm:hidden bg-green-500 text-white px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg hover:shadow-md hover:shadow-green-500/25 transition-all duration-200 font-medium flex items-center gap-2 w-full sm:w-auto justify-center text-xs sm:text-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Tenancy History
                          </button>
                          <button
                            onClick={() => {
                              setShowCompanyTransactionsModal(true);
                              const { start, end } = getCurrentMonthRange();
                              setCompanyStartDate(start);
                              setCompanyEndDate(end);
                              fetchCompanyTransactions(selectedCompany._id);
                            }}
                            className="bg-green-500 text-white px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg hover:shadow-md hover:shadow-green-500/25 transition-all duration-200 font-medium flex items-center gap-2 w-full sm:w-auto justify-center text-xs sm:text-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6a2 2 0 012-2h8m-6 8h6m-6-4h6M7 7h.01M4 17h.01" />
                            </svg>
                            Transactions
                          </button>
                          {(user?.role === 'A' || user?.canUseMpesaReconciliation) && (
                            <button
                              onClick={() => navigate(user?.role === 'A' ? '/admin/mpesa-reconciliation' : '/mpesa-reconciliation')}
                              className="bg-green-500 text-white px-2 py-1 sm:px-3 sm:py-2 rounded-md sm:rounded-lg hover:shadow-md hover:shadow-green-500/25 transition-all duration-200 font-medium flex items-center gap-2 w-full sm:w-auto justify-center text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              M-Pesa Reconciliation
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Properties section in company details */}
                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <h3 className="flex items-center gap-2 text-lg font-bold text-white">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                          <Icon name="buildings" size="1rem" />
                        </span>
                        Properties
                        <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold text-white">{filteredProperties.length}</span>
                      </h3>
                      {canManageCompany(selectedCompany?._id) && (
                        <div className="hidden sm:flex flex-row gap-2">
                          <button
                            onClick={() => {
                              setPropertyForm({ ...propertyForm, managementCompanyId: selectedCompany._id });
                              setShowPropertyForm(true);
                            }}
                            className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:shadow-md hover:shadow-green-500/25 transition-all duration-200 font-medium flex items-center gap-2 w-full sm:w-auto justify-center text-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Property
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCompanyForTenancy(selectedCompany);
                              fetchCompanyTenants(selectedCompany._id);
                              setShowTenancyHistoryModal(true);
                            }}
                            className="bg-gradient-to-r from-green-500 to-green-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:shadow-md hover:shadow-green-500/25 transition-all duration-200 font-medium flex items-center gap-2 w-full sm:w-auto justify-center text-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Tenancy History
                          </button>
                        </div>
                      )}
                    </div>
                    {/* Search input for properties */}
                    <div className="mb-4">
                      <div className="relative">
                        <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          placeholder="Search properties..."
                          value={propertySearch}
                          onChange={(e) => setPropertySearch(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                        />
                        {propertySearch && (
                          <button
                            onClick={() => setPropertySearch('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {filteredProperties.length === 0 ? (
                      <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
                        <div className="text-gray-400 mb-4">
                          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                        </div>
                        <h4 className="text-xl font-semibold text-gray-700 mb-2">
                          {propertySearch ? 'No Properties Found' : 'No Properties Yet'}
                        </h4>
                        <p className="text-gray-500">
                          {propertySearch
                            ? `No properties match "${propertySearch}". Try a different search term.`
                            : 'Add your first property to this management company.'}
                        </p>
                      </div>
                    ) : (
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {filteredProperties.map((property, propIdx) => {
                          const totalUnits = property.unitCounts?.total ?? 0;
                          const occupiedUnits = property.unitCounts?.occupied ?? 0;
                          const vacantUnits = property.unitCounts?.vacant ?? 0;
                          const occPct = totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0;
                          const occTone =
                            occPct >= 80
                              ? "bg-emerald-100 text-emerald-700"
                              : occPct >= 50
                                ? "bg-amber-100 text-amber-700"
                                : "bg-rose-100 text-rose-700";
                          return (
                            <div
                              key={property._id}
                              className="group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                            >
                              <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 to-green-500" />
                              <div className="flex flex-1 flex-col gap-3 p-4">
                                {/* Header: icon + name + occupancy % */}
                                <div className="flex items-start gap-3">
                                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                                    <Icon name="building" size="1.2rem" />
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <h5 className="truncate text-base font-bold leading-tight text-gray-900">{property.name}</h5>
                                    <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                                      <Icon name="geo-alt" size="0.7rem" className="shrink-0" />
                                      <span className="truncate">{property.address}</span>
                                    </p>
                                  </div>
                                  <span className={`shrink-0 rounded-lg px-2 py-1 text-[11px] font-bold ${occTone}`}>{occPct}%</span>
                                </div>

                                {/* Occupancy progress */}
                                <div>
                                  <div className="flex items-center justify-between text-[11px] font-medium text-gray-500">
                                    <span>Occupied {occupiedUnits}/{totalUnits}</span>
                                    <span>Vacant {vacantUnits}</span>
                                  </div>
                                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                                    <div
                                      className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-green-500"
                                      style={{ width: `${occPct}%` }}
                                    />
                                  </div>
                                </div>

                                {/* Meta */}
                                <div className="space-y-1 text-xs text-gray-500">
                                  {property.landlord && (
                                    <p className="flex items-center gap-1.5">
                                      <Icon name="person" size="0.78rem" className="shrink-0 text-gray-400" />
                                      <span className="truncate">
                                        {property.landlord.name}
                                        {property.landlord.phone && ` • ${property.landlord.phone}`}
                                      </span>
                                    </p>
                                  )}
                                  <p className="flex items-center gap-1.5">
                                    <Icon name="cash-coin" size="0.78rem" className="shrink-0 text-gray-400" />
                                    <span className="truncate">
                                      Settlement: {property?.redirectLandlordIncomeToManager ? "Mgmt Externally" : "RecordLOGS"}
                                    </span>
                                  </p>
                                </div>
                              </div>

                              <div className="mt-auto border-t border-gray-100 p-3">
                                <button
                                  onClick={() => handleViewUnits(property)}
                                  {...(propIdx === 0 ? { "data-tutorial": "view-units-btn" } : {})}
                                  className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                                >
                                  <Icon name="grid-3x3-gap" size="0.85rem" />
                                  View Units
                                  <Icon name="arrow-right" size="0.85rem" className="transition-transform group-hover:translate-x-0.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Simplified Management Companies List */
              <div>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
                  <div>
                    <h2 className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-gray-900">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                        <Icon name="building-fill-gear" size="1.1rem" />
                      </span>
                      Companies
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">{managementCompanies.length}</span>
                    </h2>
                  </div>

                </div>

                {managementCompanies.length === 0 ? (
                  <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-green-50 rounded-2xl border-2 border-dashed border-green-200">
                    <div className="text-green-300 mb-4">
                      <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No Management Companies</h3>
                    <p className="text-gray-500">Create your first management company to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {managementCompanies.map((company, idx) => {
                      const isLocked = !!company.locked
                      return (
                        <div
                          key={company._id}
                          className="relative bg-white border border-gray-200 hover:border-green-300 rounded-2xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300"
                        >
                          {/* Locked overlay with big red X */}
                          {isLocked && (
                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/80 rounded-2xl">
                              <div className="relative flex items-center justify-center w-full h-full">
                                <div className="absolute inset-6 border-4 border-red-600 rounded-2xl" />
                                <svg
                                  className="w-24 h-24 text-red-600 opacity-80"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </div>
                            </div>
                          )}

                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-4">
                              <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex-shrink-0">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{company.companyName}</h3>
                                <p className="text-gray-600 text-sm sm:text-base truncate">{company.headOfficeLocation}</p>
                                {isLocked && (
                                  <p className="mt-1 text-xs font-semibold text-red-700">
                                    Blocked by admin – all actions disabled
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                              <button
                                onClick={() => {
                                  if (isLocked) return
                                  handleViewCompany(company)
                                }}
                                disabled={isLocked}
                                {...(idx === 0 ? { "data-tutorial": "company-card" } : {})}
                                className={`bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 text-sm transition-all duration-200 ${isLocked
                                  ? 'opacity-60 cursor-not-allowed'
                                  : 'hover:shadow-lg hover:shadow-green-500/25'
                                  }`}
                              >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span>{isLocked ? 'Blocked' : 'Next'}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {/* Landlord Section */}
        {activeRole === "landlord" && isLandlord(user) && (
          <LandlordView
            user={user}
            activeRole={activeRole}
            payScore={payScore}
            payScoreLoading={payScoreLoading}
            statementLoading={statementLoading}
            setStatementLoading={setStatementLoading}
            setTransactionStatement={setTransactionStatement}
            setSelectedUnit={setSelectedUnit}
            setShowTransactionStatement={setShowTransactionStatement}
            setShowUnitDetailsModal={setShowUnitDetailsModal}
          />
        )}
        {/* Tenant Section */}
        {activeRole === "tenant" && hasAnyTenantAccess(user) && (
          <TenantView
            user={user}
            wallet={wallet}
            payScore={payScore}
            payScoreLoading={payScoreLoading}
            myUnits={myUnits}
            payRent={payRent}
            showPayRent={showTenantPayRent}
            fetchTenantMonthlyStatus={fetchTenantMonthlyStatus}
            fetchTransactionStatement={fetchTransactionStatement}
            openWaterStatement={openWaterMeterModal}
            tenantMonthlyStatusLoading={tenantMonthlyStatusLoading}
            statementLoading={statementLoading}
            utilityBalance={utilityWallet?.balance}
            utilityStatementsLoading={utilityWalletTransactionsLoading}
            showUtilityTopUpButton={showUtilityTopUpButton}
            onUtilityTopUpClick={() => setShowAddUtilityFundsForm(true)}
            onUtilityStatementsClick={() => {
              setShowUtilityWalletTransactionsModal(true);
              fetchUtilityWalletTransactions();
            }}
          />
        )}
        {/* Management Company Creation Form Modal - SIMPLE FORM */}
        {showManagementForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Create Management Company</h2>
                  <button
                    onClick={() => setShowManagementForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                {formError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-red-700 text-sm">{formError}</p>
                  </div>
                )}

                <form onSubmit={createManagement} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={managementForm.companyName}
                      onChange={(e) => setManagementForm({ ...managementForm, companyName: e.target.value })}
                      required
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent"
                      placeholder="Enter company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telephone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={managementForm.telephoneNumber}
                      onChange={(e) => setManagementForm({ ...managementForm, telephoneNumber: e.target.value })}
                      required
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Head Office Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={managementForm.headOfficeLocation}
                      onChange={(e) => setManagementForm({ ...managementForm, headOfficeLocation: e.target.value })}
                      required
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent"
                      placeholder="Enter head office address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Position in Company <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={managementForm.yourPositionInCompany}
                      onChange={(e) => setManagementForm({ ...managementForm, yourPositionInCompany: e.target.value })}
                      required
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent"
                      placeholder="e.g., Manager, Director"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Owner Name(s) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={managementForm.ownerNames}
                      onChange={(e) => setManagementForm({ ...managementForm, ownerNames: e.target.value })}
                      required
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent"
                      placeholder="Enter owner name(s)"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowManagementForm(false)}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="flex-1 py-2 px-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {formLoading ? 'Creating...' : 'Create Management Company'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Update Management Company Form Modal */}
        {showUpdateManagementForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Update</h2>
                  <button
                    onClick={() => {
                      setShowUpdateManagementForm(false);
                      setSelectedManagementCompany(null);
                      setManagementForm({
                        companyName: '',
                        telephoneNumber: '',
                        headOfficeLocation: '',
                        yourPositionInCompany: '',
                        ownerNames: ''
                      });
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-2"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {formError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {formError}
                  </div>
                )}

                <form onSubmit={updateManagement} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                    <input
                      type="text"
                      required
                      value={managementForm.companyName}
                      onChange={(e) => setManagementForm({ ...managementForm, companyName: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      placeholder="Enter company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telephone Number</label>
                    <input
                      type="tel"
                      required
                      value={managementForm.telephoneNumber}
                      onChange={(e) => setManagementForm({ ...managementForm, telephoneNumber: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      placeholder="Enter telephone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Head Office Location</label>
                    <input
                      type="text"
                      required
                      value={managementForm.headOfficeLocation}
                      onChange={(e) => setManagementForm({ ...managementForm, headOfficeLocation: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      placeholder="Enter head office location"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Your Position in Company</label>
                    <input
                      type="text"
                      required
                      value={managementForm.yourPositionInCompany}
                      onChange={(e) => setManagementForm({ ...managementForm, yourPositionInCompany: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      placeholder="Enter your position"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Owner Names</label>
                    <textarea
                      required
                      value={managementForm.ownerNames}
                      onChange={(e) => setManagementForm({ ...managementForm, ownerNames: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      rows="3"
                      placeholder="Enter owner names"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowUpdateManagementForm(false);
                        setSelectedManagementCompany(null);
                        setManagementForm({
                          companyName: '',
                          telephoneNumber: '',
                          headOfficeLocation: '',
                          yourPositionInCompany: '',
                          ownerNames: ''
                        });
                      }}
                      className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="flex-1 py-3 px-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {formLoading ? 'Updating...' : 'Update Company'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Update Property Form Modal */}
        {showUpdatePropertyForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Update Property</h2>
                    <button
                      onClick={() => {
                        setShowUpdatePropertyForm(false);
                        setUpdatePropertyForm({
                          name: '',
                          cityTown: '',
                          location: '',
                          area: '',
                          managementPercentage: 10,
                          waterUnitCharge: '',
                          waterUnitsMeasure: '',
                          trashMonthlyCharge: ''
                        });
                      }}
                    className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-2"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {formError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {formError}
                  </div>
                )}

                <form onSubmit={updateProperty} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Property Name</label>
                    <input
                      type="text"
                      required
                      value={updatePropertyForm.name}
                      onChange={(e) => setUpdatePropertyForm({ ...updatePropertyForm, name: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      placeholder="Enter property name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location Details</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">City / Town</label>
                        <input
                          type="text"
                          required
                          value={updatePropertyForm.cityTown}
                          onChange={(e) => setUpdatePropertyForm({ ...updatePropertyForm, cityTown: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                          placeholder="City / Town"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
                        <input
                          type="text"
                          required
                          value={updatePropertyForm.location}
                          onChange={(e) => setUpdatePropertyForm({ ...updatePropertyForm, location: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                          placeholder="Location"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Area</label>
                        <input
                          type="text"
                          required
                          value={updatePropertyForm.area}
                          onChange={(e) => setUpdatePropertyForm({ ...updatePropertyForm, area: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                          placeholder="Area"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">We will compose the address from these fields.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Management Percentage (%)</label>
                    <input
                      type="number"
                      required
                      min="0"
                      max="100"
                      step="0.1"
                      value={updatePropertyForm.managementPercentage}
                      onChange={(e) => setUpdatePropertyForm({ ...updatePropertyForm, managementPercentage: parseFloat(e.target.value) })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      placeholder="Enter management percentage"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Utility Settings</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Water Unit Charge</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={updatePropertyForm.waterUnitCharge}
                          onChange={(e) => setUpdatePropertyForm({ ...updatePropertyForm, waterUnitCharge: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                          placeholder="e.g. 50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Water Units Measure</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={updatePropertyForm.waterUnitsMeasure}
                          onChange={(e) => setUpdatePropertyForm({ ...updatePropertyForm, waterUnitsMeasure: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                          placeholder="e.g. 1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Trash Monthly Charge</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={updatePropertyForm.trashMonthlyCharge}
                          onChange={(e) => setUpdatePropertyForm({ ...updatePropertyForm, trashMonthlyCharge: e.target.value })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                          placeholder="e.g. 200"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowUpdatePropertyForm(false);
                        setUpdatePropertyForm({
                          name: '',
                          cityTown: '',
                          location: '',
                          area: '',
                          managementPercentage: 10,
                          waterUnitCharge: '',
                          waterUnitsMeasure: '',
                          trashMonthlyCharge: ''
                        });
                      }}
                      className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="flex-1 py-3 px-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {formLoading ? 'Updating...' : 'Update Property'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        {/* Property Creation Form Modal - COMPLEX FORM */}
        {showPropertyForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Create New Property</h2>
                  <button
                    onClick={() => setShowPropertyForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                {formError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-red-700 text-sm">{formError}</p>
                  </div>
                )}

                <form onSubmit={createProperty} className="space-y-4">
                  {/* Landlord Selection - First Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Landlord (Search ID) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative landlord-search-container">
                      <input
                        type="text"
                        data-tutorial="property-landlord"
                        value={landlordSearch}
                        onChange={(e) => handlePropertyLandlordSearch(e.target.value)}
                        placeholder="Search by ID number to continue..."
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent"
                        required
                      />

                      {/* Search Results Dropdown */}
                      {showLandlordDropdown && landlordResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {landlordResults.map(user => (
                            <button
                              key={user._id}
                              type="button"
                              onClick={() => selectPropertyLandlord(user)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="text-sm font-medium">{user.idNumber}</div>
                              <div className="text-xs text-gray-500">
                                {user.name} - {user.email}
                                {user.role && (
                                  <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                                    {user.role === 'M' ? 'Management' :
                                      user.role === 'L' ? 'Landlord' :
                                        user.role === 'T' ? 'Tenant' :
                                          user.role === 'A' ? 'Admin' :
                                            user.role === 'Mbr' ? 'Member' : user.role}
                                  </span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* No results message */}
                      {showLandlordDropdown && landlordResults.length === 0 && landlordSearch.length >= 1 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                          <div className="text-sm text-gray-500">No users found with ID "{landlordSearch}"</div>
                        </div>
                      )}
                    </div>
                    {/* Selected Landlord Display */}
                    {selectedLandlord && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-green-800">Selected: {selectedLandlord.name}</div>
                            <div className="text-xs text-green-600">{selectedLandlord.email} - {selectedLandlord.idNumber}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedLandlord(null);
                              setLandlordSearch('');
                              setComplexPropertyForm({ ...complexPropertyForm, landlordId: '' });
                            }}
                            className="text-green-600 hover:text-green-800 text-sm"
                          >
                            Change
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Management Company Selection - First property detail */}
                  <div className={`${!selectedLandlord ? 'opacity-50 pointer-events-none' : ''}`}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Management Company <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={complexPropertyForm.managementCompanyId || ''}
                      onChange={(e) => setComplexPropertyForm({ ...complexPropertyForm, managementCompanyId: e.target.value })}
                      required
                      disabled={!selectedLandlord}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">Select a management company</option>
                      {managementCompanies.map((company) => (
                        <option key={company._id} value={company._id}>
                          {company.companyName}
                        </option>
                      ))}
                    </select>
                    {managementCompanies.length === 0 && (
                      <p className="text-sm text-red-600 mt-1">
                        No management companies found. Please create one first.
                      </p>
                    )}
                  </div>

                  {/* Property Details - Disabled until landlord is selected */}
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${!selectedLandlord ? 'opacity-50 pointer-events-none' : ''}`}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Property Name</label>
                      <input
                        type="text"
                        data-tutorial="property-name"
                        value={complexPropertyForm.propertyName}
                        onChange={(e) => setComplexPropertyForm({ ...complexPropertyForm, propertyName: e.target.value })}
                        required
                        disabled={!selectedLandlord}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Property Name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={complexPropertyForm.phoneNumber}
                        onChange={(e) => setComplexPropertyForm({ ...complexPropertyForm, phoneNumber: e.target.value })}
                        required
                        disabled={!selectedLandlord}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Contact phone"
                      />
                    </div>
                  </div>

                  <div className={!selectedLandlord ? "opacity-50 pointer-events-none" : ""}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location Details</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <input
                          type="text"
                          data-tutorial="property-city"
                          value={complexPropertyForm.cityTown}
                          onChange={(e) => setComplexPropertyForm({ ...complexPropertyForm, cityTown: e.target.value })}
                          required
                          disabled={!selectedLandlord}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="City / Town"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          data-tutorial="property-location"
                          value={complexPropertyForm.location}
                          onChange={(e) => setComplexPropertyForm({ ...complexPropertyForm, location: e.target.value })}
                          required
                          disabled={!selectedLandlord}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="Location"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={complexPropertyForm.area}
                          onChange={(e) => setComplexPropertyForm({ ...complexPropertyForm, area: e.target.value })}
                          required
                          disabled={!selectedLandlord}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="Area"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">These fields help renters search for available units.</p>
                  </div>

                  <div className={!selectedLandlord ? "opacity-50 pointer-events-none" : ""}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Management Fee (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={complexPropertyForm.managementPercentage}
                      onChange={(e) => setComplexPropertyForm({ ...complexPropertyForm, managementPercentage: parseFloat(e.target.value) || 0 })}
                      required
                      disabled={!selectedLandlord}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="e.g., 10.5"
                    />
                    <p className="text-xs text-gray-500 mt-1">Percentage of rent that goes to management company</p>
                  </div>

                  <div className={!selectedLandlord ? "opacity-50 pointer-events-none" : ""}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Utility Settings</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={complexPropertyForm.waterUnitCharge}
                        onChange={(e) => setComplexPropertyForm({ ...complexPropertyForm, waterUnitCharge: e.target.value })}
                        disabled={!selectedLandlord}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Water unit charge"
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={complexPropertyForm.waterUnitsMeasure}
                        onChange={(e) => setComplexPropertyForm({ ...complexPropertyForm, waterUnitsMeasure: e.target.value })}
                        disabled={!selectedLandlord}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Water units measure"
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={complexPropertyForm.trashMonthlyCharge}
                        onChange={(e) => setComplexPropertyForm({ ...complexPropertyForm, trashMonthlyCharge: e.target.value })}
                        disabled={!selectedLandlord}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Trash monthly charge"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Used for prepaid water and monthly utility setup.</p>
                  </div>

                  <div className={!selectedLandlord ? "opacity-50 pointer-events-none" : ""}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of Units</label>
                    <input
                      type="number"
                      value={complexPropertyForm.numberOfUnits}
                      onChange={(e) => setComplexPropertyForm({ ...complexPropertyForm, numberOfUnits: e.target.value })}
                      required
                      min="1"
                      disabled={!selectedLandlord}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="e.g., 20"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowPropertyForm(false)}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      data-tutorial="property-submit"
                      disabled={formLoading || !selectedLandlord}
                      className="flex-1 py-2 px-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {formLoading ? 'Creating...' : !selectedLandlord ? 'Select Landlord First' : 'Add Property'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        {/* Unit Creation Form Modal */}
        {showUnitForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full max-h-[85vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Create New Unit</h3>
              <form onSubmit={createUnit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit No
                  </label>
                  <input
                    type="text"
                    data-tutorial="unit-label"
                    value={unitForm.label}
                    onChange={(e) => setUnitForm({ ...unitForm, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., A-101, B-205"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit Type (Select from list):
                  </label>
                  <select
                    data-tutorial="unit-type"
                    value={unitForm.type}
                    onChange={(e) => setUnitForm({ ...unitForm, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Select unit type...</option>
                    {UNIT_TYPE_OPTIONS.map((unitType) => (
                      <option key={unitType} value={unitType}>{unitType}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Rent
                  </label>
                  <input
                    type="number"
                    data-tutorial="unit-rent"
                    value={unitForm.rentAmount}
                    onChange={(e) => setUnitForm({ ...unitForm, rentAmount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div className="mb-6">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Unit Images</p>
                        <p className="text-xs text-gray-500">
                          Open this only when you want to add or change unit photos.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowCreateUnitImages((prev) => !prev)}
                        className="shrink-0 rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
                      >
                        {showCreateUnitImages ? "Hide Images" : (unitForm.images?.length ? "Add/Change Images" : "Add Images")}
                      </button>
                    </div>
                    <div className="mt-3 text-xs text-gray-600">
                      {unitForm.images?.length
                        ? `${unitForm.images.length} image(s) attached`
                        : "No unit images attached yet."}
                    </div>
                    {showCreateUnitImages && (
                      <div className="mt-4">
                        <UnitImagesUploader
                          value={unitForm.images}
                          onChange={(imgs) => setUnitForm({ ...unitForm, images: imgs })}
                          unitId={null}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    data-tutorial="unit-submit"
                    className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Add Unit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateUnitImages(false);
                      setShowUnitForm(false);
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Update Unit Form Modal */}
        {showUpdateUnitForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full max-h-[85vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Unit Info / Photos {selectedUnit?.label}</h3>
              <form onSubmit={updateUnit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit No
                  </label>
                  <input
                    type="text"
                    value={updateUnitForm.label}
                    onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., A-101, B-205"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit Type (Select from list):
                  </label>
                  <select
                    value={updateUnitForm.type}
                    onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Select unit type...</option>
                    {(() => {
                      const selectedType = String(updateUnitForm.type || "").trim();
                      const hasCustomSelectedType =
                        selectedType && !UNIT_TYPE_OPTIONS.includes(selectedType);
                      const options = hasCustomSelectedType
                        ? [selectedType, ...UNIT_TYPE_OPTIONS]
                        : UNIT_TYPE_OPTIONS;
                      return options.map((unitType) => (
                        <option key={unitType} value={unitType}>
                          {hasCustomSelectedType && unitType === selectedType
                            ? `${unitType} (current)`
                            : unitType}
                        </option>
                      ));
                    })()}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deposit
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={updateUnitForm.deposit}
                    onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, deposit: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="0"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Rent
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={updateUnitForm.rentAmount}
                    onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, rentAmount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Late Fee (Fixed Amount)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={updateUnitForm.lateFee}
                    onChange={(e) => setUpdateUnitForm({ ...updateUnitForm, lateFee: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="0"
                  />
                </div>
                <div className="mb-6">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Unit Photos</p>
                        <p className="text-xs text-gray-500">
                          Keep this closed unless you want to add, replace, or remove images.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowEditUnitImages((prev) => !prev)}
                        className="shrink-0 rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
                      >
                        {showEditUnitImages ? "Hide Images" : (updateUnitForm.images?.length ? "Add/Change Images" : "Add Images")}
                      </button>
                    </div>
                    <div className="mt-3 text-xs text-gray-600">
                      {updateUnitForm.images?.length
                        ? `${updateUnitForm.images.length} image(s) currently attached`
                        : "No unit images attached yet."}
                    </div>
                    {showEditUnitImages && (
                      <div className="mt-4">
                        <UnitImagesUploader
                          value={updateUnitForm.images}
                          onChange={(imgs) => setUpdateUnitForm({ ...updateUnitForm, images: imgs })}
                          unitId={selectedUnit?._id || null}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Update Unit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditUnitImages(false);
                      setShowUpdateUnitForm(false);
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Assignment Form Modal */}
        {showAssignForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
            <div className="bg-white p-4 sm:p-6 rounded-lg max-w-md w-full max-h-[92vh] overflow-y-auto">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
                Assign {assignmentType === "landlord" ? "Landlord" : "Tenant"} to Unit {selectedUnit?.label}
              </h3>
              <form onSubmit={assignUser} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {assignmentType === "landlord" ? "Landlord" : "Tenant"}
                  </label>
                  {assignmentType === "tenant" && (
                    <>
                      {(() => {
                        const totals = getTenantAssignmentWalletTotals(selectedUnit);
                        return (
                          <div className="mb-2 rounded-lg border border-emerald-100 bg-emerald-50/70 p-2.5">
                            <p className="text-xs font-semibold text-emerald-900 mb-2">
                              Required Wallet Amount
                            </p>
                            <div className="grid grid-cols-3 gap-2 text-[11px] sm:text-xs">
                              <div className="rounded-md bg-white px-2 py-1.5 border border-emerald-100">
                                <p className="text-gray-600">Deposit (set)</p>
                                <p className="font-semibold text-gray-900">{formatMoney(totals.deposit)}</p>
                              </div>
                              <div className="rounded-md bg-white px-2 py-1.5 border border-emerald-100">
                                <p className="text-gray-600">Rent (set)</p>
                                <p className="font-semibold text-gray-900">{formatMoney(totals.rent)}</p>
                              </div>
                              <div className="rounded-md bg-emerald-100/80 px-2 py-1.5 border border-emerald-200">
                                <p className="text-emerald-800">Total (Deposit + Rent)</p>
                                <p className="font-semibold text-emerald-900">{formatMoney(totals.total)}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-xs text-gray-500">
                        Search existing tenant or create a new account.
                      </p>
                      {!createTenantAccountMode ? (
                        <button
                          type="button"
                          onClick={openCreateTenantAccountMode}
                          className="text-xs font-semibold text-green-700 hover:text-green-800"
                        >
                          Create account
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={closeCreateTenantAccountMode}
                          className="text-xs font-semibold text-gray-600 hover:text-gray-800"
                        >
                          Search existing
                        </button>
                      )}
                    </div>
                    </>
                  )}

                  <div className={`relative ${assignmentType === "landlord" ? "landlord-search-container" : "tenant-search-container"}`}>
                    {!(assignmentType === "tenant" && createTenantAccountMode) && (
                      <input
                        type="text"
                        data-tutorial="tenant-search"
                        value={assignmentType === "landlord" ? landlordSearch : tenantSearch}
                        onChange={(e) => assignmentType === "landlord" ? handleLandlordSearch(e.target.value) : handleTenantSearch(e.target.value)}
                        placeholder="Search by ID number"
                        className="w-full h-9 sm:h-10 px-2.5 sm:px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        required={assignmentType === "landlord" || !createTenantAccountMode}
                      />
                    )}

                    {assignmentType === "tenant" && createTenantAccountMode && (
                      <div className="space-y-2.5 rounded-lg border border-emerald-100 bg-emerald-50/40 p-2.5 sm:p-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">First Name *</label>
                            <input
                              type="text"
                              value={newTenantAccountForm.firstName}
                              onChange={(e) => setNewTenantAccountForm({ ...newTenantAccountForm, firstName: e.target.value })}
                              className="w-full h-9 sm:h-10 px-2.5 sm:px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                              placeholder="First name"
                              required={createTenantAccountMode}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Last Name *</label>
                            <input
                              type="text"
                              value={newTenantAccountForm.lastName}
                              onChange={(e) => setNewTenantAccountForm({ ...newTenantAccountForm, lastName: e.target.value })}
                              className="w-full h-9 sm:h-10 px-2.5 sm:px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                              placeholder="Last name"
                              required={createTenantAccountMode}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number *</label>
                          <input
                            type="text"
                            value={newTenantAccountForm.phone}
                            onChange={(e) => setNewTenantAccountForm({ ...newTenantAccountForm, phone: e.target.value })}
                            className="w-full h-9 sm:h-10 px-2.5 sm:px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder="e.g. 07XXXXXXXX"
                            required={createTenantAccountMode}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">ID Number *</label>
                            <input
                              type="text"
                              value={newTenantAccountForm.idNumber}
                              onChange={(e) => setNewTenantAccountForm({ ...newTenantAccountForm, idNumber: e.target.value })}
                              className="w-full h-9 sm:h-10 px-2.5 sm:px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                              placeholder="Tenant ID"
                              required={createTenantAccountMode}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Confirm ID *</label>
                            <input
                              type="text"
                              value={newTenantAccountForm.confirmIdNumber}
                              onChange={(e) => setNewTenantAccountForm({ ...newTenantAccountForm, confirmIdNumber: e.target.value })}
                              className="w-full h-9 sm:h-10 px-2.5 sm:px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                              placeholder="Confirm ID"
                              required={createTenantAccountMode}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">4-Digit PIN *</label>
                            <input
                              type="password"
                              value={newTenantAccountForm.pin}
                              onChange={(e) => setNewTenantAccountForm({ ...newTenantAccountForm, pin: e.target.value })}
                              className="w-full h-9 sm:h-10 px-2.5 sm:px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                              placeholder="Create PIN"
                              maxLength={4}
                              pattern="[0-9]{4}"
                              required={createTenantAccountMode}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Confirm PIN *</label>
                            <input
                              type="password"
                              value={newTenantAccountForm.confirmPin}
                              onChange={(e) => setNewTenantAccountForm({ ...newTenantAccountForm, confirmPin: e.target.value })}
                              className="w-full h-9 sm:h-10 px-2.5 sm:px-3 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                              placeholder="Confirm PIN"
                              maxLength={4}
                              pattern="[0-9]{4}"
                              required={createTenantAccountMode}
                            />
                          </div>
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-600">
                            Email is hidden for this flow. A system fallback email is used in the background.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Selected User Display */}
                    {assignmentType === "landlord" && selectedLandlord && (
                      <div className="mt-2 p-3 bg-gradient-to-r from-green-50 to-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-green-800">Selected: {selectedLandlord.name}</div>
                            <div className="text-xs text-green-600">{getReadableUserEmail(selectedLandlord.email)} - {selectedLandlord.idNumber}</div>
                            {renderUserPayScoreBadges(selectedLandlord?._id)}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedLandlord(null);
                              setSelectedUserId("");
                              setLandlordSearch("");
                            }}
                            className="text-green-400 hover:text-green-600 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    {assignmentType === "tenant" && selectedTenant && !createTenantAccountMode && (
                      <div className="mt-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-green-800">Name: {selectedTenant.name}</div>
                            <div className="text-xs text-green-600">{getReadableUserEmail(selectedTenant.email)} - {selectedTenant.idNumber}</div>
                            {renderUserPayScoreBadges(selectedTenant?._id, "frpOnly")}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedTenant(null);
                              setSelectedUserId("");
                              setTenantSearch("");
                            }}
                            className="text-green-400 hover:text-green-600 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Search Results Dropdown */}
                    {assignmentType === "landlord" && showLandlordDropdown && landlordResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                        {landlordResults.map(landlord => (
                          <button
                            key={landlord._id}
                            type="button"
                            onClick={() => selectLandlord(landlord)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900">{landlord.idNumber}</div>
                                <div className="text-xs text-gray-500 truncate">
                                  {landlord.name} - {getReadableUserEmail(landlord.email)}
                                </div>
                                {renderUserPayScoreBadges(landlord?._id)}
                              </div>
                              {landlord.role && (
                                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] font-semibold whitespace-nowrap">
                                  {getUserRoleChipLabel(landlord.role)}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {assignmentType === "tenant" && showTenantDropdown && !createTenantAccountMode && tenantResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                        {tenantResults.map(tenant => (
                          <button
                            key={tenant._id}
                            type="button"
                            onClick={() => selectTenant(tenant)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-900">{tenant.idNumber}</div>
                                <div className="text-xs text-gray-500 truncate">
                                  {tenant.name} - {getReadableUserEmail(tenant.email)}
                                </div>
                                {renderUserPayScoreBadges(tenant?._id, "frpOnly")}
                              </div>
                              {tenant.role && (
                                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] font-semibold whitespace-nowrap">
                                  {getUserRoleChipLabel(tenant.role)}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* No results message */}
                    {assignmentType === "landlord" && showLandlordDropdown && landlordResults.length === 0 && landlordSearch.length >= ASSIGN_SEARCH_MIN_CHARS && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                        <div className="text-sm text-gray-500">No users found with ID "{landlordSearch}"</div>
                      </div>
                    )}

                    {assignmentType === "tenant" && showTenantDropdown && !createTenantAccountMode && tenantResults.length === 0 && tenantSearch.length >= ASSIGN_SEARCH_MIN_CHARS && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                        <div className="text-sm text-gray-500">No users found with ID "{tenantSearch}"</div>
                        <button
                          type="button"
                          onClick={openCreateTenantAccountMode}
                          className="mt-2 text-xs font-semibold text-green-700 hover:text-green-800"
                        >
                          Create tenant account with this ID
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Move in date field - only show for tenant assignments */}
                {assignmentType === "tenant" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Move in Date *
                    </label>
                    <input
                      type="date"
                      value={moveInDate}
                      onChange={(e) => setMoveInDate(e.target.value)}
                      className="w-full h-9 sm:h-10 px-2.5 sm:px-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  {assignmentType === "tenant" && createTenantAccountMode && (
                    <button
                      type="button"
                      onClick={registerTenantAccount}
                      disabled={creatingTenantAccount}
                      className="flex-1 bg-emerald-600 text-white py-2 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {creatingTenantAccount ? "Registering..." : "Register"}
                    </button>
                  )}
                  <button
                    type="submit"
                    data-tutorial="tenant-submit"
                    disabled={creatingTenantAccount || isAssigningUser || !selectedUserId}
                    className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isAssigningUser ? "Assigning..." : creatingTenantAccount ? "Please wait..." : "Assign"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAssignForm(false);
                      resetAssignFormState();
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <AsyncActionModal
          open={!!assignmentActionResult}
          status={assignmentActionResult?.status}
          title={assignmentActionResult?.title}
          statusLabel={assignmentActionResult?.statusLabel}
          message={assignmentActionResult?.message}
          details={assignmentActionResult?.details || []}
          onClose={closeAssignmentActionModal}
          primaryLabel={
            assignmentActionResult?.status === "success"
              ? "Done"
              : assignmentActionResult?.status === "error"
                ? "Back to Form"
                : undefined
          }
          onPrimary={
            assignmentActionResult?.status === "success"
              ? finishAssignmentAction
              : assignmentActionResult?.status === "error"
                ? retryAssignmentAction
                : undefined
          }
          secondaryLabel={assignmentActionResult?.status === "error" ? "Close" : undefined}
          onSecondary={closeAssignmentActionModal}
        />

        {tenantAccountVerificationModal.open && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-2 py-4 sm:px-4 sm:py-6">
            <div className="w-full max-w-lg overflow-hidden rounded-[24px] sm:rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-white px-4 py-4 sm:px-5">
                <h3 className="text-lg font-semibold text-gray-900">Review Tenant Verification</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Check the verification results before creating the tenant account.
                </p>
              </div>

              <div className="space-y-4 px-4 py-4 sm:px-5">
                {tenantAccountVerificationModal.issues.length > 0 ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                    <div className="text-sm font-semibold text-amber-800">Verification issues found</div>
                    <div className="mt-2 space-y-1">
                      {tenantAccountVerificationModal.issues.map((issue, index) => (
                        <p key={`${issue}-${index}`} className="text-sm text-amber-700">
                          {issue}
                        </p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                    All verification checks passed. You can proceed with tenant creation.
                  </div>
                )}

                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="text-sm font-semibold text-slate-800">Tax verification</div>
                  <div className="mt-2 space-y-1 break-words text-sm text-slate-700">
                    <p>Status: {tenantAccountVerificationModal.kraStatus?.kraNameMatches ? "Verified" : "Not verified"}</p>
                    <p>Verified PIN: {tenantAccountVerificationModal.kraStatus?.kraPin ? maskKraPin(tenantAccountVerificationModal.kraStatus.kraPin) : "Not available"}</p>
                    {tenantAccountVerificationModal.kraStatus?.kraNameMatches ? (
                      <p>Taxpayer Name: {tenantAccountVerificationModal.kraStatus?.kraTaxpayerName || "Not available"}</p>
                    ) : null}
                  </div>
                </div>

                {tenantAccountVerificationModal.allowKraOverride ? (
                  <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    Tax verification did not confirm this tenant. You can decline and correct the details, or proceed without tax verification.
                  </div>
                ) : null}
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-gray-200 px-4 py-4 sm:px-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeTenantAccountVerificationModal}
                  disabled={creatingTenantAccount}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 sm:w-auto"
                >
                  Cancel
                </button>
                {tenantAccountVerificationModal.existingUser ? (
                  <button
                    type="button"
                    onClick={handleUseExistingTenantAccount}
                    disabled={creatingTenantAccount}
                    className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400 sm:w-auto"
                  >
                    Use Existing Account
                  </button>
                ) : null}
                {tenantAccountVerificationModal.allowKraOverride ? (
                  <button
                    type="button"
                    onClick={handleConfirmTenantAccountRegistrationWithoutKra}
                    disabled={creatingTenantAccount}
                    className="w-full rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-gray-400 sm:w-auto"
                  >
                    {creatingTenantAccount ? "Creating Account..." : "Proceed Without Verification"}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={handleConfirmTenantAccountRegistration}
                  disabled={creatingTenantAccount || !tenantAccountVerificationModal.canProceed}
                  className={`w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition sm:w-auto ${
                    creatingTenantAccount || !tenantAccountVerificationModal.canProceed
                      ? "cursor-not-allowed bg-gray-400"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  {creatingTenantAccount
                    ? "Creating Account..."
                    : tenantAccountVerificationModal.canProceed
                      ? "Create Tenant Account"
                      : "Cannot Proceed"}
                </button>
              </div>
            </div>
          </div>
        )}

        {showAssignBlockedModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-semibold text-amber-700 mb-2">Cannot Assign Tenant Yet</h3>
              <p className="text-sm text-gray-700 mb-3">
                Update unit {selectedUnit?.label ? <span className="font-semibold">{selectedUnit.label}</span> : <span className="font-semibold">details</span>} before assigning a tenant.
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 mb-5">
                {assignBlockedMissingFields.includes("rent") && (
                  <li>Set Monthly Rent to an amount greater than 0.</li>
                )}
                {assignBlockedMissingFields.includes("type") && (
                  <li>Select a Unit Type.</li>
                )}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignBlockedModal(false);
                    setAssignBlockedMissingFields([]);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAssignBlockedModal(false);
                    setAssignBlockedMissingFields([]);
                    setShowUnitDetailsModal(false);
                    openUpdateUnitForm(selectedUnit);
                  }}
                  className="flex-1 bg-amber-600 text-white py-2 rounded-lg hover:bg-amber-700 transition-colors"
                >
                  Update Unit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Funds Modal */}
        {showAddFundsForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Add Funds to Wallet</h3>
              <p className="text-sm text-gray-600 mb-4">
                Current balance: {wallet?.balance != null ? formatMoney(wallet.balance) : "0.00"}
              </p>
              <form onSubmit={addFunds}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount to Add
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={addFundsAmount}
                    onChange={(e) => setAddFundsAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter amount"
                    required
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Add Funds
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddFundsForm(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Funds Modal (UT-Bills Wallet - staging only) */}
        {showAddUtilityFundsForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-md w-full">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold">Top Up UT-Bills Wallet</h3>
                <button
                  type="button"
                  onClick={() => setShowAddUtilityFundsForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  x
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-1">
                Current UT-Bills Wallet balance: {utilityWallet?.balance != null ? formatMoney(utilityWallet.balance) : "0.00"}
              </p>
              <p className="text-xs text-blue-700 mb-4">
                This top-up path is only shown on staging. Real users should top up with Paybill 4123905 and account U + ID number.
              </p>
              <form onSubmit={addUtilityFunds}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount to Add
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={addUtilityFundsAmount}
                    onChange={(e) => setAddUtilityFundsAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter amount"
                    required
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Top Up
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddUtilityFundsForm(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* UT-Bills Wallet Statements Modal */}
        {showUtilityWalletTransactionsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white w-screen h-screen sm:max-w-6xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-3 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg sm:text-2xl font-bold">UT-Bills Wallet Statements</h2>
                    <p className="mt-1 text-xs sm:text-sm text-blue-50">
                      Current UT-Bills Wallet Balance: {utilityWallet?.balance != null ? formatMoney(utilityWallet.balance) : "0.00"}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowUtilityWalletTransactionsModal(false);
                      setUtilityWalletStartDate("");
                      setUtilityWalletEndDate("");
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                    aria-label="Close UT-Bills statements"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-3 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setUtilityWalletFilterOpen(prev => !prev)}
                  className="w-full flex items-center justify-between text-gray-800 font-medium"
                >
                  <span>Filter</span>
                  <span className="text-sm text-gray-600">{utilityWalletFilterOpen ? "Hide" : "Show"}</span>
                </button>
                {utilityWalletFilterOpen && (
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={utilityWalletStartDate}
                        onChange={(e) => setUtilityWalletStartDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={utilityWalletEndDate}
                        onChange={(e) => setUtilityWalletEndDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={fetchFilteredUtilityWalletTransactions}
                        className="flex-1 sm:flex-none bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                      >
                        Filter
                      </button>
                      <button
                        onClick={clearUtilityWalletDateFilters}
                        className="flex-1 sm:flex-none bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:max-w-md">
                  <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
                    <div className="text-gray-500">Wallet Balance</div>
                    <div className="font-bold text-blue-700">
                      {formatMoney(getUtilityWalletBalance(utilityWallet || {}))}
                    </div>
                  </div>
                  <div className="rounded-lg border border-red-100 bg-white px-3 py-2">
                    <div className="text-gray-500">Bills Due</div>
                    <div className={`font-bold ${getUtilityDebtSummary(utilityWalletStatementSummary || {}).amountDue > 0 ? "text-red-700" : "text-gray-700"}`}>
                      {formatMoney(getUtilityDebtSummary(utilityWalletStatementSummary || {}).amountDue)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="p-2 sm:p-6">
                  {utilityWalletTransactionsLoading ? (
                    <div className="text-center py-8 text-gray-600">Loading statements...</div>
                  ) : utilityWalletTransactions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No transactions found.</div>
                  ) : (
                    <table className="w-full table-fixed border border-blue-200 rounded-none sm:rounded-lg text-[9px] sm:text-xs leading-[1.05]">
                      <colgroup>
                        <col className="w-[20%] sm:w-[18%]" />
                        <col className="w-[38%] sm:w-auto" />
                        <col className="w-[14%] sm:w-[14%]" />
                        <col className="w-[14%] sm:w-[14%]" />
                        <col className="w-[14%] sm:w-[14%]" />
                      </colgroup>
                      <thead className="bg-blue-600 text-white">
                        <tr className="text-center">
                          <th className="border border-blue-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Date</th>
                          <th className="border border-blue-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Description</th>
                          <th className="border border-blue-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Invoice</th>
                          <th className="border border-blue-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Payment</th>
                          <th className="border border-blue-500/40 px-0.5 sm:px-2 sm:py-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {utilityWalletTransactions.map((transaction, index) => {
                          const invoiceAmount = getUtilityRowChargeAmount(transaction);
                          const paymentAmount = getUtilityRowPaymentAmount(transaction);
                          const rowDate = getUtilityStatementDate(transaction);
                          const statusLabel = transaction.type === "deposit"
                            ? "Deposit"
                            : transaction.status || transaction.type || "posted";
                          return (
                            <tr key={getUtilitySettlementId(transaction) || transaction._id || transaction.invoiceId || index} className="odd:bg-white even:bg-blue-50/40">
                              <td className="border border-blue-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top whitespace-normal break-words">
                                <div className="block sm:hidden break-inside-avoid">{formatDateWithTime(rowDate)}</div>
                                <div className="hidden sm:block break-inside-avoid">{formatDateWithTime(rowDate)}</div>
                              </td>
                              <td className="border border-blue-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top break-words whitespace-normal leading-tight" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>
                                {transaction.description}
                                {transaction.utilityType && (
                                  <div className="mt-0.5 text-[10px] capitalize text-gray-600">Utility: {transaction.utilityType}</div>
                                )}
                                {transaction.type === "invoice" && (
                                  <div className="mt-1">
                                    <UtilityInvoiceStatusBadge invoice={transaction} compact />
                                  </div>
                                )}
                              </td>
                              <td className="border border-blue-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">
                                {invoiceAmount > 0 ? (
                                  <span className="text-red-600">{formatMoney(invoiceAmount)}</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="border border-blue-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">
                                {paymentAmount > 0 ? (
                                  <span className="text-blue-700">{formatMoney(paymentAmount)}</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="border border-blue-100 px-0.5 sm:px-2 py-1 sm:py-2 text-center align-top whitespace-normal break-words capitalize">
                                {statusLabel}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transaction Statement Modal */}
        {showTransactionStatement && transactionStatement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10 p-0 sm:p-4">
            <div className="bg-white w-screen h-screen sm:max-w-4xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-3 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">
                      {transactionStatement.isPersonalView
                        ? 'Statements'
                        : transactionStatement.isManagementView
                          ? transactionStatement.tenantName
                            ? `${transactionStatement.tenantName}'s Unit History`
                            : 'Unit History'
                          : 'Transaction Statement'}
                    </h3>
                    <div className="text-sm opacity-90">
                      <span className="font-medium">{transactionStatement.propertyName}</span> •
                      <span> Unit {transactionStatement.unitLabel}</span>
                      {transactionStatement.isPersonalView && (
                        <span> • My Statements</span>
                      )}
                      {transactionStatement.isManagementView && transactionStatement.tenantName && (
                        <span> • {transactionStatement.tenantName}</span>
                      )}
                      {!transactionStatement.isPersonalView && !transactionStatement.isManagementView && transactionStatement.tenantNames && (
                        <span> • {transactionStatement.tenantNames.join(', ')}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowTransactionStatement(false);
                      setStatementStartDate("");
                      setStatementEndDate("");
                      setSelectedTenantForHistory(null);
                      setTenantMonthlyStatus(null);
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Date Filter Section (collapsible) */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setStatementFilterOpen(prev => !prev)}
                  className="w-full flex items-center justify-between text-gray-800 font-medium"
                >
                  <span>Filter</span>
                  <Icon name={statementFilterOpen ? 'chevron-up' : 'chevron-down'} size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
                </button>
                {statementFilterOpen && (
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={statementStartDate}
                        onChange={(e) => setStatementStartDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={statementEndDate}
                        onChange={(e) => setStatementEndDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={fetchFilteredStatement}
                        className="flex-1 sm:flex-none bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                      >
                        Filter
                      </button>
                      <button
                        onClick={clearDateFilters}
                        className="flex-1 sm:flex-none bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
                      >
                        Clear
                      </button>
                      <button
                        onClick={downloadStatementAsPDF}
                        className="flex-1 sm:flex-none bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm flex items-center gap-1"
                        title="Download as PDF"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                        </svg>
                        Export PDF
                        <span className="hidden sm:inline"></span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="p-2 sm:p-4">
                  {/* Unpaid invoices section — shown when opened from unit details modal */}
                  {tenantMonthlyStatus && (() => {
                    const allInvoiceRows = Array.isArray(tenantMonthlyStatus.invoiceRows)
                      ? tenantMonthlyStatus.invoiceRows
                      : [];
                    const visibleInvoiceRows = getTenantMonthlyStatusVisibleInvoices(tenantMonthlyStatus);
                    const hasMoreInvoiceRows = allInvoiceRows.length > visibleInvoiceRows.length;
                    return (
                      <div className="mb-4">
                        <div className="px-1 sm:px-2 pb-3">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-gray-600">Net Balance</span>
                            <span className={`text-base sm:text-lg font-semibold ${Number(tenantMonthlyStatus.summary?.netBalance || 0) > 0 ? "text-red-700" : Number(tenantMonthlyStatus.summary?.netBalance || 0) < 0 ? "text-emerald-700" : "text-gray-900"}`}>
                              {Number(tenantMonthlyStatus.summary?.netBalance || 0) < 0
                                ? `-${formatMoney(Math.abs(Number(tenantMonthlyStatus.summary?.netBalance || 0)))}`
                                : formatMoney(tenantMonthlyStatus.summary?.netBalance || 0)}
                            </span>
                          </div>
                          <div className="mt-1 text-xs text-gray-600">Outstanding Invoices: {formatMoney(tenantMonthlyStatus.summary?.totalOutstanding || 0)}</div>
                          <div className="text-xs text-gray-600">Unapplied Credit: {formatMoney(tenantMonthlyStatus.summary?.unappliedCredit || 0)}</div>
                          <div className="text-xs text-gray-600">Oldest Unpaid: {tenantMonthlyStatus.summary?.oldestUnpaid || "-"}</div>
                        </div>
                        {visibleInvoiceRows.length > 0 ? (
                          <div className="overflow-x-hidden">
                            <table className="w-full table-fixed border border-gray-200 text-[8px] sm:text-xs leading-tight">
                              <thead className="bg-slate-100 text-slate-700">
                                <tr className="text-center">
                                  <th className="w-[18%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Invoice Date</th>
                                  <th className="w-[24%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Description</th>
                                  <th className="w-[13%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Invoice Amount</th>
                                  <th className="w-[13%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Paid Amount</th>
                                  <th className="w-[10%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Balance</th>
                                  <th className="w-[12%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Cleared Date</th>
                                  <th className="w-[10%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Details</th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleInvoiceRows.map((row, rowIdx) => (
                                  <tr
                                    key={row.invoiceId || `${row.date || "no-date"}-${rowIdx}`}
                                    onClick={() => { setSelectedTenantInvoiceRow(row); setShowTenantInvoicePaymentsModal(true); }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedTenantInvoiceRow(row); setShowTenantInvoicePaymentsModal(true); } }}
                                    className={`cursor-pointer ${Number(row.balance || 0) > 0 ? "bg-red-50/50 hover:bg-red-100/60" : "bg-emerald-50/70 hover:bg-emerald-100/60"}`}
                                  >
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-center font-medium text-gray-800 break-words">{formatStatusDate(row.date) || "-"}</td>
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-gray-700 break-words whitespace-normal">{row.description || "Invoice"}</td>
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-right">{formatMoney(row.invoiceAmount)}</td>
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-right">{formatMoney(row.paidAmount)}</td>
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-right font-semibold">
                                      <span className={Number(row.balance || 0) > 0 ? "text-red-700" : "text-emerald-700"}>{formatMoney(row.balance)}</span>
                                    </td>
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-center">
                                      {Number(row.balance || 0) > 0 && Number(row.paidAmount || 0) > 0 ? (
                                        <span className="inline-flex items-center justify-center rounded-full bg-yellow-100 border border-yellow-200 px-1 sm:px-2 py-0.5 text-[8px] sm:text-[10px] font-semibold text-yellow-800">Partial</span>
                                      ) : Number(row.balance || 0) > 0 ? (
                                        <span className="inline-flex items-center justify-center rounded-full bg-red-100 border border-red-200 px-1 sm:px-2 py-0.5 text-[8px] sm:text-[10px] font-semibold text-red-800 text-center"><span className="sm:hidden">Overdue</span><span className="hidden sm:inline">Vacate if 2 months overdue</span></span>
                                      ) : (
                                        <span className="text-gray-700">{formatStatusDate(row.clearedDate || row.date) || "-"}</span>
                                      )}
                                    </td>
                                    <td className="border border-gray-200 px-0 sm:px-2 py-2 text-center align-middle">
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setSelectedTenantInvoiceRow(row); setShowTenantInvoicePaymentsModal(true); }}
                                        className="inline-flex h-6 w-6 sm:h-auto sm:w-auto items-center justify-center gap-1 rounded-md border border-slate-300 bg-white p-0 sm:px-2 sm:py-1 text-[10px] sm:text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
                                        aria-label="View invoice payments"
                                      >
                                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                        <span className="hidden sm:inline">View</span>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {hasMoreInvoiceRows && (
                              <div className="flex justify-center pt-1">
                                <button
                                  type="button"
                                  onClick={() => setTenantMonthlyStatusVisibleInvoices((prev) => prev + TENANT_MONTHLY_STATUS_LIMIT)}
                                  className="inline-flex h-8 items-center rounded-md border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
                                >
                                  More
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-gray-500 text-sm">No invoices found.</div>
                        )}
                        <div className="my-4 border-t border-gray-200" />
                      </div>
                    );
                  })()}
                  {transactionStatement.transactions.length > 0 ? (
                    <div>
                      <table className="w-full table-fixed border border-emerald-200 rounded-none sm:rounded-lg text-[9px] sm:text-xs leading-[1.05]">
                        <colgroup>
                          <col className="w-[18%] sm:w-[18%]" />
                          <col className="w-[30%] sm:w-auto" />
                          <col className="w-[12%] sm:w-[12%]" />
                          <col className="w-[12%] sm:w-[12%]" />
                          <col className="w-[16%] sm:w-[18%]" />
                        </colgroup>
                        <thead className="bg-emerald-600 text-white">
                          <tr className="text-center">
                            <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Date</th>
                            <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Description</th>
                            <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Invoice</th>
                            <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Payment</th>
                            <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const txs = Array.isArray(transactionStatement.transactions)
                              ? transactionStatement.transactions
                              : [];

                            // FIFO allocation: walk oldest-first to map each payment to the invoice(s) it covers
                            const sortedAsc = [...txs].sort((a, b) => new Date(a.date) - new Date(b.date));
                            const openInvoices = [];
                            const paymentAssociations = {};
                            for (const tx of sortedAsc) {
                              if (Number(tx.invoiceAmount || 0) > 0) {
                                openInvoices.push({
                                  remaining: Number(tx.invoiceAmount),
                                  date: tx.date,
                                  description: tx.description,
                                });
                              } else if (Number(tx.paymentAmount || 0) > 0 && tx.paymentId) {
                                let rem = Number(tx.paymentAmount);
                                const covered = [];
                                while (rem > 0.005 && openInvoices.length > 0) {
                                  const inv = openInvoices[0];
                                  const applied = Math.min(rem, inv.remaining);
                                  rem -= applied;
                                  inv.remaining -= applied;
                                  const d = inv.date ? new Date(inv.date) : null;
                                  const label = d && !isNaN(d)
                                    ? d.toLocaleString('en-US', { month: 'short', year: 'numeric' })
                                    : (inv.description || 'Invoice');
                                  if (!covered.includes(label)) covered.push(label);
                                  if (inv.remaining <= 0.005) openInvoices.shift();
                                }
                                if (covered.length > 0) paymentAssociations[String(tx.paymentId)] = covered.join(', ');
                              }
                            }

                            return txs.map((transaction, index) => {
                              const isPayment = Number(transaction.paymentAmount || 0) > 0;
                              const assoc = isPayment && transaction.paymentId
                                ? paymentAssociations[String(transaction.paymentId)]
                                : null;
                              const displayDescription = assoc
                                ? `${transaction.description} (for ${assoc})`
                                : transaction.description;

                              return (
                              <tr key={index} className="odd:bg-white even:bg-emerald-50/40">
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top whitespace-normal break-words">
                                  <div className="block sm:hidden break-inside-avoid">{formatDateWithTime(transaction.date, true)}</div>
                                  <div className="hidden sm:block break-inside-avoid">{formatDateWithTime(transaction.date, true)}</div>
                                </td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top break-words whitespace-normal leading-tight" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>{displayDescription}</td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top text-right whitespace-nowrap">{transaction.invoiceAmount !== 0 ? formatMoney(transaction.invoiceAmount) : ''}</td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top text-right whitespace-nowrap">{transaction.paymentAmount > 0 ? formatMoney(transaction.paymentAmount) : ''}</td>
                                <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 font-semibold text-right align-top whitespace-nowrap">
                                  <span className={`${transaction.runningBalance > 0 ? 'text-red-700' : transaction.runningBalance < 0 ? 'text-emerald-700' : 'text-gray-800'}`}>
                                    {transaction.runningBalance < 0 ? `-${formatMoney(Math.abs(transaction.runningBalance))}` : formatMoney(transaction.runningBalance)}
                                  </span>
                                </td>
                              </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">No transactions found for this unit.</p>
                    </div>
                  )}

                </div>
              </div>

              <div className="p-3 sm:p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setShowTransactionStatement(false)}
                  className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tenant Invoices Modal */}
        {showTenantMonthlyStatus && tenantMonthlyStatus && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20 p-0 sm:p-4">
            <div className="bg-white w-screen h-screen sm:max-w-4xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
              <div className="bg-slate-800 p-3 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center gap-3">
                  <div className="min-w-0">
                    <h3 className="text-xl sm:text-2xl font-bold mb-1">Invoices</h3>
                    <p className="text-sm opacity-90 truncate">
                      <span className="font-medium">{tenantMonthlyStatus.propertyName}</span>
                      <span> - Unit {tenantMonthlyStatus.unitLabel}</span>
                      <span> - {tenantMonthlyStatus.tenantName}</span>
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={downloadTenantInvoicesAsPDF}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-600 sm:text-sm"
                      title="Export invoices to PDF"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v12m0 0l-4-4m4 4l4-4M5 19h14" />
                      </svg>
                      <span className="hidden sm:inline">Export PDF</span>
                      <span className="sm:hidden">Export</span>
                    </button>
                    <button
                    onClick={() => {
                      setShowTenantMonthlyStatus(false);
                      setTenantMonthlyStatus(null);
                      setShowTenantInvoicePaymentsModal(false);
                      setSelectedTenantInvoiceRow(null);
                      setTenantMonthlyStatusVisibleInvoices(TENANT_MONTHLY_STATUS_LIMIT);
                    }}
                    className="bg-white/10 hover:bg-white/20 text-white rounded-lg p-2 transition-colors"
                    aria-label="Close monthly status"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="p-2 sm:p-4">
                  <div className="px-1 sm:px-2 pb-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-gray-600">Net Balance</span>
                      <span
                        className={`text-base sm:text-lg font-semibold ${
                          Number(tenantMonthlyStatus.summary?.netBalance || 0) > 0
                            ? "text-red-700"
                            : Number(tenantMonthlyStatus.summary?.netBalance || 0) < 0
                              ? "text-emerald-700"
                              : "text-gray-900"
                        }`}
                      >
                        {Number(tenantMonthlyStatus.summary?.netBalance || 0) < 0
                          ? `-${formatMoney(Math.abs(Number(tenantMonthlyStatus.summary?.netBalance || 0)))}`
                          : formatMoney(tenantMonthlyStatus.summary?.netBalance || 0)}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      Outstanding Invoices: {formatMoney(tenantMonthlyStatus.summary?.totalOutstanding || 0)}
                    </div>
                    <div className="text-xs text-gray-600">
                      Unapplied Credit: {formatMoney(tenantMonthlyStatus.summary?.unappliedCredit || 0)}
                    </div>
                    <div className="text-xs text-gray-600">
                      Oldest Unpaid: {tenantMonthlyStatus.summary?.oldestUnpaid || "-"}
                    </div>
                  </div>
                  {(() => {
                    const allInvoiceRows = Array.isArray(tenantMonthlyStatus.invoiceRows)
                      ? tenantMonthlyStatus.invoiceRows
                      : [];
                    const visibleInvoiceRows = getTenantMonthlyStatusVisibleInvoices(tenantMonthlyStatus);
                    const hasMoreInvoiceRows = allInvoiceRows.length > visibleInvoiceRows.length;
                    return (
                      <div className="space-y-4">
                        {visibleInvoiceRows.length > 0 ? (
                          <div className="overflow-x-hidden">
                            <table className="w-full table-fixed border border-gray-200 text-[8px] sm:text-xs leading-tight">
                              <thead className="bg-slate-100 text-slate-700">
                                <tr className="text-center">
                                  <th className="w-[18%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Invoice Date</th>
                                  <th className="w-[24%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Description</th>
                                  <th className="w-[13%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Invoice Amount</th>
                                  <th className="w-[13%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Paid Amount</th>
                                  <th className="w-[10%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Balance</th>
                                  <th className="w-[12%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">Cleared Date</th>
                                  <th className="w-[44px] sm:w-[10%] border border-gray-200 px-0.5 sm:px-2 py-2 font-semibold break-words whitespace-normal">
                                    <span className="hidden sm:inline">Details</span>
                                    <span className="sm:hidden inline-flex items-center justify-center" aria-hidden="true">
                                      <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                    </span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleInvoiceRows.map((row, rowIdx) => {
                                  return (
                                  <tr
                                    key={row.invoiceId || `${row.date || "no-date"}-${rowIdx}`}
                                    onClick={() => {
                                      setSelectedTenantInvoiceRow(row);
                                      setShowTenantInvoicePaymentsModal(true);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setSelectedTenantInvoiceRow(row);
                                        setShowTenantInvoicePaymentsModal(true);
                                      }
                                    }}
                                    className={`cursor-pointer ${
                                      Number(row.balance || 0) > 0
                                        ? "bg-red-50/50 hover:bg-red-100/60"
                                        : "bg-emerald-50/70 hover:bg-emerald-100/60"
                                    }`}
                                  >
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-center font-medium text-gray-800 break-words">
                                      {formatStatusDate(row.date) || "-"}
                                    </td>
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-gray-700 break-words whitespace-normal">
                                      {row.description || "Invoice"}
                                    </td>
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-right">
                                      {formatMoney(row.invoiceAmount)}
                                    </td>
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-right">
                                      {formatMoney(row.paidAmount)}
                                    </td>
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-right font-semibold">
                                      <span className={Number(row.balance || 0) > 0 ? "text-red-700" : "text-emerald-700"}>
                                        {formatMoney(row.balance)}
                                      </span>
                                    </td>
                                    <td className="border border-gray-200 px-0.5 sm:px-2 py-2 text-center">
                                      {Number(row.balance || 0) > 0 && Number(row.paidAmount || 0) > 0 ? (
                                        <span className="inline-flex max-w-full items-center justify-center rounded-full bg-yellow-100 border border-yellow-200 px-1 sm:px-2 py-0.5 text-[8px] sm:text-[10px] font-semibold text-yellow-800 whitespace-normal break-words text-center">
                                          <span className="sm:hidden">Partial</span>
                                          <span className="hidden sm:inline">Partial</span>
                                        </span>
                                      ) : Number(row.balance || 0) > 0 ? (
                                        <span className="inline-flex max-w-full items-center justify-center rounded-full bg-red-100 border border-red-200 px-1 sm:px-2 py-0.5 text-[8px] sm:text-[10px] font-semibold text-red-800 whitespace-normal break-words text-center">
                                          <span className="sm:hidden">Overdue</span>
                                          <span className="hidden sm:inline">Vacate NOTICE if 2nd Month</span>
                                        </span>
                                      ) : (
                                        <span className="text-gray-700">{formatStatusDate(row.clearedDate || row.date) || "-"}</span>
                                      )}
                                    </td>
                                    <td className="border border-gray-200 px-0 sm:px-2 py-2 text-center align-middle">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedTenantInvoiceRow(row);
                                          setShowTenantInvoicePaymentsModal(true);
                                        }}
                                        className="inline-flex h-6 w-6 sm:h-auto sm:w-auto items-center justify-center gap-1 rounded-md border border-slate-300 bg-white p-0 sm:px-2 sm:py-1 text-[10px] sm:text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
                                        aria-label="View invoice payments"
                                      >
                                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                        <span className="hidden sm:inline">View</span>
                                      </button>
                                    </td>
                                  </tr>
                                )})}
                              </tbody>
                            </table>
                            {hasMoreInvoiceRows && (
                              <div className="flex justify-center pt-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTenantMonthlyStatusVisibleInvoices((prev) => prev + TENANT_MONTHLY_STATUS_LIMIT);
                                  }}
                                  className="inline-flex h-8 items-center rounded-md border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
                                >
                                  More
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-10 text-gray-500 text-sm">No invoices found for this unit.</div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="p-3 sm:p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => {
                    setShowTenantMonthlyStatus(false);
                    setTenantMonthlyStatus(null);
                    setShowTenantInvoicePaymentsModal(false);
                    setSelectedTenantInvoiceRow(null);
                    setTenantMonthlyStatusVisibleInvoices(TENANT_MONTHLY_STATUS_LIMIT);
                  }}
                  className="w-full px-4 py-2 bg-slate-700 text-white rounded hover:bg-slate-800 transition-colors text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tenant Invoice Payments Modal */}
        {showTenantInvoicePaymentsModal && tenantMonthlyStatus && selectedTenantInvoiceRow && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30 p-0 sm:p-4">
            <div className="bg-white w-screen h-screen sm:max-w-3xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
              <div className="bg-slate-900 p-3 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center gap-3">
                  <div className="min-w-0">
                    <h3 className="text-lg sm:text-2xl font-bold mb-1">Invoice Payments</h3>
                    <p className="text-sm opacity-90 truncate">
                      <span className="font-medium">{tenantMonthlyStatus.propertyName}</span>
                      <span> - Unit {tenantMonthlyStatus.unitLabel}</span>
                      <span> - {tenantMonthlyStatus.tenantName}</span>
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={downloadTenantInvoicePaymentsAsPDF}
                      className="bg-white/10 hover:bg-white/20 text-white rounded-lg p-2 transition-colors"
                      aria-label="Export invoice payments to PDF"
                      title="Export PDF"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v12m0 0l-4-4m4 4l4-4M5 19h14" />
                      </svg>
                    </button>
                    <button
                    onClick={() => {
                      setShowTenantInvoicePaymentsModal(false);
                      setSelectedTenantInvoiceRow(null);
                    }}
                    className="bg-white/10 hover:bg-white/20 text-white rounded-lg p-2 transition-colors"
                    aria-label="Close invoice payments"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="p-2 sm:p-4 space-y-3">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs sm:text-sm text-gray-800">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold break-words">{selectedTenantInvoiceRow.description || "Invoice"}</div>
                        <div className="text-[11px] text-gray-600">
                          Date: {formatStatusDate(selectedTenantInvoiceRow.date) || "-"}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-[11px] sm:text-xs">
                        <div className="text-right">
                          <div className="text-gray-600">Invoice</div>
                          <div className="font-semibold">{formatMoney(selectedTenantInvoiceRow.invoiceAmount || 0)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-600">Paid</div>
                          <div className="font-semibold">{formatMoney(selectedTenantInvoiceRow.paidAmount || 0)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-600">Balance</div>
                          <div className={`font-semibold ${Number(selectedTenantInvoiceRow.balance || 0) > 0 ? "text-red-700" : "text-emerald-700"}`}>
                            {formatMoney(selectedTenantInvoiceRow.balance || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const invoiceId = selectedTenantInvoiceRow?.invoiceId ? String(selectedTenantInvoiceRow.invoiceId) : "";
                    const rows = invoiceId && tenantMonthlyStatus?.invoicePayments
                      ? tenantMonthlyStatus.invoicePayments[invoiceId]
                      : [];
                    const paymentRows = Array.isArray(rows) ? rows : [];

                    if (!invoiceId) {
                      return (
                        <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 px-3 py-2">
                          No invoice ID found for this row.
                        </div>
                      );
                    }

                    return paymentRows.length > 0 ? (
                      <table className="w-full table-fixed border border-gray-200 text-[8px] sm:text-xs leading-tight">
                        <colgroup>
                          <col className="w-[28%]" />
                          <col className="w-[18%]" />
                          <col className="w-[34%]" />
                          <col className="w-[20%]" />
                        </colgroup>
                        <thead className="bg-slate-100 text-slate-700">
                          <tr className="text-center">
                            <th className="border border-gray-200 px-1 sm:px-2 py-2 font-semibold break-words whitespace-normal">Date</th>
                            <th className="border border-gray-200 px-1 sm:px-2 py-2 font-semibold break-words whitespace-normal">Type</th>
                            <th className="border border-gray-200 px-1 sm:px-2 py-2 font-semibold break-words whitespace-normal">Description</th>
                            <th className="border border-gray-200 px-1 sm:px-2 py-2 font-semibold break-words whitespace-normal">Paid</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentRows.map((row, idx) => {
                            const type = String(row?.type || "").toLowerCase();
                            const typeLabel =
                              type === "payment" ? "Payment" : type === "waiver" ? "Waiver" : "Credit";
                            const badgeClass =
                              type === "payment"
                                ? "bg-emerald-100 border-emerald-200 text-emerald-800"
                                : type === "waiver"
                                  ? "bg-amber-100 border-amber-200 text-amber-800"
                                  : "bg-slate-100 border-slate-200 text-slate-700";
                            return (
                              <tr key={`invoice-payment-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                <td className="border border-gray-200 px-1 sm:px-2 py-1.5 text-gray-700 break-words">
                                  {formatDateWithTime(row?.date, true)}
                                </td>
                                <td className="border border-gray-200 px-1 sm:px-2 py-1.5 text-center">
                                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[8px] sm:text-[10px] font-semibold ${badgeClass}`}>
                                    {typeLabel}
                                  </span>
                                </td>
                                <td className="border border-gray-200 px-1 sm:px-2 py-1.5 text-gray-700 break-words">
                                  {row?.description || "-"}
                                </td>
                                <td className="border border-gray-200 px-1 sm:px-2 py-1.5 text-right font-semibold text-gray-800">
                                  {formatMoney(row?.amountApplied || 0)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 px-3 py-2">
                        No payments found for this invoice.
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="p-3 sm:p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => {
                    setShowTenantInvoicePaymentsModal(false);
                    setSelectedTenantInvoiceRow(null);
                  }}
                  className="w-full px-4 py-2 bg-slate-800 text-white rounded hover:bg-slate-900 transition-colors text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Property Statements Modal (Manager - Property level) */}
        {showPropertyStatementsModal && selectedProperty && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white w-screen h-screen sm:max-w-5xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-3 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold mb-1">Property Statements</h3>
                    <div className="text-sm opacity-90">
                      <span className="font-medium">{selectedProperty.name}</span> •
                      <span> Payments by tenants</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowPropertyStatementsModal(false);
                      setPropertyStatementStartDate("");
                      setPropertyStatementEndDate("");
                      setPropertyPayments([]);
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Date Filter Section (collapsible) */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setPropertyFilterOpen(prev => !prev)}
                  className="w-full flex items-center justify-between text-gray-800 font-medium"
                >
                  <span>Filter</span>
                  <Icon name={propertyFilterOpen ? 'chevron-up' : 'chevron-down'} size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
                </button>
                {propertyFilterOpen && (
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={propertyStatementStartDate}
                        onChange={(e) => setPropertyStatementStartDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={propertyStatementEndDate}
                        onChange={(e) => setPropertyStatementEndDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => fetchPropertyPayments(selectedProperty._id)}
                        disabled={propertyPaymentsLoading}
                        className="flex-1 sm:flex-none bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                      >
                        {propertyPaymentsLoading ? 'Refreshing...' : 'Refresh'}
                      </button>
                      <button
                        onClick={() => { setPropertyStatementStartDate(""); setPropertyStatementEndDate(""); }}
                        className="flex-1 sm:flex-none bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="p-2 sm:p-4">
                  {propertyPaymentsLoading ? (
                    <div className="flex items-center justify-center py-8 text-gray-600">Loading statements...</div>
                  ) : (() => {
                    const filtered = filterRowsByDateRange(
                      propertyPayments,
                      propertyStatementStartDate,
                      propertyStatementEndDate
                    );
                    // Totals across filtered rows using raw split amounts from Payment.split
                    const totals = sumPaymentRows(filtered);

                    const totalAmount = totals.amount || 0;

                    // Total current balance for the property:
                    // sum of per-unit balances using the same logic as the units table
                    const totalCurrentBalance = sumCurrentUnitBalances(
                      units,
                      managerUnitBalances
                    );

                    const totalMgmtPct = totalAmount > 0 ? (totals.manager / totalAmount) * 100 : 0;
                    const totalPortalPct = totalAmount > 0 ? (totals.platform / totalAmount) * 100 : 0;
                    // eslint-disable-next-line no-unused-vars
                    const totalLlPct = Math.max(0, 100 - totalMgmtPct - totalPortalPct);

                    return filtered.length > 0 ? (
                      <div>
                        <table className="w-full table-fixed border border-green-200 rounded-none sm:rounded-lg text-[9px] sm:text-xs leading-[1.05]">
                          <colgroup>
                            <col className="w-[12%] sm:w-[12%]" />
                            <col className="w-[18%] sm:w-auto" />
                            <col className="w-[10%] sm:w-[10%]" />
                            <col className="w-[10%] sm:w-[10%]" />
                            <col className="w-[10%] sm:w-[10%]" />
                            <col className="w-[20%] sm:w-auto" />
                          </colgroup>
                          <thead className="bg-green-600 text-white">
                            <tr className="text-center">
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Date</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Tenant</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Unit</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Amount</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Current Balance</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Description</th>
                            </tr>
                            <tr className="bg-green-50 text-green-900">
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2">Total</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2">Across selected period</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2"></td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2 text-left text-[7px]">{formatMoney(totalAmount)}</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right text-[7px]">
                                {formatMoney(totalCurrentBalance)}
                              </td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2 text-center">-</td>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const runningBalancesByTenant = {};
                              return filtered.map((payment, idx) => {
                                const tenantKey = (payment?.tenant?._id) || (payment?.tenantId) || (payment?.tenant?.email) || (payment?.tenant?.name) || 'unknown';
                                const previousBalance = runningBalancesByTenant[tenantKey] ?? 0;
                                const deltaAmount = Number(payment?.amount ?? 0);
                                const currentBalance = previousBalance + deltaAmount;
                                runningBalancesByTenant[tenantKey] = currentBalance;
                                const isNegative = currentBalance < 0;
                                return (
                                  <tr key={payment._id || idx} className="odd:bg-white even:bg-green-50/40">
                                    <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top sm:whitespace-nowrap whitespace-normal break-words">
                                      <div className="block sm:hidden break-inside-avoid">{formatDateWithTime(payment.date)}</div>
                                      <div className="hidden sm:block break-inside-avoid">{formatDateWithTime(payment.date)}</div>
                                    </td>
                                    <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top break-words" style={{ wordBreak: 'break-word' }}>
                                      {payment?.tenant?.name || payment?.tenant?.email || 'Unknown'}
                                    </td>
                                    <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top sm:whitespace-nowrap whitespace-normal">
                                      {payment?.unit?.label || '-'}
                                    </td>
                                    <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 font-semibold text-right align-top whitespace-nowrap">{formatMoney(payment?.amount || 0)}</td>
                                    <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 font-semibold text-right align-top whitespace-nowrap">
                                      <span className={isNegative ? 'text-red-700' : 'text-gray-800'}>
                                        {formatMoney(Math.abs(currentBalance))}
                                      </span>
                                    </td>
                                    <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top">
                                      <div className="whitespace-pre-wrap break-words leading-tight" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>{payment?.description || 'Payment'}</div>
                                    </td>
                                  </tr>
                                );
                              });
                            })()}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p className="text-sm">No payments found for this property.</p>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="p-3 sm:p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setShowPropertyStatementsModal(false)}
                  className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Company Transactions Modal */}
        {showCompanyTransactionsModal && selectedCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white w-screen h-screen sm:max-w-6xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-3 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg sm:text-2xl font-bold mb-1">Company Transactions</h3>
                    <div className="text-sm opacity-90">
                      <span className="font-medium">{selectedCompany.companyName}</span> • Payments across all properties
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowCompanyTransactionsModal(false);
                      setCompanyStartDate("");
                      setCompanyEndDate("");
                      setCompanyTransactions([]);
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setCompanyFilterOpen(prev => !prev)}
                  className="w-full flex items-center justify-between text-gray-800 font-medium"
                >
                  <span>Filter</span>
                  <Icon name={companyFilterOpen ? 'chevron-up' : 'chevron-down'} size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
                </button>
                {companyFilterOpen && (
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={companyStartDate}
                        onChange={(e) => setCompanyStartDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={companyEndDate}
                        onChange={(e) => setCompanyEndDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => fetchCompanyTransactions(selectedCompany._id)}
                        disabled={companyTransactionsLoading}
                        className="flex-1 sm:flex-none bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                      >
                        {companyTransactionsLoading ? 'Refreshing...' : 'Refresh'}
                      </button>
                      <button
                        onClick={() => { setCompanyStartDate(""); setCompanyEndDate(""); }}
                        className="flex-1 sm:flex-none bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
                      >
                        Clear
                      </button>
                      <button
                        onClick={downloadCompanyTransactionsAsPDF}
                        className="flex-1 sm:flex-none bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm flex items-center gap-1"
                        title="Download as PDF"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                        </svg>
                        Export PDF
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="p-2 sm:p-6">
                  {companyTransactionsLoading ? (
                    <div className="text-center py-8 text-gray-600">Loading transactions...</div>
                  ) : (() => {
                    const rows = filterRowsByDateRange(
                      companyTransactions,
                      companyStartDate,
                      companyEndDate
                    );
                    const paymentTotals = sumPaymentRows(rows);
                    const totals = {
                      amount: paymentTotals.amount,
                      rl: paymentTotals.platform,
                      mgmt: paymentTotals.manager,
                      ll: paymentTotals.landlord,
                    };

                    return (
                      <div>
                        <table className="w-full table-fixed border border-green-200 rounded-none sm:rounded-lg text-[9px] sm:text-xs leading-[1.05]">
                          <colgroup>
                            <col className="w-[12%] sm:w-[16%]" />
                            <col className="w-[30%] sm:w-auto" />
                            <col className="w-[14%] sm:w-[12%]" />
                            <col className="w-[10%] sm:w-[10%]" />
                            <col className="w-[17%] sm:w-[12%]" />
                            <col className="w-[17%] sm:w-[12%]" />
                          </colgroup>
                          <thead className="bg-green-600 text-white">
                            <tr className="text-center">
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Date</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Description</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Amount</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">RL</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Mgmt</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">LL</th>
                            </tr>
                            <tr className="bg-green-50 text-green-900">
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2">Total</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2">Gross across selected period</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.amount)}</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.rl)}</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.mgmt)}</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.ll)}</td>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((p, idx) => {
                              const split = getPaymentSplit(p);
                              return (
                                <tr key={p._id || idx} className="odd:bg-white even:bg-green-50/40">
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top whitespace-normal break-words">
                                    <div className="block sm:hidden break-inside-avoid">{formatDateWithTime(p.date)}</div>
                                    <div className="hidden sm:block break-inside-avoid">{formatDateWithTime(p.date)}</div>
                                  </td>
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top break-words whitespace-normal leading-tight" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>
                                    {p.description || `Payment - ${p.unit?.label || ''}`}
                                  </td>
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.amount)}</td>
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.platform)}</td>
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.manager)}</td>
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.landlord)}</td>
                                </tr>
                              );
                            })}
                            {rows.length === 0 && (
                              <tr>
                                <td colSpan={6} className="text-center text-gray-500 px-4 py-6 text-sm">No transactions found for this company.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setShowCompanyTransactionsModal(false)}
                  className="w-full sm:w-auto px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Management Transactions Modal (combined across all companies) */}
        {showManagementTransactionsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white w-screen h-screen sm:max-w-6xl max-h-[90vh] sm:rounded-lg rounded-none flex flex-col overflow-x-hidden">
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-4 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-1">Transactions</h3>
                    <div className="text-sm opacity-90">
                      <span className="font-medium">All Companies</span> • Payments across all managed properties
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowManagementTransactionsModal(false);
                      setMgmtStartDate("");
                      setMgmtEndDate("");
                      setManagementTransactions([]);
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Date Filter Section (collapsible) */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setMgmtFilterOpen(prev => !prev)}
                  className="w-full flex items-center justify-between text-gray-800 font-medium"
                >
                  <span>Filter</span>
                  <Icon name={mgmtFilterOpen ? 'chevron-up' : 'chevron-down'} size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
                </button>
                {mgmtFilterOpen && (
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={mgmtStartDate}
                        onChange={(e) => setMgmtStartDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={mgmtEndDate}
                        onChange={(e) => setMgmtEndDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={fetchManagementTransactions}
                        disabled={managementTransactionsLoading}
                        className="flex-1 sm:flex-none bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                      >
                        {managementTransactionsLoading ? 'Refreshing...' : 'Apply'}
                      </button>
                      <button
                        onClick={() => { setMgmtStartDate(""); setMgmtEndDate(""); fetchManagementTransactions(); }}
                        className="flex-1 sm:flex-none bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="p-4 sm:p-6">
                  {managementTransactionsLoading ? (
                    <div className="text-center py-8 text-gray-600">Loading transactions...</div>
                  ) : (() => {
                    const rows = filterRowsByDateRange(
                      managementTransactions || [],
                      mgmtStartDate,
                      mgmtEndDate
                    );
                    const paymentTotals = sumPaymentRows(rows);
                    const totals = {
                      amount: paymentTotals.amount,
                      rl: paymentTotals.platform,
                      mgmt: paymentTotals.manager,
                      ll: paymentTotals.landlord,
                    };

                    return (
                      <div>
                        <table className="w-full table-fixed border border-emerald-200 rounded-none sm:rounded-lg text-[9px] sm:text-xs leading-[1.05]">
                          <colgroup>
                            <col className="w-[12%] sm:w-[16%]" />
                            <col className="w-[15%] sm:w-auto" />
                            <col className="w-[13%] sm:w-auto" />
                            <col className="w-[20%] sm:w-[12%]" />
                            <col className="w-[13%] sm:w-[10%]" />
                            <col className="w-[13%] sm:w-[12%]" />
                            <col className="w-[14%] sm:w-[12%]" />
                          </colgroup>
                          <thead className="bg-emerald-600 text-white">
                            <tr className="text-center">
                              <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Date</th>
                              <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">
                                <span className="inline sm:hidden">Desc</span>
                                <span className="hidden sm:inline">Description</span>
                              </th>
                              <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">
                                <span className="inline sm:hidden">Co</span>
                                <span className="hidden sm:inline">Company</span>
                              </th>
                              <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Amount</th>
                              <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">RL</th>
                              <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Mgmt</th>
                              <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">LL</th>
                            </tr>
                            <tr className="bg-emerald-50 text-emerald-900">
                              <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2">Total</td>
                              <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2">Gross across selected period</td>
                              <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2"></td>
                              <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.amount)}</td>
                              <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.rl)}</td>
                              <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.mgmt)}</td>
                              <td className="border border-emerald-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.ll)}</td>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((p, idx) => {
                              const split = getPaymentSplit(p);
                              return (
                                <tr key={p._id || idx} className="odd:bg-white even:bg-emerald-50/40">
                                  <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top whitespace-normal break-words">
                                    <div className="block sm:hidden break-inside-avoid">{formatDateWithTime(p.date)}</div>
                                    <div className="hidden sm:block break-inside-avoid">{formatDateWithTime(p.date)}</div>
                                  </td>
                                  <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top break-words whitespace-normal leading-tight" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>
                                    {p.description || `Payment - ${p.unit?.label || ''}`}
                                  </td>
                                  <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top break-words whitespace-normal leading-tight" style={{ wordBreak: 'break-word' }}>
                                    {p._companyName || '-'}
                                  </td>
                                  <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.amount)}</td>
                                  <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.platform)}</td>
                                  <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.manager)}</td>
                                  <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.landlord)}</td>
                                </tr>
                              );
                            })}
                            {rows.length === 0 && (
                              <tr>
                                <td colSpan={7} className="text-center text-gray-500 px-4 py-6">No transactions found.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setShowManagementTransactionsModal(false)}
                  className="w-full sm:w-auto px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Landlord Transactions Modal (combined across all landlord properties) */}
        {showLandlordTransactionsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
            <div className="bg-white w-screen h-screen sm:max-w-6xl max-h-[90vh] sm:rounded-lg rounded-none flex flex-col overflow-x-hidden">
              <div className="bg-gradient-to-r from-green-600 to-green-700 p-4 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-1">Transactions</h3>
                    <div className="text-sm opacity-90">
                      <span className="font-medium">All Properties</span> • Payments across your properties
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowLandlordTransactionsModal(false);
                      setLdStartDate("");
                      setLdEndDate("");
                      setLandlordTransactions([]);
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Date Filter Section (collapsible) */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setLdFilterOpen(prev => !prev)}
                  className="w-full flex items-center justify-between text-gray-800 font-medium"
                >
                  <span>Filter</span>
                  <Icon name={ldFilterOpen ? 'chevron-up' : 'chevron-down'} size="1rem" className="text-gray-600 sm:w-5 sm:h-5" />
                </button>
                {ldFilterOpen && (
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-4 sm:items-end">
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={ldStartDate}
                        onChange={(e) => setLdStartDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="date"
                        value={ldEndDate}
                        onChange={(e) => setLdEndDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        onClick={fetchLandlordTransactions}
                        disabled={landlordTransactionsLoading}
                        className="flex-1 sm:flex-none bg-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                      >
                        {landlordTransactionsLoading ? 'Refreshing...' : 'Apply'}
                      </button>
                      <button
                        onClick={() => { setLdStartDate(""); setLdEndDate(""); fetchLandlordTransactions(); }}
                        className="flex-1 sm:flex-none bg-gray-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium text-sm"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="p-4 sm:p-6">
                  {landlordTransactionsLoading ? (
                    <div className="text-center py-8 text-gray-600">Loading transactions...</div>
                  ) : (() => {
                    // eslint-disable-next-line no-unused-vars
                    const uniqueCompanyNames = Array.from(new Set((landlordTransactions || []).map(r => r?._companyName).filter(Boolean)));
                    const rows = filterRowsByDateRange(
                      landlordTransactions || [],
                      ldStartDate,
                      ldEndDate
                    );
                    const paymentTotals = sumPaymentRows(rows);
                    const totals = {
                      amount: paymentTotals.amount,
                      rl: paymentTotals.platform,
                      mgmt: paymentTotals.manager,
                      ll: paymentTotals.landlord,
                    };

                    return (
                      <div>
                        <table className="w-full table-fixed border border-green-200 rounded-none sm:rounded-lg text-[9px] sm:text-xs leading-[1.05]">
                          <colgroup>
                            <col className="w-[12%] sm:w-[16%]" />
                            <col className="w-[28%] sm:w-auto" />
                            <col className="w-[12%] sm:w-[12%]" />
                            <col className="w-[12%] sm:w-[12%]" />
                            <col className="w-[12%] sm:w-[12%]" />
                            <col className="w-[12%] sm:w-[12%]" />
                          </colgroup>
                          <thead className="bg-green-600 text-white">
                            <tr className="text-center">
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Date</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Description</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Gross revenue</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Agent (%)</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Portal 1%</th>
                              <th className="border border-green-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">Net Revenue</th>
                            </tr>
                            <tr className="bg-green-50 text-green-900">
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2">Total</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2">Across selected period</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.amount)}</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.mgmt)}</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.rl)}</td>
                              <td className="border border-green-100 px-0.5 py-1 sm:px-2 sm:py-2 text-right">{formatMoney(totals.ll)}</td>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((p, idx) => {
                              const split = getPaymentSplit(p);
                              return (
                                <tr key={p._id || idx} className="odd:bg-white even:bg-green-50/40">
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top whitespace-normal break-words">
                                    <div className="block sm:hidden break-inside-avoid">{formatDateWithTime(p.date)}</div>
                                    <div className="hidden sm:block break-inside-avoid">{formatDateWithTime(p.date)}</div>
                                  </td>
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top break-words whitespace-normal leading-tight" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>
                                    {(() => {
                                      const baseDescription = p.description || `Payment - ${p.unit?.label || ''}`;
                                      const companyName = p._companyName;
                                      return companyName ? `${baseDescription} - ${companyName}` : baseDescription;
                                    })()}
                                  </td>
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.amount)}</td>
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.manager)}</td>
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.platform)}</td>
                                  <td className="border border-green-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(split.landlord)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="p-3 sm:p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setShowLandlordTransactionsModal(false)}
                  className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tenant History Modal */}
        {showTenantHistory && tenantHistory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 sm:p-6 text-white flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2">Tenant History</h3>
                    <div className="text-sm opacity-90">
                      <span className="font-medium">{tenantHistory.propertyName}</span> •
                      <span> Unit {tenantHistory.unitLabel}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowTenantHistory(false);
                      setTenantHistory(null);
                      setSelectedTenantForHistory(null);
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="p-4 sm:p-6">
                  {tenantHistory.tenantHistory.length > 0 ? (
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Select a tenant to view their statements:</h4>
                      {tenantHistory.tenantHistory.map((tenant, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h5 className="font-medium text-gray-900">{tenant.tenantName}</h5>
                                {tenant.isActive && (
                                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                                    Current Tenant
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <p className="break-words" style={{ overflowWrap: 'anywhere' }}><span className="font-medium">Email:</span> {tenant.tenantEmail}</p>
                                {tenant.tenantIdNumber && (
                                  <p><span className="font-medium">ID:</span> {tenant.tenantIdNumber}</p>
                                )}
                                {tenant.tenantPhone && (
                                  <p><span className="font-medium">Phone:</span> {tenant.tenantPhone}</p>
                                )}
                                <p><span className="font-medium">Role:</span> {tenant.role}</p>
                                <p><span className="font-medium">bal:</span> {formatMoney(Math.max(0, Number(tenant.currentBalance || 0)))}</p>
                                <p><span className="font-medium">Period:</span> {formatDateLong(tenant.startDate)}
                                  {tenant.endDate ? ` - ${formatDateLong(tenant.endDate)}` : ' - Present'}
                                </p>
                                <p><span className="font-medium">Duration:</span> {Math.floor(tenant.duration / 30)} months</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  if (!tenant?.tenantId) {
                                    alert("No tenant selected for statement.");
                                    return;
                                  }
                                  setShowTenantHistory(false);
                                  fetchTenantMonthlyStatus(selectedUnit, "", "", tenant.tenantId);
                                }}
                                disabled={tenantMonthlyStatusLoading}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                              >
                                {tenantMonthlyStatusLoading ? "Loading..." : "Invoices"}
                              </button>
                              {canManageProperty(selectedProperty || selectedUnit?.property) && (
                                <>
                                  <button
                                    onClick={() => {
                                      setShowTenantHistory(false);
                                      createInvoice(selectedUnit, tenant);
                                    }}
                                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                                  >
                                    Add Bill
                                  </button>
                                  <button
                                    onClick={() => {
                                      setShowTenantHistory(false);
                                      openWaiveBillModal(selectedUnit, tenant);
                                    }}
                                    className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
                                  >
                                    Waive Bill
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No tenant history found for this unit.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => {
                    setShowTenantHistory(false);
                    setTenantHistory(null);
                    setSelectedTenantForHistory(null);
                  }}
                  className="w-full sm:w-auto px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Vacate Unit Form Modal */}
        {showVacateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-red-600">⚠️ Vacate Unit</h2>
                  <button
                    onClick={() => setShowVacateForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>

                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h3 className="font-semibold text-red-800 mb-2">Unit: {selectedUnit?.label}</h3>
                  <p className="text-sm text-red-700 mb-2">
                    <strong>Property:</strong> {selectedUnit?.property?.name}
                  </p>
                  <p className="text-sm text-red-600">
                    <strong>Warning:</strong> This action will remove the current tenant from the unit and cannot be undone.
                  </p>
                </div>

                <form onSubmit={vacateUnit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vacate Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={vacateForm.vacateDate}
                      onChange={(e) => setVacateForm({ ...vacateForm, vacateDate: e.target.value })}
                      required
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type "VACATE" to confirm <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={vacateForm.confirmVacate}
                      onChange={(e) => setVacateForm({ ...vacateForm, confirmVacate: e.target.value })}
                      placeholder="Type VACATE to confirm"
                      required
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This confirmation is required to prevent accidental vacations
                    </p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowVacateForm(false)}
                      className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={vacateLoading || vacateForm.confirmVacate !== "VACATE"}
                      className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {vacateLoading ? 'Vacating...' : 'Vacate Unit'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        {/* Unit Details Modal */}
        {showUnitDetailsModal && selectedUnit && (() => {
          const currentTenants = getUnitCurrentTenants(selectedUnit);
          const landlord = selectedUnit?.landlord || selectedUnit?.property?.landlord || selectedProperty?.landlord;
          const moveInDate = getUnitMoveInDate(selectedUnit);
          const lateFeeAmount = Number(selectedUnit?.lateFee || 0);
          const unitStatus = selectedUnit?.status || "Unknown";
          const selectedUnitTenantId = getPreferredStatementTenantId(selectedUnit);
          const currentUserId = normalizeId(user?._id || user?.id);
          const isTenantViewingOwnUnit = Boolean(
            currentUserId &&
            selectedUnitTenantId &&
            currentUserId === selectedUnitTenantId
          );
          const hasOccupant =
            currentTenants.length > 0 ||
            Boolean(selectedUnit?.tenant) ||
            (Array.isArray(selectedUnit?.tenants) && selectedUnit.tenants.length > 0);
          const canUpdateWaterForUnit =
            canUpdateWaterReading(selectedProperty || selectedUnit?.property);
          const canCaptureWaterForUnit =
            canCaptureWaterPurchase(selectedProperty || selectedUnit?.property, {
              isTenantViewingOwnUnit,
            });
          const canOpenWaterStatement =
            hasOccupant &&
            (
              canManageProperty(selectedProperty || selectedUnit?.property) ||
              canUpdateWaterForUnit ||
              isTenantViewingOwnUnit
            );
          const actionButtonBase = "flex flex-col items-center justify-center gap-1 min-h-[60px] px-2.5 py-2 rounded-2xl border text-[10px] font-medium transition-colors sm:min-h-[72px] sm:gap-1.5 sm:px-3 sm:text-[11px]";
          const actionButtonMuted = `${actionButtonBase} border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-emerald-600`;
          const actionButtonWarning = `${actionButtonBase} border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800`;
          const actionButtonDanger = `${actionButtonBase} border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700`;
          const actionButtonPositive = `${actionButtonBase} border-green-300`;

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-[2px] sm:p-4">
              <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-[1rem] border border-white/80 bg-gradient-to-br from-white via-[#fefefe] to-[#f4faf5] shadow-[0_28px_90px_rgba(15,23,42,0.22)]">
                <div className="p-3.5 sm:p-6">
                  <div className="mb-4 flex items-start justify-between gap-2.5 sm:mb-5 sm:gap-3">
                    <div className="min-w-0">
                      <h2 className="text-[1.65rem] font-semibold leading-none tracking-tight text-slate-900 sm:text-[2rem]">
                        Unit {selectedUnit.label}
                      </h2>
                      <p className="mt-1.5 text-sm text-slate-500 sm:mt-2 sm:text-lg">
                        {selectedUnit?.property?.name || "Property not assigned"}
                      </p>
                    </div>

                    <div className="flex items-start gap-1.5 sm:gap-2">
                      <span className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium sm:px-4 sm:py-2 sm:text-sm ${getUnitStatusPillClasses(unitStatus)}`}>
                        {unitStatus}
                      </span>
                      <button
                        onClick={() => setShowUnitDetailsModal(false)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 sm:h-11 sm:w-11"
                        aria-label="Close unit details"
                      >
                        <Icon name="x-lg" size="1.25rem" className="sm:text-[1.5rem]" />
                      </button>
                    </div>
                  </div>

                  <div className="rounded-[1.75rem] border border-emerald-100 bg-gradient-to-br from-[#fbfefb] via-white to-[#f5fbf7] p-3.5 shadow-[0_12px_30px_rgba(15,23,42,0.06)] sm:p-5">
                    <div className="grid grid-cols-2 gap-x-3 gap-y-4 sm:gap-x-4 sm:gap-y-5">
                      <div className="border-r border-slate-200/80 pr-3 sm:pr-5">
                        <p className="text-xs font-medium text-slate-600 sm:text-sm">Rent</p>
                        <p className="mt-1 text-[1.05rem] font-semibold tracking-tight text-slate-900 sm:text-2xl">
                          {formatMoney(selectedUnit.rentAmount)}
                        </p>
                      </div>
                      <div className="pl-1 sm:pl-2">
                        <p className="text-xs font-medium text-slate-600 sm:text-sm">Type</p>
                        <p className="mt-1 text-[1.05rem] font-semibold tracking-tight text-slate-900 sm:text-2xl">
                          {selectedUnit.type || "Not set"}
                        </p>
                      </div>

                      <div className="border-r border-slate-200/80 pr-3 sm:pr-5">
                        <p className="text-xs font-medium text-slate-600 sm:text-sm">Deposit</p>
                        <p className="mt-1 text-[1.05rem] font-semibold tracking-tight text-slate-900 sm:text-2xl">
                          {formatMoney(selectedUnit.deposit)}
                        </p>
                      </div>
                      <div className="pl-1 sm:pl-2">
                        <p className="text-xs font-medium text-slate-600 sm:text-sm">Late Fee</p>
                        <p className="mt-1 text-[1.05rem] font-semibold tracking-tight text-slate-900 sm:text-2xl">
                          {formatMoney(selectedUnit.lateFee)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 border-t border-slate-200/80 pt-4 sm:mt-5 sm:pt-5">
                      <p className="text-xs font-medium text-slate-600 sm:text-sm">Move-in Date</p>
                      <div className="mt-2 flex items-center gap-2.5 text-slate-800 sm:gap-3">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 sm:h-10 sm:w-10">
                          <Icon name="calendar3" size="0.95rem" className="sm:text-[1.1rem]" />
                        </span>
                        <span className="text-base font-medium leading-tight text-slate-900 sm:text-xl">
                          {moveInDate ? formatDateLong(moveInDate) : "Not assigned"}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 border-t border-slate-200/80 pt-4 sm:mt-5 sm:pt-5">
                      <div className="flex items-start gap-2.5 sm:gap-3">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 sm:h-10 sm:w-10">
                          <Icon name="geo-alt" size="0.95rem" className="sm:text-[1.1rem]" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2.5 sm:gap-3">
                            <p className="text-xs font-medium text-slate-600 sm:text-sm">Address</p>
                            <div className="h-px flex-1 bg-slate-200/80" />
                          </div>
                          <p className="mt-2 text-sm leading-snug text-slate-900 sm:text-lg">
                            {selectedUnit?.property?.address || "Address not added"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 border-t border-slate-200/80 pt-4 sm:mt-5 sm:pt-5">
                      <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <div className="border-r border-slate-200/80 pr-3 sm:pr-5">
                          <p className="text-xs font-medium text-slate-600 sm:text-sm">Landlord</p>
                          {landlord ? (
                            <div className="mt-2.5 space-y-1 text-slate-700 sm:mt-3 sm:space-y-1.5">
                              <p className="text-[1rem] font-medium leading-tight text-slate-900 sm:text-2xl">
                                {landlord?.name || "No name"}
                              </p>

                              <p className="break-words text-xs sm:text-sm">
                                {getReadableUserEmail(landlord?.email)}
                              </p>

                              {landlord?.phone && (
                                <p className="text-xs sm:text-sm">
                                  {landlord.phone}
                                </p>
                              )}
                            </div>
                          ) : (
                            <p className="mt-2.5 text-sm italic text-slate-400 sm:mt-3 sm:text-base">Not assigned</p>
                          )}
                        </div>

                        <div className="pl-1 sm:pl-2">
                          <p className="text-xs font-medium text-slate-600 sm:text-sm">
                            {currentTenants.length > 1 ? "Tenants" : "Tenant"}
                          </p>
                          {currentTenants.length > 0 ? (
                            <div className="mt-2.5 space-y-3 sm:mt-3 sm:space-y-4">
                              {currentTenants.map((tenant) => (
                                <div key={tenant.key} className="space-y-1 text-slate-700 sm:space-y-1.5">
                                  <p className="text-[1rem] font-medium leading-tight text-slate-900 sm:text-2xl">
                                    {tenant.user?.name || tenant.user?.email?.split("@")[0] || "Unknown"}
                                  </p>
                                  <p className="break-words text-xs sm:text-base">
                                    {getReadableUserEmail(tenant.user?.email)}
                                  </p>
                                  {tenant.user?.phone && <p className="text-xs sm:text-base">{tenant.user.phone}</p>}
                                  {tenant.user?.idNumber && (
                                    <p className="text-xs font-medium sm:text-base">ID: {tenant.user.idNumber}</p>
                                  )}
                                  {canManageProperty(selectedProperty || selectedUnit?.property) && currentTenants.length > 1 && (
                                    <button
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            `Are you sure you want to remove ${tenant.role}: ${tenant.user?.email || "Unknown"}?`
                                          )
                                        ) {
                                          unassignTenant(selectedUnit._id, tenant.user._id);
                                          setShowUnitDetailsModal(false);
                                        }
                                      }}
                                      className="mt-2 inline-flex items-center rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-100"
                                      title={`Remove ${tenant.role}`}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-2.5 text-sm italic text-slate-400 sm:mt-3 sm:text-base">No tenants assigned</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={`mt-4 flex items-center gap-2.5 rounded-2xl border px-3.5 py-2.5 sm:mt-5 sm:gap-3 sm:px-4 sm:py-3 ${
                      lateFeeAmount > 0
                        ? "border-amber-200 bg-amber-50 text-amber-900"
                        : "border-slate-200 bg-white/80 text-slate-600"
                    }`}>
                      <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full sm:h-9 sm:w-9 ${
                        lateFeeAmount > 0 ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500"
                      }`}>
                        <Icon name={lateFeeAmount > 0 ? "exclamation-triangle-fill" : "info-circle"} size="0.9rem" className="sm:text-[1rem]" />
                      </span>
                      <p className="text-xs font-medium sm:text-sm">
                        {lateFeeAmount > 0 ? "Late fee applies after due date" : "No late fee configured for this unit"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3.5 grid grid-cols-3 gap-2 sm:mt-4 sm:grid-cols-6">
                    {/* Only show management actions when user has manage access */}
                    {canManageProperty(selectedProperty || selectedUnit?.property) && (
                      <>
                        <button
                          onClick={() => {
                            setShowUnitDetailsModal(false);
                            openUpdateUnitForm(selectedUnit);
                          }}
                          className={actionButtonMuted}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Edit</span>
                        </button>
                        {!selectedUnit.tenant && (!selectedUnit.tenants || selectedUnit.tenants.length === 0) && (
                          (() => {
                            const missingAssignmentFields = getMissingAssignmentFields(selectedUnit);
                            const isUnitReady = missingAssignmentFields.length === 0;

                            if (!isUnitReady) {
                              return (
                                <button
                                  onClick={() => {
                                    setAssignBlockedMissingFields(missingAssignmentFields);
                                    setShowAssignBlockedModal(true);
                                  }}
                                  className={actionButtonWarning}
                                  title="Update unit rent amount and type before assigning a tenant"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                  </svg>
                                  <span>Assign</span>
                                </button>
                              );
                            }

                            return (
                              <button
                                data-tutorial="assign-btn"
                                onClick={() => {
                                  setShowUnitDetailsModal(false);
                                  setAssignmentType("tenant");
                                  resetAssignFormState();
                                  setShowAssignForm(true);
                                }}
                                className={actionButtonMuted}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                </svg>
                                <span>Assign</span>
                              </button>
                            );
                          })()
                        )}
                        {canOpenWaterStatement && (
                          <button
                            onClick={() => {
                              openWaterMeterModal(selectedUnit);
                            }}
                            className={actionButtonMuted}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3c3 4 6 7.2 6 10.2A6 6 0 1112 3z" />
                            </svg>
                            <span>{canManageProperty(selectedProperty || selectedUnit?.property) ? "Utilities" : "Utility Statement"}</span>
                          </button>
                        )}
                        {(selectedUnit.tenants && selectedUnit.tenants.length > 0) && (
                          <button
                            onClick={() => {
                              setShowUnitDetailsModal(false);
                              createInvoice(selectedUnit, null);
                            }}
                            className={actionButtonMuted}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            <span>Add to Rent</span>
                          </button>
                        )}
                        {(selectedUnit.tenants && selectedUnit.tenants.length > 0) && (
                          <button
                            onClick={() => {
                              setShowUnitDetailsModal(false);
                              openWaiveBillModal(selectedUnit, null);
                            }}
                            className={actionButtonMuted}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            <span>Waive from Rent</span>
                          </button>
                        )}
                        {(selectedUnit.tenants && selectedUnit.tenants.length > 0) && (
                          <button
                            onClick={() => {
                              setShowUnitDetailsModal(false);
                              setVacateForm({ vacateDate: "", confirmVacate: "" });
                              setShowVacateForm(true);
                            }}
                            className={actionButtonDanger}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            <span>Vacate</span>
                          </button>
                        )}
                      </>
                    )}

                    {/* Read-only actions available to both managers and landlords */}
                    {hasOccupant && (
                      <button
                        onClick={() => {
                          const tenantIdForStatement = getPreferredStatementTenantId(selectedUnit);
                          setShowUnitDetailsModal(false);
                          fetchTransactionStatement(selectedUnit, "", "", tenantIdForStatement);
                          if (tenantIdForStatement) {
                            fetchTenantMonthlyStatus(selectedUnit, "", "", tenantIdForStatement, false);
                          }
                        }}
                        disabled={statementLoading || tenantMonthlyStatusLoading}
                        className={`${actionButtonPositive} disabled:opacity-40 disabled:cursor-not-allowed`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        <span>{(statementLoading || tenantMonthlyStatusLoading) ? '...' : 'Statements'}</span>
                      </button>
                    )}
                    {(isManager(user) || isLandlord(user)) && (
                      <button
                        onClick={() => {
                          setShowUnitDetailsModal(false);
                          fetchTenantHistory(selectedUnit);
                        }}
                        disabled={statementLoading}
                        className={`${actionButtonMuted} disabled:opacity-40 disabled:cursor-not-allowed`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{statementLoading ? '...' : 'History'}</span>
                      </button>
                    )}
                    {/* Lease Agreement button - available to both managers and landlords */}
                    {hasOccupant && (
                      <button
                        onClick={() => {
                          setShowUnitDetailsModal(false);
                          setShowLeaseAgreementModal(true);
                        }}
                        className={actionButtonMuted}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Lease</span>
                      </button>
                    )}
                    {/* Close button - always last */}
                    <button
                      onClick={() => setShowUnitDetailsModal(false)}
                      className={`${actionButtonBase} border-slate-200 bg-white text-slate-400 hover:bg-slate-50 hover:text-slate-600`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>Close</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </main>
      {(() => {
        const pendingUnit = utPayConfirm?.unit;
        if (!pendingUnit) return null;
        const pendingUnitId = normalizeId(pendingUnit?._id);
        const pendingBalance = managerUnitUtilityBalances[pendingUnitId];
        const pendingTenant = getUnitCurrentTenants(pendingUnit)[0]?.user;
        const details = [{ label: "Unit", value: pendingUnit?.label || "Unit" }];
        if (pendingTenant?.name) details.push({ label: "Tenant", value: pendingTenant.name });
        if (typeof pendingBalance === "number") {
          details.push({ label: "Utility owed", value: formatMoney(pendingBalance) });
        }

        return (
          <ConfirmModal
            open
            title="Trigger UT Pay?"
            message="The tenant's utility wallet will be used to pay their outstanding utility bills. This cannot be undone."
            details={details}
            icon="lightning-charge-fill"
            tone="teal"
            confirmLabel="Yes, trigger pay"
            busy={Boolean(
              utilitySettlementSubmitting || (pendingUnitId && utilitySettlementLoadingByUnit[pendingUnitId])
            )}
            onCancel={() => setUtPayConfirm(null)}
            onConfirm={async () => {
              const { unit, options } = utPayConfirm;
              setUtPayConfirm(null);
              await triggerUtilitySettlementForUnit(unit, options);
            }}
          />
        );
      })()}
      <ConfirmModal
        open={Boolean(meterReplacementConfirm)}
        title="Replace this water meter?"
        message="The old meter will be closed. Any paid units will move to the new reading. Any final overuse will become a water bill."
        details={meterReplacementConfirm ? [
          { label: "Old final reading", value: Number(meterReplacementConfirm.oldFinal).toFixed(4) },
          { label: "New current reading", value: Number(meterReplacementConfirm.newCurrent).toFixed(4) },
          { label: "Tenant", value: waterMeterState?.tenant?.name || "Current tenant" }
        ] : []}
        icon="arrow-repeat"
        tone="amber"
        confirmLabel="Replace meter"
        busy={formLoading}
        onCancel={() => setMeterReplacementConfirm(null)}
        onConfirm={submitMeterReplacement}
      />
      <ConfirmModal
        open={Boolean(meterCorrectionConfirm)}
        title="Save this meter correction?"
        message="This changes the tenant's displayed water units and value. Existing payments and water debt invoices will stay unchanged."
        details={meterCorrectionConfirm ? [
          { label: "Meter reading", value: `${meterCorrectionConfirm.savedCurrent.toFixed(4)} → ${meterCorrectionConfirm.currentReading.toFixed(4)}` },
          { label: "Cut-off reading", value: `${meterCorrectionConfirm.savedCutOff.toFixed(4)} → ${meterCorrectionConfirm.cutOffReading.toFixed(4)}` },
          { label: "Remaining units", value: `${meterCorrectionConfirm.beforeUnits.toFixed(4)} → ${meterCorrectionConfirm.afterUnits.toFixed(4)}` },
          { label: "Remaining value", value: `${formatMoney(meterCorrectionConfirm.beforeValue)} → ${formatMoney(meterCorrectionConfirm.afterValue)}` }
        ] : []}
        icon="pencil-square"
        tone="amber"
        confirmLabel="Save correction"
        busy={formLoading}
        onCancel={() => setMeterCorrectionConfirm(null)}
        onConfirm={submitMeterCorrection}
      />
      <AsyncActionModal
        open={!!(showPaymentModal && selectedPaymentUnit && (isProcessingPayment || paymentResult))}
        status={isProcessingPayment ? "loading" : paymentResult?.success ? "success" : "error"}
        title={isProcessingPayment ? "Processing Payment" : paymentResult?.success ? "Payment Successful" : "Payment Failed"}
        statusLabel={isProcessingPayment ? "Processing Payment" : paymentResult?.success ? "Payment Successful" : "Payment Failed"}
        message={
          isProcessingPayment
            ? "Please wait. Do not close this window."
            : paymentResult?.success
              ? "Your rent payment has been processed."
              : paymentResult?.error || "Payment failed. Please try again."
        }
        details={
          paymentResult?.success
            ? [
                { label: "Amount paid", value: formatMoney(paymentResult.amount) },
                { label: "New wallet balance", value: formatMoney(paymentResult.newBalance) },
              ]
            : paymentResult?.shortfall
              ? [
                  { label: "Current balance", value: formatMoney(paymentResult.currentBalance) },
                  { label: "Required", value: formatMoney(paymentResult.requiredAmount) },
                  { label: "Shortfall", value: formatMoney(paymentResult.shortfall), tone: "error" },
                ]
              : []
        }
        onClose={closePaymentModal}
        primaryLabel={paymentResult?.success ? "Done" : paymentResult && !paymentResult.success ? "Try Again" : undefined}
        onPrimary={paymentResult?.success ? closePaymentModal : paymentResult && !paymentResult.success ? () => setPaymentResult(null) : undefined}
        secondaryLabel={paymentResult && !paymentResult.success ? "Cancel" : undefined}
        onSecondary={closePaymentModal}
      />

      {/* Payment Form Modal */}
      {showPaymentModal && selectedPaymentUnit && !isProcessingPayment && !paymentResult && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            {/* Header */}
            <div className="flex justify-between items-center px-6 pt-6 pb-4 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-800">Pay Rent</h2>
              <button
                onClick={closePaymentModal}
                disabled={isProcessingPayment}
                className={`w-8 h-8 flex items-center justify-center rounded-full text-lg font-bold transition-colors ${
                  isProcessingPayment
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                }`}
              >
                x
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
                <div className="bg-green-50 p-4 rounded-xl">
                  <h3 className="font-semibold text-green-800">{selectedPaymentUnit.property?.name}</h3>
                  <p className="text-green-600 text-sm">Unit: {selectedPaymentUnit.label}</p>
                  <p className="text-green-600 text-sm">Balance due: {selectedPaymentUnit.currentBalance ? formatMoney(selectedPaymentUnit.currentBalance) : '0.00'}</p>
                  {wallet && (
                    <p className="text-green-700 font-semibold text-sm mt-1">Your wallet: {formatMoney(wallet.balance)}</p>
                  )}
                </div>

                {(() => {
                  const enteredAmount = parseFloat(paymentAmount);
                  const overLimit = wallet && enteredAmount > 0 && enteredAmount > wallet.balance;
                  return (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                        <input
                          type="number"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          placeholder="Enter amount to pay"
                          className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 ${
                            overLimit
                              ? "border-red-400 focus:ring-red-400 bg-red-50"
                              : "border-gray-300 focus:ring-green-500"
                          }`}
                          min="0"
                          step="0.01"
                        />
                        {overLimit ? (
                          <p className="text-xs text-red-500 mt-1 font-medium">
                            Exceeds wallet balance by {formatMoney(enteredAmount - wallet.balance)} - top up your wallet first
                          </p>
                        ) : (
                          <p className="text-xs text-gray-400 mt-1">You can pay any amount - it does not have to be the full balance</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                        <input
                          type="text"
                          value={paymentDescription}
                          onChange={(e) => setPaymentDescription(e.target.value)}
                          placeholder="e.g. June rent, partial payment..."
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </div>

                      <div className="pt-1 pb-1">
                        <button
                          onClick={processPayment}
                          disabled={overLimit}
                          className={`w-full py-3 px-4 rounded-lg font-semibold text-base transition-colors ${
                            overLimit
                              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                              : "bg-green-600 text-white hover:bg-green-700"
                          }`}
                        >
                          Pay {enteredAmount > 0 ? formatMoney(enteredAmount) : "-"}
                        </button>
                      </div>
                    </>
                  );
                })()}
            </div>
          </div>
        </div>
      )}
      {/* Invoice Modal */}
      {showInvoiceModal && selectedInvoiceUnit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Add Bill</h2>
                <button
                  onClick={() => {
                    setShowInvoiceModal(false);
                    setSelectedInvoiceUnit(null);
                    setSelectedInvoiceTenant(null);
                    setInvoiceAmount("");
                    setInvoiceDescription("");
                    setInvoiceDueDate("");
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-purple-800">{selectedInvoiceUnit.property?.name}</h3>
                  <p className="text-purple-600">Unit: {selectedInvoiceUnit.label}</p>
                  <p className="text-purple-600">
                    Tenant: {selectedInvoiceTenant?.tenantName || selectedInvoiceUnit.tenant?.name || 'No tenant assigned'}
                    {selectedInvoiceTenant && !selectedInvoiceTenant.isActive ? ' (Former Tenant)' : ''}
                  </p>
                  <p className="text-purple-600">Monthly Rent: {formatMoney(selectedInvoiceUnit.rentAmount)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Amount
                  </label>
                  <input
                    type="number"
                    value={invoiceAmount}
                    onChange={(e) => setInvoiceAmount(e.target.value)}
                    placeholder="Enter invoice amount"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={invoiceDescription}
                    onChange={(e) => setInvoiceDescription(e.target.value)}
                    placeholder="Enter invoice description (e.g., Monthly rent for January 2025)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={invoiceDueDate}
                    onChange={(e) => setInvoiceDueDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowInvoiceModal(false);
                      setSelectedInvoiceUnit(null);
                      setSelectedInvoiceTenant(null);
                      setInvoiceAmount("");
                      setInvoiceDescription("");
                      setInvoiceDueDate("");
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={processInvoiceCreation}
                    disabled={!invoiceAmount || !invoiceDescription || formLoading}
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {formLoading ? 'Creating...' : 'Add Bill'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {showPropertyWaterStatementsModal && selectedProperty && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white w-full max-w-5xl max-h-[94vh] rounded-[24px] sm:rounded-[28px] overflow-hidden shadow-2xl flex flex-col">
              <div className="bg-gradient-to-r from-cyan-600 to-sky-700 px-4 py-4 sm:px-6 sm:py-6 text-white flex-shrink-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-lg sm:text-2xl font-bold mb-1">Property UTILITY-Bills Statement</h3>
                    <div className="text-xs sm:text-sm opacity-90 leading-relaxed">
                      <span className="font-medium">{selectedProperty.name}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowPropertyWaterStatementsModal(false);
                      setWaterPropertyTransactions([]);
                      setWaterPropertyLatestByUnit([]);
                      setWaterPropertySummary(null);
                      setPropertyWaterStartDate("");
                      setPropertyWaterEndDate("");
                      setPropertyWaterSearch("");
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-xl p-2 transition-colors flex-shrink-0"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-3 py-4 sm:px-6 sm:py-6 space-y-4">
                {/* Manager shortcut into the Utilities module Billing tab for
                    one-off bills. Recurring billing stays property-scoped in the
                    inline panel below. */}
                {canManageProperty(selectedProperty) &&
                  (selectedProperty.managementCompany?._id || selectedProperty.managementCompany) && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          navigate("/app/ut-bills", {
                            state: {
                              tab: "billing",
                              providerId: `mc:${selectedProperty.managementCompany?._id || selectedProperty.managementCompany}`,
                              openModal: "bill",
                              propertyId: selectedProperty._id,
                            },
                          })
                        }
                        className="inline-flex items-center gap-1.5 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 text-xs font-semibold text-teal-700 transition hover:bg-teal-100"
                      >
                        <Icon name="receipt" size="0.8rem" />
                        Issue bill
                      </button>
                    </div>
                  )}

                {/* Per-property monthly auto-invoicing (managers only) */}
                {canManageProperty(selectedProperty) &&
                  (selectedProperty.managementCompany?._id || selectedProperty.managementCompany) && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm sm:p-4">
                      <PropertyAutoInvoicing
                        providerRef={`mc:${selectedProperty.managementCompany?._id || selectedProperty.managementCompany}`}
                        property={{ _id: selectedProperty._id, name: selectedProperty.name }}
                      />
                    </div>
                  )}

                {propertyWaterLoading ? (
                  <div className="flex items-center justify-center py-10 text-gray-600">Loading UTILITY-Bills statement...</div>
                ) : (
                  <>
                    {(() => {
                      const dateFilteredWaterRows = filterRowsByDateRange(
                        waterPropertyTransactions,
                        propertyWaterStartDate,
                        propertyWaterEndDate,
                        (row) => row?.readingDate
                      );
                      const normalizedWaterSearch = propertyWaterSearch.trim().toLowerCase();
                      const filteredWaterRows = dateFilteredWaterRows.filter((row) => {
                        const tenantName = String(row?.tenant?.name || "").toLowerCase();
                        const tenantIdNumber = String(row?.tenant?.idNumber || "").toLowerCase();
                        const unitLabel = String(row?.unit?.label || "").toLowerCase();
                        return (
                          !normalizedWaterSearch ||
                          tenantName.includes(normalizedWaterSearch) ||
                          tenantIdNumber.includes(normalizedWaterSearch) ||
                          unitLabel.includes(normalizedWaterSearch)
                        );
                      });
                      const filteredWaterSummary = {
                        totalTransactions: filteredWaterRows.length,
                        totalUnitsPurchased: filteredWaterRows.reduce((sum, row) => sum + Number(row?.allowedUnits || 0), 0),
                        totalAmountPaid: filteredWaterRows.reduce((sum, row) => sum + Number(row?.amountPaid || 0), 0),
                      };
                      return (
                        <>
                          <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4">
                            <div className="grid grid-cols-2 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-end">
                              <div className="min-w-0">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                <input
                                  type="date"
                                  value={propertyWaterStartDate}
                                  onChange={(e) => setPropertyWaterStartDate(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                              </div>
                              <div className="min-w-0">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">End Date</label>
                                <input
                                  type="date"
                                  value={propertyWaterEndDate}
                                  onChange={(e) => setPropertyWaterEndDate(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                              </div>
                            </div>
                            <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                              <div className="min-w-0">
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Search Tenant or Unit</label>
                                <input
                                  type="text"
                                  value={propertyWaterSearch}
                                  onChange={(e) => setPropertyWaterSearch(e.target.value)}
                                  placeholder="Search by tenant name, ID, or unit"
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                              </div>
                              <button
                                onClick={() => {
                                  const { start, end } = getCurrentMonthRange();
                                  setPropertyWaterStartDate(start);
                                  setPropertyWaterEndDate(end);
                                  setPropertyWaterSearch("");
                                }}
                                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium lg:w-auto"
                              >
                                Clear
                              </button>
                            </div>
                          </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg bg-gray-50 p-3">
                        <p className="text-xs text-gray-500">Units Purchased</p>
                        <p className="text-sm font-semibold text-gray-900">{Number(filteredWaterSummary.totalUnitsPurchased || 0).toFixed(4)}</p>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-3">
                        <p className="text-xs text-gray-500">Top Ups</p>
                        <p className="text-sm font-semibold text-gray-900">{formatMoney(filteredWaterSummary.totalAmountPaid || 0)}</p>
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-3 sm:p-4">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">Water Transactions</p>
                          <p className="text-xs text-gray-500">All recorded prepaid water purchases and reading updates for this property</p>
                        </div>
                      </div>
                      {filteredWaterRows.length === 0 ? (
                        <div className="py-8 text-sm text-gray-500">
                          {dateFilteredWaterRows.length === 0
                            ? "No prepaid water entries recorded for this property yet."
                            : "No tenants match the selected water filters."}
                        </div>
                      ) : (
                        <div className="max-h-80 overflow-auto rounded-lg border border-gray-100">
                          <div className="origin-top-left scale-[0.82] sm:scale-100">
                          <table className="min-w-full text-[10px] sm:text-sm">
                            <thead className="bg-gray-50 text-left text-[9px] sm:text-xs uppercase tracking-wide text-gray-500">
                              <tr>
                                <th className="px-2 py-2 sm:px-3">Date</th>
                                <th className="px-2 py-2 sm:px-3">Tenant / Unit</th>
                                <th className="px-2 py-2 sm:px-3">Type</th>
                                <th className="px-2 py-2 sm:px-3 text-right">Top Up</th>
                                <th className="px-2 py-2 sm:px-3 text-right">Units</th>
                                <th className="px-2 py-2 sm:px-3 text-right">Cut Off</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {filteredWaterRows.map((row) => (
                                <tr key={row._id}>
                                  <td className="px-2 py-2 text-gray-700 sm:px-3">{formatDateLong(row.readingDate)}</td>
                                  <td className="px-2 py-2 sm:px-3">
                                    <div className="font-medium text-gray-900">{row?.tenant?.name || "Unknown tenant"}</div>
                                    <div className="text-xs text-gray-500">Unit {row?.unit?.label || "-"}{row?.tenant?.idNumber ? ` • ${row.tenant.idNumber}` : ""}</div>
                                  </td>
                                  <td className="px-2 py-2 sm:px-3">
                                    <span className={`inline-flex rounded-full px-2 py-1 text-[10px] font-semibold ${
                                      row?.entryType === "meter_replacement"
                                        ? "bg-amber-100 text-amber-800"
                                        : row?.entryType === "meter_correction"
                                          ? "bg-violet-100 text-violet-800"
                                        : row?.entryType === "reading_update"
                                          ? "bg-slate-100 text-slate-700"
                                          : "bg-cyan-100 text-cyan-700"
                                    }`}>
                                      {row?.entryType === "meter_replacement"
                                        ? "Meter Replaced"
                                        : row?.entryType === "meter_correction"
                                          ? "Meter Corrected"
                                        : row?.entryType === "reading_update" ? "Reading Update" : "Purchase"}
                                    </span>
                                  </td>
                                  <td className="px-2 py-2 text-right font-medium text-gray-900 sm:px-3">{formatMoney(row.amountPaid || 0)}</td>
                                  <td className="px-2 py-2 text-right text-gray-700 sm:px-3">{Number(row.allowedUnits || 0).toFixed(4)}</td>
                                  <td className="px-2 py-2 text-right sm:px-3">
                                    <div className="font-medium text-gray-900">{Number(row.cutOffReading || 0).toFixed(4)}</div>
                                    <div className="text-xs text-gray-500">Remaining {Number(row.remainingUnits || 0).toFixed(4)}</div>
                                    {row.isDueCutOff && (
                                      <div className="text-xs text-red-600">Cut off due</div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      )}
                    </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      {showWaterMeterModal && selectedWaterUnit && (() => {
        const preview = getWaterPreview();
        const meterOutstandingDebtAmount = Number(
          waterMeterState?.meterState?.outstandingDebtAmount || 0
        );
        const waterPurchaseValidation = getWaterPurchaseValidation({
          amountPaid: waterMeterForm.amountPaid,
          outstandingDebtAmount: meterOutstandingDebtAmount,
          rate: preview.rate,
        });
        const lastCutOff = Number(waterMeterState?.meterState?.cutOffReading || 0);
        const actualStoredReading = Number(waterMeterState?.meterState?.currentReading || 0);
        const propertyName = waterMeterState?.unit?.property?.name || selectedWaterUnit?.property?.name || "Property";
        const utilityUnitProperty = waterMeterState?.unit?.property || selectedWaterUnit?.property || null;
        const utilityUnitPropertyId = normalizeId(utilityUnitProperty?._id || utilityUnitProperty);
        const selectedPropertyId = normalizeId(selectedProperty?._id || selectedProperty);
        const modalProperty = utilityUnitPropertyId && selectedPropertyId && utilityUnitPropertyId === selectedPropertyId
          ? {
              ...(utilityUnitProperty && typeof utilityUnitProperty === "object" ? utilityUnitProperty : {}),
              ...(selectedProperty && typeof selectedProperty === "object" ? selectedProperty : {})
            }
          : (utilityUnitProperty || selectedProperty || null);
        const modalCompany = modalProperty?.managementCompany;
        const modalCompanyId = modalCompany?._id || modalCompany;
        const canManageUtilityModalProperty = Boolean(modalProperty) && canManageProperty(modalProperty);
        const tenantName = waterMeterState?.tenant?.name || selectedWaterUnit?.tenant?.name || "No tenant assigned";
        const canUpdateSelectedWaterUnit = canUpdateWaterReading(
          modalProperty || waterMeterState?.unit?.property || selectedWaterUnit?.property
        );
        const canCaptureSelectedWaterUnit = canCaptureWaterPurchase(
          modalProperty || waterMeterState?.unit?.property || selectedWaterUnit?.property,
          {
            isTenantViewingOwnUnit: Boolean(
              normalizeId(user?._id || user?.id) &&
              normalizeId(waterMeterState?.tenant?._id || selectedWaterUnit?.tenant?._id || selectedWaterUnit?.tenant) &&
              normalizeId(user?._id || user?.id) ===
                normalizeId(waterMeterState?.tenant?._id || selectedWaterUnit?.tenant?._id || selectedWaterUnit?.tenant)
            ),
          }
        );
        const canViewPurchaseCard = canCaptureSelectedWaterUnit || canUpdateSelectedWaterUnit || user?.role === "T" || hasAnyTenantAccess(user);
        const hasLatestReading = waterMeterState?.meterState != null;
        const normalizedEnteredReading = buildWaterReadingValue(
          waterMeterForm.currentReadingWhole,
          waterMeterForm.currentReadingDecimal
        );
        const enteredReadingValue = Number(normalizedEnteredReading);
        const hasEnteredReading = normalizedEnteredReading !== "";
        const isReadingUnchanged = hasLatestReading
          && Number.isFinite(enteredReadingValue)
          && Number(enteredReadingValue.toFixed(3)) === Number(Number(actualStoredReading || 0).toFixed(3));
        const displayReading = hasEnteredReading && Number.isFinite(enteredReadingValue)
          ? enteredReadingValue
          : actualStoredReading;
        const hasExistingCutOff = lastCutOff > 0;
        const computedRemainingWaterUnits = hasLatestReading
          ? Number((lastCutOff - displayReading).toFixed(4))
          : getWaterRemainingUnits();
        const remainingWaterUnits = Number.isFinite(computedRemainingWaterUnits)
          ? computedRemainingWaterUnits
          : 0;
        const computedRemainingWaterBalance = Number((remainingWaterUnits * Number(preview.rate || 0)).toFixed(2));
        const remainingWaterBalance = Number.isFinite(computedRemainingWaterBalance)
          ? computedRemainingWaterBalance
          : getWaterRemainingBalance();
        const utilityDebtSummary = getUtilityDebtSummary(
          utilityStatement?.summary || {},
          utilityStatement?.invoices || []
        );
        const billedWaterDebtAmount = utilityDebtSummary.amountDue;
        const extraBilledWaterAmount =
          remainingWaterBalance < 0 && billedWaterDebtAmount > Math.abs(remainingWaterBalance || 0)
            ? Number((billedWaterDebtAmount - Math.abs(remainingWaterBalance || 0)).toFixed(2))
            : 0;
        const displayedWaterBalanceAmount =
          remainingWaterBalance < 0 && billedWaterDebtAmount > 0
            ? billedWaterDebtAmount
            : Math.abs(remainingWaterBalance || 0);
        const dueCutOff = hasExistingCutOff && displayReading >= lastCutOff;
        const accountStatus = dueCutOff
          ? "Cut-off due"
          : hasLatestReading
            ? "Prepaid water active"
            : "Awaiting first reading";
        const alertTitle = dueCutOff
          ? "Water credit is exhausted."
          : remainingWaterUnits > 0
            ? "You have enough credit."
            : "";
        const alertMessage = dueCutOff
          ? "Add a new water payment and update the reading to extend the next cut-off point."
          : remainingWaterUnits > 0
            ? "Keep monitoring your usage and save the latest meter reading when it changes."
            : "";
        const statusToneClasses = dueCutOff
          ? "border-rose-200 bg-rose-50 text-rose-700"
          : "border-emerald-200 bg-emerald-50 text-emerald-700";
        // Only All + Water tabs. Trash and other utility bills are billType-agnostic
        // (a "Trash" category is stored as billType 'other'), so a dedicated Trash
        // tab misleads and an Other tab is noise — everything non-water lives under All.
        const utilityTypeOptions = [
          { value: "all", label: "All" },
          { value: "water", label: "Water" }
        ];
        const selectedUtilityLabel = utilityTypeOptions.find((option) => option.value === selectedUtilityType)?.label || "Water";
        const utilityStatementRows = getVisibleUtilitySettlementRows(utilityStatement?.rows);
        const isUnifiedUtilityTab = selectedUtilityType === "all";
        const selectedUtilityHeading = isUnifiedUtilityTab ? "All Utilities" : selectedUtilityLabel;
        const filteredUtilityStatementRows = utilityStatementRows.filter((row) => {
          if (isUnifiedUtilityTab) return true;

          if (selectedUtilityType === "water") {
            return (
              String(row?.utilityType || "").toLowerCase() === "water" ||
              row?.source === "water_meter" ||
              row?.type === "water_top_up" ||
              row?.type === "reading_update"
            );
          }

          if (row?.type === "deposit" || row?.source === "wallet") {
            return false;
          }

          return String(row?.utilityType || "").toLowerCase() === selectedUtilityType;
        });
        const utilityStatementRowsWithRunningBalance = computeUtilityStatementRunningBalances(
          filteredUtilityStatementRows,
          utilityDebtSummary.netDebtBalance
        );
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-2 backdrop-blur-[2px] sm:p-3">
            <div className="w-full max-w-5xl overflow-hidden rounded-[24px] border border-emerald-100 bg-[#f7faf7] shadow-[0_24px_70px_rgba(15,23,42,0.24)]">
              <div className="max-h-[92vh] overflow-y-auto">
                <div className="border-b border-emerald-100 bg-white px-4 py-3 sm:px-5 sm:py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-600">Utilities</p>
                      <h2 className="text-lg font-bold text-slate-900 sm:text-xl">{selectedUtilityHeading} Account</h2>
                      <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">{propertyName} - Unit {selectedWaterUnit.label}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 sm:text-[11px]">
                          Tenant: {tenantName}
                        </span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold sm:text-[11px] ${statusToneClasses}`}>
                          {accountStatus}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={closeWaterMeterModal}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-100"
                      aria-label="Close prepaid water meter modal"
                    >
                      <span>Close</span>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="space-y-4 px-3 py-3 sm:px-5 sm:py-4">
                  <div className="rounded-[16px] border border-slate-200 bg-white p-1.5 shadow-sm sm:p-2">
                    <div className="grid grid-cols-4 gap-1">
                      {utilityTypeOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setSelectedUtilityType(option.value)}
                          className={`rounded-lg px-1.5 py-1.5 text-[10px] font-semibold transition sm:rounded-xl sm:px-3 sm:py-2 sm:text-sm ${
                            selectedUtilityType === option.value
                              ? "bg-emerald-600 text-white shadow-sm"
                              : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {selectedUtilityType === "water" && (alertTitle || alertMessage) && (
                    <div className={`rounded-[20px] border px-3 py-3 shadow-sm ${statusToneClasses}`}>
                      <div className="flex items-start gap-2.5">
                        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/80">
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {dueCutOff ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M10.29 3.86l-7.5 13A1 1 0 003.67 18h16.66a1 1 0 00.88-1.5l-7.5-13a1 1 0 00-1.74 0z" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            )}
                          </svg>
                        </div>
                        <div>
                          {alertTitle && (
                            <p className="text-sm font-bold sm:text-base">{alertTitle}</p>
                          )}
                          {alertMessage && (
                            <p className="mt-1 text-[11px] opacity-90 sm:text-xs">{alertMessage}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedUtilityType === "water" ? (
                  <>
                  <div className="rounded-[16px] border border-emerald-100 bg-white p-2 shadow-sm">
                    <div className="mb-1.5 flex items-center gap-1.5">
                      <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3C9 7.1 6 10.3 6 13.5A6 6 0 0018 13.5C18 10.3 15 7.1 12 3z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-slate-900">Meter Summary</p>
                        <p className="text-[9px] text-slate-500">Current reading, cut-off point, and remaining prepaid balance.</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      <div className="rounded-xl bg-slate-50 px-2 py-1.5">
                        <p className="text-[9px] font-semibold uppercase tracking-[0.06em] text-slate-500">Current Reading</p>
                        <p className="mt-0.5 text-[11px] font-bold text-slate-900 sm:text-xs">{Number(actualStoredReading || 0).toFixed(4)} m3</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 px-2 py-1.5">
                        <p className="text-[9px] font-semibold uppercase tracking-[0.06em] text-slate-500">Cut-Off Reading</p>
                        <p className={`mt-0.5 text-[11px] font-bold sm:text-xs ${dueCutOff ? "text-rose-600" : "text-amber-600"}`}>{Number(lastCutOff || 0).toFixed(4)} m3</p>
                      </div>
                      <div className="rounded-xl bg-slate-50 px-2 py-1.5">
                        <p className="text-[9px] font-semibold uppercase tracking-[0.06em] text-slate-500">
                          {remainingWaterUnits < 0 || remainingWaterBalance < 0 ? "Overused" : "Remaining Balance"}
                        </p>
                        <p className={`mt-0.5 text-[11px] font-bold sm:text-xs ${remainingWaterUnits < 0 ? "text-rose-600" : remainingWaterUnits > 0 ? "text-emerald-600" : "text-slate-900"}`}>{Number(Math.abs(remainingWaterUnits || 0)).toFixed(4)} units</p>
                        <p className={`mt-0.5 text-[9px] ${remainingWaterBalance < 0 ? "text-rose-600" : "text-slate-500"}`}>ksh {formatMoney(displayedWaterBalanceAmount)}</p>
                        {remainingWaterBalance < 0 && extraBilledWaterAmount > 0 && (
                          <p className="mt-0.5 text-[8px] text-rose-500">
                            Raw {formatMoney(Math.abs(remainingWaterBalance || 0))} + extra {formatMoney(extraBilledWaterAmount)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={`overflow-hidden rounded-[20px] text-white shadow-[0_12px_26px_rgba(15,23,42,0.22)] ${
                    remainingWaterUnits < 0 || remainingWaterBalance < 0
                      ? "bg-gradient-to-br from-rose-500 via-red-600 to-red-700"
                      : "bg-gradient-to-br from-green-500 via-emerald-600 to-emerald-700"
                  }`}>
                    <div className="grid gap-3 px-4 py-3 sm:px-4 sm:py-3.5 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:items-center">
                      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3">
                        <div className={`grid h-10 w-10 place-items-center rounded-full bg-white/95 shadow-md ${
                          remainingWaterUnits < 0 || remainingWaterBalance < 0 ? "text-red-600" : "text-emerald-600"
                        }`}>
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3C9 7.1 6 10.3 6 13.5A6 6 0 0018 13.5C18 10.3 15 7.1 12 3z" />
                          </svg>
                        </div>
                        <div className="grid grid-cols-2 items-start gap-3">
                          <div className="min-w-0">
                            <p className={`text-xs font-semibold sm:text-sm ${remainingWaterUnits < 0 ? "text-white" : "text-emerald-50"}`}>
                              {remainingWaterUnits < 0 ? "Overused" : "Balance"}
                            </p>
                            <p className={`mt-0.5 text-lg font-black tracking-tight sm:text-xl ${remainingWaterUnits < 0 ? "text-white" : ""}`}>
                              {Number(Math.abs(remainingWaterUnits || 0)).toFixed(4)} <span className="text-xs font-semibold sm:text-sm">Units</span>
                            </p>
                            <p className={`mt-0.5 text-[10px] sm:text-xs ${remainingWaterBalance < 0 ? "text-white" : "text-emerald-50/90"}`}>
                              {remainingWaterBalance < 0 ? `ksh ${formatMoney(displayedWaterBalanceAmount)} overused balance` : `ksh ${formatMoney(displayedWaterBalanceAmount)} remaining credit`}
                            </p>
                            {remainingWaterBalance < 0 && extraBilledWaterAmount > 0 && (
                              <p className="mt-0.5 text-[10px] text-red-100">
                                Raw {formatMoney(Math.abs(remainingWaterBalance || 0))} + extra {formatMoney(extraBilledWaterAmount)}
                              </p>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${remainingWaterUnits < 0 || remainingWaterBalance < 0 ? "text-red-100" : "text-emerald-100"}`}>Rate per Unit</p>
                            <p className="mt-0.5 text-sm font-bold sm:text-base">ksh {formatMoney(preview.rate)} <span className={`text-[11px] font-medium sm:text-xs ${remainingWaterUnits < 0 || remainingWaterBalance < 0 ? "text-red-100" : "text-emerald-50/90"}`}>/ unit</span></p>
                          </div>
                        </div>
                      </div>
                      {canCaptureSelectedWaterUnit && (
                        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                          <div className="w-full rounded-[16px] bg-white/95 p-2.5 text-slate-900 shadow-md">
                            <label className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                              Amount Paid
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={waterMeterForm.amountPaid}
                              onChange={(e) => updateWaterMeterForm("amountPaid", e.target.value)}
                              placeholder="Enter amount"
                              className="mt-1.5 w-full rounded-xl border border-emerald-200 bg-white px-3 py-2 text-xs font-medium text-slate-900 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 sm:text-sm"
                            />
                            <p className="mt-1 text-[10px] text-slate-500 sm:text-[11px]">
                              UT-Bills Wallet: ksh {formatMoney(waterMeterWalletBalance || 0)}
                            </p>
                            <p className="mt-1 text-[10px] text-slate-500 sm:text-[11px]">
                              {meterOutstandingDebtAmount > 0
                                ? `Water debt: ksh ${formatMoney(meterOutstandingDebtAmount)}. Pay exactly this amount to clear the debt, or at least ksh ${formatMoney(meterOutstandingDebtAmount + Number(preview.rate || 0))} to also buy 1 unit.`
                                : `Minimum purchase: 1 unit${preview.rate > 0 ? ` (ksh ${formatMoney(preview.rate)})` : ""}`}
                            </p>
                            {Number(waterMeterForm.amountPaid) > 0 && !waterPurchaseValidation.isValid && (
                              <p className="mt-1 text-[10px] font-semibold text-rose-600 sm:text-[11px]">
                                {waterPurchaseValidation.message}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={submitWaterPurchase}
                            disabled={
                              formLoading ||
                              !hasEnteredReading ||
                              !waterPurchaseValidation.isValid
                            }
                            className="inline-flex items-center justify-center gap-2 rounded-[16px] bg-white px-3 py-2 text-xs font-semibold text-emerald-700 shadow-md transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-[180px] sm:text-sm"
                          >
                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white">
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </span>
                            {formLoading ? "Processing..." : "Top Up from UT-Bills"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {canViewPurchaseCard ? (
                    <div className="rounded-[20px] border border-emerald-100 bg-white p-3.5 shadow-sm sm:p-4">
                      <div className="mt-3 grid gap-3">
                        <div>
                          <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                            Cut-Off Reading
                          </label>
                          <div className="overflow-x-auto pb-1">
                            {renderWaterMeterDisplay({
                              readingValue: lastCutOff,
                              editable: false,
                              label: "Cut-off reading"
                            })}
                          </div>
                        </div>

                        {canUpdateSelectedWaterUnit ? (
                          <div>
                            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                              Meter Reading
                            </label>
                            <div className="overflow-x-auto pb-1">
                              {renderWaterMeterDisplay({
                                editable: true,
                                label: "Current meter reading"
                              })}
                            </div>
                            {isReadingUnchanged && (
                              <p className="mt-2 text-[10px] font-medium text-amber-700 sm:text-[11px]">
                                Save stays disabled until the reading changes.
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 px-3 py-2.5 text-xs text-emerald-800 sm:text-sm">
                            View-only access for your unit water purchases, purchased units, and cut-off checkpoints.
                          </div>
                        )}
                      </div>

                      {canUpdateSelectedWaterUnit && (
                        <>
                          <button
                            type="button"
                            onClick={submitWaterMeterReading}
                            disabled={formLoading || !hasEnteredReading || isReadingUnchanged}
                            className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-emerald-600 px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                          >
                            {formLoading ? "Saving..." : "Save Current Reading"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowMeterReplacementForm((value) => !value);
                              setShowMeterCorrectionForm(false);
                            }}
                            disabled={formLoading || !hasLatestReading}
                            className="mt-2 inline-flex w-full items-center justify-center rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs font-semibold text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                          >
                            {showMeterReplacementForm ? "Cancel Meter Replacement" : "Switch / Replace Meter"}
                          </button>

                          {showMeterReplacementForm && (
                            <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50/60 p-3">
                              <p className="text-xs font-bold text-slate-900">Replace water meter</p>
                              <p className="mt-1 text-[10px] leading-relaxed text-slate-600 sm:text-xs">
                                Enter the final old-meter reading and the reading shown on the new meter. Paid units will be carried forward.
                              </p>
                              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                <label className="text-[11px] font-semibold text-slate-700">
                                  Old Meter – Final Reading
                                  <input
                                    type="number"
                                    min={actualStoredReading}
                                    step="0.0001"
                                    value={meterReplacementForm.oldMeterFinalReading}
                                    onChange={(event) => setMeterReplacementForm((form) => ({ ...form, oldMeterFinalReading: event.target.value }))}
                                    className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                                  />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-700">
                                  New Meter – Current Reading
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.0001"
                                    value={meterReplacementForm.newMeterCurrentReading}
                                    onChange={(event) => setMeterReplacementForm((form) => ({ ...form, newMeterCurrentReading: event.target.value }))}
                                    className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                                  />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-700">
                                  Replacement Date
                                  <input
                                    type="date"
                                    value={meterReplacementForm.replacementDate}
                                    onChange={(event) => setMeterReplacementForm((form) => ({ ...form, replacementDate: event.target.value }))}
                                    className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                                  />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-700 sm:col-span-2">
                                  Note (optional)
                                  <input
                                    type="text"
                                    maxLength="500"
                                    value={meterReplacementForm.note}
                                    onChange={(event) => setMeterReplacementForm((form) => ({ ...form, note: event.target.value }))}
                                    placeholder="Reason or meter details"
                                    className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                                  />
                                </label>
                              </div>
                              <button
                                type="button"
                                onClick={requestMeterReplacement}
                                disabled={formLoading}
                                className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-amber-600 px-3 py-2.5 text-xs font-bold text-white transition hover:bg-amber-700 disabled:opacity-60 sm:text-sm"
                              >
                                Review Meter Replacement
                              </button>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setShowMeterCorrectionForm((value) => !value);
                              setShowMeterReplacementForm(false);
                            }}
                            disabled={formLoading || !hasLatestReading}
                            className="mt-2 inline-flex w-full items-center justify-center rounded-xl border border-slate-300 bg-slate-50 px-3 py-2.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                          >
                            {showMeterCorrectionForm ? "Cancel Meter Correction" : "Correct Meter Details"}
                          </button>

                          {showMeterCorrectionForm && (
                            <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                              <p className="text-xs font-bold text-slate-900">Correct saved meter details</p>
                              <p className="mt-1 text-[10px] leading-relaxed text-slate-600 sm:text-xs">
                                Use this only to fix a wrong saved value. Existing payments and water debt invoices will not be changed.
                              </p>
                              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                <label className="text-[11px] font-semibold text-slate-700">
                                  Correct Meter Reading
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.0001"
                                    value={meterCorrectionForm.currentReading}
                                    onChange={(event) => setMeterCorrectionForm((form) => ({ ...form, currentReading: event.target.value }))}
                                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                                  />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-700">
                                  Correct Cut-Off Reading
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.0001"
                                    value={meterCorrectionForm.cutOffReading}
                                    onChange={(event) => setMeterCorrectionForm((form) => ({ ...form, cutOffReading: event.target.value }))}
                                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                                  />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-700">
                                  Correction Date
                                  <input
                                    type="date"
                                    value={meterCorrectionForm.correctionDate}
                                    onChange={(event) => setMeterCorrectionForm((form) => ({ ...form, correctionDate: event.target.value }))}
                                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                                  />
                                </label>
                                <label className="text-[11px] font-semibold text-slate-700 sm:col-span-2">
                                  Reason for Correction
                                  <input
                                    type="text"
                                    maxLength="500"
                                    value={meterCorrectionForm.reason}
                                    onChange={(event) => setMeterCorrectionForm((form) => ({ ...form, reason: event.target.value }))}
                                    placeholder="Example: Reading was entered incorrectly"
                                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                                  />
                                </label>
                              </div>
                              {meterCorrectionForm.currentReading !== "" && meterCorrectionForm.cutOffReading !== "" && (
                                <div className="mt-3 grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-white p-2.5 text-[10px] sm:text-xs">
                                  <div>
                                    <p className="font-semibold text-slate-500">Before</p>
                                    <p className="mt-1 font-bold text-slate-900">
                                      {Number((actualStoredReading || 0)).toFixed(4)} / {Number((lastCutOff || 0)).toFixed(4)}
                                    </p>
                                    <p className="text-slate-600">{Number(lastCutOff - actualStoredReading).toFixed(4)} units</p>
                                  </div>
                                  <div>
                                    <p className="font-semibold text-slate-500">After</p>
                                    <p className="mt-1 font-bold text-slate-900">
                                      {Number(meterCorrectionForm.currentReading || 0).toFixed(4)} / {Number(meterCorrectionForm.cutOffReading || 0).toFixed(4)}
                                    </p>
                                    <p className="text-slate-600">
                                      {Number(Number(meterCorrectionForm.cutOffReading || 0) - Number(meterCorrectionForm.currentReading || 0)).toFixed(4)} units
                                    </p>
                                  </div>
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={requestMeterCorrection}
                                disabled={formLoading}
                                className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-slate-700 px-3 py-2.5 text-xs font-bold text-white transition hover:bg-slate-800 disabled:opacity-60 sm:text-sm"
                              >
                                Review Meter Correction
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-[20px] border border-emerald-100 bg-white p-3.5 shadow-sm">
                      <p className="text-sm font-semibold text-slate-900">Tenant UTILITY-Bills Statement</p>
                      <p className="mt-1 text-xs text-slate-500">
                        View-only access for your unit water purchases, purchased units, and cut-off checkpoints.
                      </p>
                    </div>
                  )}

                  </>
                  ) : (
                    <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
                      <p className="text-sm font-semibold text-slate-900">{selectedUtilityHeading}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {isUnifiedUtilityTab
                          ? "This tab combines utility invoices, water top ups, wallet deposits, payments, and waivers into one account history."
                          : `Utility payments made with U + ID number are posted to the Utilities balance and applied to ${selectedUtilityLabel.toLowerCase()} invoices.`}
                      </p>
                      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                        <div className="rounded-xl bg-slate-50 px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Utility Balance</p>
                          <p className="mt-1 text-sm font-bold text-slate-900">ksh {formatMoney(getUtilityWalletBalance(utilityStatement || {}))}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Open Balance</p>
                          <p className="mt-1 text-sm font-bold text-slate-900">ksh {formatMoney(getDisplayedUtilityOutstandingAmount())}</p>
                          {utilityDebtSummary.creditAmount > 0 && (
                            <p className="mt-0.5 text-[10px] font-semibold text-emerald-700">
                              Credit {formatMoney(utilityDebtSummary.creditAmount)}
                            </p>
                          )}
                        </div>
                        <div className="rounded-xl bg-slate-50 px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Invoices</p>
                          <p className="mt-1 text-sm font-bold text-slate-900">{utilityStatement?.summary?.totalInvoices || 0}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 px-3 py-2">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">Waived</p>
                          <p className="mt-1 text-sm font-bold text-slate-900">ksh {formatMoney(utilityStatement?.summary?.totalWaivedAmount || 0)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedUtilityType !== "water" && !isUnifiedUtilityTab && canManageUtilityModalProperty && (
                    <div className="rounded-[20px] border border-emerald-100 bg-white p-3.5 shadow-sm">
                      <div className="mb-2 flex items-center gap-1.5">
                        <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Send {selectedUtilityLabel} Bill</p>
                          <p className="text-[10px] text-slate-500">Bills {tenantName}. Paid automatically from their UT Bills wallet when funded.</p>
                        </div>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={utilityBillForm.amount}
                          onChange={(e) => setUtilityBillForm({ ...utilityBillForm, amount: e.target.value })}
                          placeholder="Amount"
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        />
                        <input
                          type="date"
                          value={utilityBillForm.dueDate}
                          onChange={(e) => setUtilityBillForm({ ...utilityBillForm, dueDate: e.target.value })}
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        />
                        <input
                          value={utilityBillForm.description}
                          onChange={(e) => setUtilityBillForm({ ...utilityBillForm, description: e.target.value })}
                          placeholder={`${selectedUtilityLabel} bill description (optional)`}
                          className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100 sm:col-span-2"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={submitUtilityBill}
                        disabled={utilityBillSubmitting}
                        className="mt-3 inline-flex w-full items-center justify-center rounded-xl bg-emerald-600 px-3 py-2.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
                      >
                        {utilityBillSubmitting ? "Sending..." : `Send ${selectedUtilityLabel} Bill`}
                      </button>
                    </div>
                  )}

                  <div className="rounded-[20px] border border-emerald-100 bg-white p-3 shadow-sm sm:p-4">
                    <div className="mb-2.5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                      <div>
                          <p className="text-xs font-semibold text-slate-900 sm:text-sm">{selectedUtilityHeading} Statement</p>
                          <p className="mt-1 text-[10px] text-slate-500 sm:text-xs">
                            {selectedUtilityType === "water"
                              ? "All recorded water top ups, purchased units, and cut-off checkpoints for this unit."
                              : isUnifiedUtilityTab
                                ? "One merged utility history across water and other bills, wallet deposits, top ups, payments, and waivers."
                              : "Utility invoices, payments, and top ups for the selected utility type."}
                          </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
                        {filteredUtilityStatementRows.length > 0 && (
                          <div className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 sm:text-xs">
                            {filteredUtilityStatementRows.length} record{filteredUtilityStatementRows.length === 1 ? "" : "s"}
                          </div>
                        )}
                        {modalCompanyId && canManageUtilityModalProperty && (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                navigate("/app/ut-bills", {
                                  state: {
                                    tab: "billing",
                                    providerId: `mc:${modalCompanyId}`,
                                    openModal: "bill",
                                    propertyId: normalizeId(modalProperty?._id || modalProperty),
                                    unitId: normalizeId(selectedWaterUnit?._id),
                                  },
                                })
                              }
                              className="inline-flex items-center justify-center rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 text-[10px] font-semibold text-teal-700 transition hover:bg-teal-100 sm:text-xs"
                            >
                              Issue bill
                            </button>
                          </>
                        )}
                        {canManageUtilityModalProperty && (
                          <button
                            type="button"
                            onClick={triggerTenantUtilitySettlement}
                            disabled={utilityStatementLoading || utilitySettlementSubmitting}
                            className="inline-flex items-center justify-center rounded-xl bg-emerald-100 px-3 py-2 text-[10px] font-semibold text-emerald-800 transition hover:bg-emerald-200 disabled:cursor-not-allowed disabled:opacity-60 sm:text-xs"
                          >
                            {utilitySettlementSubmitting ? "Processing..." : "Pay from UT Wallet"}
                          </button>
                        )}
                        {canManageUtilityModalProperty && (
                          <button
                            type="button"
                            onClick={() => {
                              if (utilityStatementLoading) {
                                alert("Utility balance is still loading. Please wait a moment and try again.");
                                return;
                              }

                              const outstandingAmount = getDisplayedUtilityOutstandingAmount();

                              openUtilityWaiveModal({
                                _id: `utility-balance-${selectedUtilityType || "all"}`,
                                providerId: null,
                                source: "utility_balance",
                                outstandingAmount,
                                description: `${selectedUtilityHeading} total utility debt`,
                                customerName:
                                  waterMeterState?.tenant?.name ||
                                  selectedWaterUnit?.tenant?.name ||
                                  ""
                              });
                            }}
                            className="inline-flex items-center justify-center rounded-xl bg-amber-100 px-3 py-2 text-[10px] font-semibold text-amber-800 transition hover:bg-amber-200 sm:text-xs"
                          >
                            Waive
                          </button>
                        )}
                      </div>
                    </div>
                    {utilityStatementLoading ? (
                      <div className="py-8 text-xs text-slate-500 sm:text-sm">Loading utility statement...</div>
                    ) : filteredUtilityStatementRows.length === 0 ? (
                      <div className="py-8 text-xs text-slate-500 sm:text-sm">No {selectedUtilityHeading.toLowerCase()} records found yet.</div>
                    ) : (
                      <table className="w-full table-fixed border border-emerald-200 text-[9px] leading-[1.05] sm:text-xs">
                        <colgroup>
                          <col className="w-[19%]" />
                          <col />
                          <col className="w-[15%]" />
                          <col className="w-[15%]" />
                          <col className="w-[16%]" />
                          {canManageUtilityModalProperty && (
                            <col className="w-[19%]" />
                          )}
                        </colgroup>
                        <thead className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
                          <tr>
                            <th className="border border-emerald-500/40 px-0.5 py-1 font-semibold sm:px-2 sm:py-2">Date</th>
                            <th className="border border-emerald-500/40 px-0.5 py-1 font-semibold sm:px-2 sm:py-2">Description</th>
                            <th className="border border-emerald-500/40 px-0.5 py-1 text-right font-semibold sm:px-2 sm:py-2">Charge</th>
                            <th className="border border-emerald-500/40 px-0.5 py-1 text-right font-semibold sm:px-2 sm:py-2">Payment</th>
                            <th className="border border-emerald-500/40 px-0.5 py-1 text-right font-semibold sm:px-2 sm:py-2">Balance</th>
                            {canManageUtilityModalProperty && (
                              <th className="border border-emerald-500/40 px-0.5 py-1 text-center font-semibold sm:px-2 sm:py-2">Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {utilityStatementRowsWithRunningBalance.map((row, index) => {
                            const actionInvoice = resolveUtilityInvoiceActionTarget(row);
                            const canMutate = canMutateUtilityInvoice(actionInvoice);
                            return (
                              <tr key={`${row.type}-${getUtilitySettlementId(row) || row.invoiceId || index}-${row.date || index}`} className="odd:bg-white even:bg-emerald-50/40">
                                <td className="border border-emerald-100 px-0.5 py-1 align-top text-slate-700 break-words sm:px-2 sm:py-2" style={{ wordBreak: "break-word" }}>{formatDateWithTime(getUtilityStatementDate(row))}</td>
                                <td className="border border-emerald-100 px-0.5 py-1 align-top break-words sm:px-2 sm:py-2" style={{ wordBreak: "break-word" }}>
                                  <div className="font-medium text-slate-900 whitespace-pre-line">{row.description || "Utility record"}</div>
                                  <div className="mt-0.5 capitalize text-slate-500">{row.type}</div>
                                  {row.type === "invoice" && (
                                    <div className="mt-1">
                                      <UtilityInvoiceStatusBadge invoice={actionInvoice || row} compact />
                                    </div>
                                  )}
                                  {row?.source === "water_meter" && row?.meta && (
                                    <div className="text-slate-400">
                                      {Number(row.meta.currentReading || 0).toFixed(2)}→{Number(row.meta.cutOffReading || 0).toFixed(2)} ({Number(row.meta.allowedUnits || 0).toFixed(2)} u)
                                    </div>
                                  )}
                                </td>
                                <td className="border border-emerald-100 px-0.5 py-1 text-right align-top font-semibold text-red-600 break-words sm:px-2 sm:py-2">{getUtilityRowChargeAmount(row) > 0 ? formatMoney(getUtilityRowChargeAmount(row)) : <span className="text-slate-400">-</span>}</td>
                                <td className="border border-emerald-100 px-0.5 py-1 text-right align-top font-semibold text-emerald-700 break-words sm:px-2 sm:py-2">{getUtilityRowPaymentAmount(row) > 0 ? formatMoney(getUtilityRowPaymentAmount(row)) : <span className="text-slate-400">-</span>}</td>
                                <td className="border border-emerald-100 px-0.5 py-1 text-right align-top font-semibold text-slate-700 break-words sm:px-2 sm:py-2">{formatMoney(row.runningBalance || 0)}</td>
                                {canManageUtilityModalProperty && (
                                  <td className="border border-emerald-100 px-0.5 py-1 align-top sm:px-2 sm:py-2">
                                    {row.type === "invoice" && actionInvoice ? (
                                      <div className="flex flex-wrap items-center justify-center gap-1">
                                        <button
                                          type="button"
                                          disabled={!canMutate}
                                          onClick={() => openUtilityEditModal(actionInvoice)}
                                          className="rounded-lg border border-sky-200 bg-sky-50 px-1.5 py-1 text-[8px] font-semibold text-sky-700 transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-40 sm:text-[10px]"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          disabled={!canMutate}
                                          onClick={() => processUtilityInvoiceDelete(actionInvoice)}
                                          className="rounded-lg border border-rose-200 bg-rose-50 px-1.5 py-1 text-[8px] font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-40 sm:text-[10px]"
                                        >
                                          Delete
                                        </button>
                                        <button
                                          type="button"
                                          disabled={!(getUtilityInvoiceSettlement(actionInvoice).outstandingAmount > 0)}
                                          onClick={() => openUtilityWaiveModal(actionInvoice)}
                                          className="rounded-lg border border-amber-200 bg-amber-50 px-1.5 py-1 text-[8px] font-semibold text-amber-700 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40 sm:text-[10px]"
                                        >
                                          Waive
                                        </button>
                                      </div>
                                    ) : (
                                      <span className="text-slate-300">-</span>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Waive Bill Modal */}
      {showWaiveBillModal && selectedWaiveBillUnit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Waive Bill</h2>
                <button
                  onClick={() => {
                    setShowWaiveBillModal(false);
                    setSelectedWaiveBillUnit(null);
                    setSelectedWaiveBillTenant(null);
                    setSelectedWaiveBillInvoice(null);
                    setWaiveBillAmount("");
                    setWaiveBillDescription("");
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-800">{selectedWaiveBillUnit.property?.name}</h3>
                  <p className="text-green-600">Unit: {selectedWaiveBillUnit.label}</p>
                  <p className="text-green-600">
                    Tenant: {selectedWaiveBillTenant?.tenantName || selectedWaiveBillUnit.tenant?.name || 'No tenant assigned'}
                    {selectedWaiveBillTenant && !selectedWaiveBillTenant.isActive ? ' (Former Tenant)' : ''}
                  </p>
                  {selectedWaiveBillInvoice?.invoiceId ? (
                    <>
                      <p className="text-green-600">
                        Water Bill: {selectedWaiveBillInvoice.description || 'Water invoice'}
                      </p>
                      <p className="text-green-600">
                        Original Bill Amount: {formatMoney(selectedWaiveBillInvoice.amount || 0)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-green-600">Current Balance: {formatMoney(getWaiveBillCurrentBalance())}</p>
                      <p className="text-green-600">Monthly Rent: {formatMoney(selectedWaiveBillUnit.rentAmount)}</p>
                    </>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Waive Amount
                  </label>
                  <input
                    type="number"
                    value={waiveBillAmount}
                    onChange={(e) => setWaiveBillAmount(e.target.value)}
                    placeholder="Enter amount to waive"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    min="0"
                    step="0.01"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This will create a credit that reduces the tenant's balance
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Waiver
                  </label>
                  <textarea
                    value={waiveBillDescription}
                    onChange={(e) => setWaiveBillDescription(e.target.value)}
                    placeholder="Enter reason for waiving this amount (e.g., Late fee waiver, Damage deposit refund, etc.)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    rows="3"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowWaiveBillModal(false);
                      setSelectedWaiveBillUnit(null);
                      setSelectedWaiveBillTenant(null);
                      setSelectedWaiveBillInvoice(null);
                      setWaiveBillAmount("");
                      setWaiveBillDescription("");
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={processWaiveBill}
                    disabled={!waiveBillAmount || formLoading}
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {formLoading ? 'Creating...' : 'Waive Bill'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUtilityWaiveModal && selectedUtilityWaiveInvoice && (
        <WaiveBillModal
          providerId={selectedUtilityWaiveInvoice.providerId}
          invoice={selectedUtilityWaiveInvoice}
          categoryOptions={selectedUtilityWaiveInvoice?.source === "utility_balance" ? utilityWaiveCategoryOptions : []}
          onClose={closeUtilityWaiveModal}
          onWaive={processUtilityInvoiceWaive}
        />
      )}

      {showUtilityEditModal && selectedUtilityEditInvoice && (
        <EditInvoiceModal
          invoice={selectedUtilityEditInvoice}
          onClose={closeUtilityEditModal}
          onSave={(payload) => processUtilityInvoiceEdit(payload, selectedUtilityEditInvoice)}
        />
      )}

      <LeaseAgreementModal
        open={showLeaseAgreementModal}
        onClose={() => setShowLeaseAgreementModal(false)}
        selectedUnit={selectedUnit}
        properties={properties}
        managementCompanies={managementCompanies}
        selectedManagementCompany={selectedManagementCompany}
        user={user}
      />

      {showProfileModal && (
        <ProfileModal
          user={user}
          banksCatalog={banksCatalog}
          onClose={() => setShowProfileModal(false)}
          onSaved={(u) => { setUser(u); setShowProfileModal(false); }}
        />
      )}
      {/* Monthly Summary Modal */}
      {showMonthlySummaryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="p-3 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-800">Monthly Income Summary</h2>
                <button
                  onClick={() => { setShowMonthlySummaryModal(false); setSummaryRole(null); }}
                  className="text-gray-500 hover:text-gray-700 text-xl sm:text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Month/Year Selector */}
              <div className="flex gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select
                    value={selectedSummaryMonth}
                    onChange={(e) => setSelectedSummaryMonth(parseInt(e.target.value))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>
                        {getMonthName(month)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <select
                    value={selectedSummaryYear}
                    onChange={(e) => setSelectedSummaryYear(parseInt(e.target.value))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    {Array.from({ length: 5 }, (_, i) => getNairobiYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div className="self-end">
                  <button
                    onClick={() => fetchMonthlySummary(selectedSummaryMonth, selectedSummaryYear)}
                    disabled={monthlySummaryLoading}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm"
                  >
                    {monthlySummaryLoading ? 'Loading...' : 'Update'}
                  </button>
                </div>
              </div>

              {monthlySummaryLoading ? (
                <div className="space-y-6" aria-busy="true" aria-label="Loading summary">
                  <div className="rounded-lg bg-gradient-to-r from-green-50 to-green-50 p-6">
                    <Skeleton className="h-6 w-64" />
                    <Skeleton className="mt-3 h-3 w-40" />
                  </div>
                  <SkeletonStatGrid count={4} />
                  <SkeletonTable rows={5} />
                </div>
              ) : !monthlySummary ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No data found for the selected period.</p>
                </div>
              ) : (() => {
                const roleLabel = summaryRole === 'manager' ? 'Management' : summaryRole === 'landlord' ? 'Landlord' : summaryRole === 'tenant' ? 'Tenant' : null;
                const summaryToShow = roleLabel ? {
                  ...monthlySummary,
                  userRoles: [roleLabel],
                  hasMultipleRoles: false,
                  sections: (monthlySummary.sections || []).filter(section => section.role === roleLabel),
                  note: null,
                } : monthlySummary;

                return (
                  <div className="overflow-auto max-h-[70vh] space-y-6">
                    {/* Overall Summary Header */}
                    <div className="bg-gradient-to-r from-green-50 to-green-50 p-6 rounded-lg">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">
                        Monthly Summary - {summaryToShow.period}
                      </h3>

                      {summaryToShow.hasMultipleRoles ? (
                        <div className="mb-4">
                          <p className="text-sm text-gray-600 mb-2">
                            You have multiple roles: {summaryToShow.userRoles.join(', ')}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-white p-4 rounded-lg shadow-sm">
                              <p className="text-sm text-gray-600">Combined Earnings</p>
                              <p className="text-lg font-bold text-green-600">
                                {formatMoney(summaryToShow.combinedTotals.totalEarned)}
                              </p>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-sm">
                              <p className="text-sm text-gray-600">Total Paid</p>
                              <p className="text-lg font-bold text-red-600">
                                {formatMoney(summaryToShow.combinedTotals.totalPaid)}
                              </p>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow-sm">
                              <p className="text-sm text-gray-600">Total Transactions</p>
                              <p className="text-lg font-bold text-green-600">
                                {summaryToShow.combinedTotals.totalTransactions}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600 mb-4">
                          Role: {summaryToShow.userRoles.join(', ')}
                        </p>
                      )}

                      {summaryToShow.note && (
                        <div className="bg-green-100 border border-green-200 rounded-lg p-3 mt-4">
                          <p className="text-sm text-green-800">{summaryToShow.note}</p>
                        </div>
                      )}
                    </div>

                    {/* Individual Role Sections */}
                    {summaryToShow.sections.map((section, sectionIndex) => (
                      <div key={sectionIndex} className="bg-white border border-gray-200 rounded-lg">
                        <div className="p-4 border-b border-gray-200">
                          <h4 className="text-lg font-semibold text-gray-800">
                            {section.role} Activity
                          </h4>
                        </div>

                        <div className="p-4">
                          {section.role === 'Management' && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Payments Processed</p>
                                <p className="text-lg font-bold text-green-600">
                                  {section.totalPaymentsProcessed != null ? formatMoney(section.totalPaymentsProcessed) : '0.00'}
                                </p>
                                <p className="text-xs text-gray-500">{section.paymentsCount} transactions</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Commission Earned</p>
                                <p className="text-lg font-bold text-green-600">
                                  {section.totalCommissionEarned != null ? formatMoney(section.totalCommissionEarned) : '0.00'}
                                </p>
                                <p className="text-xs text-gray-500">Rate: {section.averageCommissionRate}</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Properties</p>
                                <p className="text-lg font-bold text-purple-600">{section.propertiesManaged}</p>
                                <p className="text-xs text-gray-500">{section.unitsManaged} units</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Avg per Property</p>
                                <p className="text-lg font-bold text-green-600">
                                  {section.propertiesManaged > 0 ? formatMoney(section.totalCommissionEarned / section.propertiesManaged) : '0.00'}
                                </p>
                              </div>
                            </div>
                          )}

                          {section.role === 'Landlord' && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Payments Received</p>
                                <p className="text-lg font-bold text-green-600">
                                  {section.totalPaymentsReceived != null ? formatMoney(section.totalPaymentsReceived) : '0.00'}
                                </p>
                                <p className="text-xs text-gray-500">{section.paymentsCount} transactions</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Income Earned</p>
                                <p className="text-lg font-bold text-green-600">
                                  {section.totalIncomeEarned != null ? formatMoney(section.totalIncomeEarned) : '0.00'}
                                </p>
                                <p className="text-xs text-gray-500">Net after fees</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Commission Paid</p>
                                <p className="text-lg font-bold text-red-600">
                                  {section.totalCommissionPaid != null ? formatMoney(section.totalCommissionPaid) : '0.00'}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Units Owned</p>
                                <p className="text-lg font-bold text-purple-600">{section.unitsOwned}</p>
                                <p className="text-xs text-gray-500">{formatMoney(section.averageIncomePerUnit)}/unit</p>
                              </div>
                            </div>
                          )}

                          {section.role === 'Tenant' && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Total Paid</p>
                                <p className="text-lg font-bold text-red-600">
                                  {section.totalPaid != null ? formatMoney(section.totalPaid) : '0.00'}
                                </p>
                                <p className="text-xs text-gray-500">{section.paymentsCount} payments</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Average Payment</p>
                                <p className="text-lg font-bold text-green-600">
                                  {formatMoney(section.averagePayment)}
                                </p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Units Rented</p>
                                <p className="text-lg font-bold text-purple-600">{section.unitsRented}</p>
                              </div>
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-600">Avg per Unit</p>
                                <p className="text-lg font-bold text-green-600">
                                  {section.unitsRented > 0 ? formatMoney(section.totalPaid / section.unitsRented) : '0.00'}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* No Data Message */}
                    {(!summaryToShow.sections || summaryToShow.sections.length === 0) && (
                      <div className="text-center py-8">
                        <p className="text-gray-500">{summaryToShow.message || 'No financial activity found for the selected period.'}</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {/* Wallet Transactions Modal */}
      {showWalletTransactionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white w-screen h-screen sm:max-w-6xl max-h-[90vh] sm:rounded-lg rounded-none overflow-x-hidden flex flex-col">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-3 sm:p-6 text-white flex-shrink-0">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-2xl font-bold">Wallet Transactions</h2>
                <button
                  onClick={() => setShowWalletTransactionsModal(false)}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              <div className="p-2 sm:p-6">
                {walletTransactionsLoading ? (
                  <div className="text-center py-8 text-gray-600">Loading transactions...</div>
                ) : walletTransactions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No transactions found.</div>
                ) : (
                  <table className="w-full table-fixed border border-emerald-200 rounded-none sm:rounded-lg text-[9px] sm:text-xs leading-[1.05]">
                    <colgroup>
                      <col className="w-[20%] sm:w-[18%]" />
                      <col className="w-[44%] sm:w-auto" />
                      <col className="w-[18%] sm:w-[18%]" />
                      <col className="w-[18%] sm:w-[18%]" />
                    </colgroup>
                    <thead className="bg-emerald-600 text-white">
                      <tr className="text-center">
                        <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Date</th>
                        <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Description</th>
                        <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 font-semibold">Amount</th>
                        <th className="border border-emerald-500/40 px-0.5 py-1 sm:px-2 sm:py-2 text-right font-semibold">New Bal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {walletTransactions.map((transaction, index) => {
                        const hasBalanceSnapshot = transaction?.balanceAfter !== null &&
                          transaction?.balanceAfter !== undefined &&
                          Number.isFinite(Number(transaction.balanceAfter));
                        let balanceAfterTransaction = hasBalanceSnapshot ? Number(transaction.balanceAfter) : wallet?.balance || 0;
                        if (!hasBalanceSnapshot) {
                          for (let i = 0; i < index; i++) {
                            balanceAfterTransaction -= walletTransactions[i].amount;
                          }
                        }
                        return (
                          <tr key={transaction._id || index} className="odd:bg-white even:bg-emerald-50/40">
                            <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-gray-800 align-top whitespace-normal break-words">
                              <div className="block sm:hidden break-inside-avoid">{formatDateWithTime(transaction.timestamp)}</div>
                              <div className="hidden sm:block break-inside-avoid">{formatDateWithTime(transaction.timestamp)}</div>
                            </td>
                            <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 align-top break-words whitespace-normal leading-tight" style={{ hyphens: 'auto', wordBreak: 'break-word' }}>
                              {transaction.description}
                              {transaction.relatedUnit && (
                                <div className="text-[10px] text-gray-600 mt-0.5">Unit: {transaction.relatedUnit.label}</div>
                              )}
                            </td>
                            <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">
                              {transaction.amount >= 0 ? `+${formatMoney(transaction.amount)}` : formatMoney(transaction.amount)}
                            </td>
                            <td className="border border-emerald-100 px-0.5 sm:px-2 py-1 sm:py-2 text-right align-top whitespace-nowrap">{formatMoney(balanceAfterTransaction)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Tenancy History Modal */}
      {showTenancyHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-4 sm:p-6 text-white flex-shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-2">
                    Tenancy History - {selectedCompanyForTenancy ? selectedCompanyForTenancy.companyName : (selectedProperty?.name || 'Property')}
                  </h3>
                  <div className="text-sm opacity-90">
                    {selectedCompanyForTenancy ? (
                      <span>All tenants across all properties in this company</span>
                    ) : (
                      <>
                        <span className="font-medium">{selectedProperty?.address}</span>
                        <span> • All tenants who have lived in this property</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowTenancyHistoryModal(false);
                    setManagedTenants([]);
                    setSelectedProperty(null);
                    setSelectedPropertyForTenancy(null);
                    setSelectedCompanyForTenancy(null);
                  }}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg p-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="p-4 sm:p-6">
                {tenancyHistoryLoading ? (
                  <SkeletonList rows={5} />
                ) : managedTenants.length > 0 ? (
                  <div className="space-y-4">
                    {/* Search Input */}
                    <div className="mb-4">
                      <div className="relative">
                        <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          placeholder="Search tenants by name, email, or ID number..."
                          value={tenantHistorySearch}
                          onChange={(e) => setTenantHistorySearch(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                        />
                        {tenantHistorySearch && (
                          <button
                            onClick={() => setTenantHistorySearch('')}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Tenants ({managedTenants.filter(tenant =>
                        tenant.tenantName.toLowerCase().includes(tenantHistorySearch.toLowerCase()) ||
                        tenant.tenantEmail.toLowerCase().includes(tenantHistorySearch.toLowerCase()) ||
                        (tenant.tenantIdNumber && tenant.tenantIdNumber.toLowerCase().includes(tenantHistorySearch.toLowerCase()))
                      ).length})
                    </h4>

                    {/* Tenant Cards Grid - Mobile responsive */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {managedTenants.filter(tenant =>
                        tenant.tenantName.toLowerCase().includes(tenantHistorySearch.toLowerCase()) ||
                        tenant.tenantEmail.toLowerCase().includes(tenantHistorySearch.toLowerCase()) ||
                        (tenant.tenantIdNumber && tenant.tenantIdNumber.toLowerCase().includes(tenantHistorySearch.toLowerCase()))
                      ).map((tenant, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                          {/* Tenant Header */}
                          <div className="mb-3">
                            <div className="flex items-start justify-between mb-2">
                              <h5 className="font-semibold text-gray-900 text-base leading-tight">{tenant.tenantName}</h5>
                              <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ml-2 ${tenant.units.filter(unit => unit.isCurrentTenant).length > 0
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                                }`}>
                                {tenant.units.filter(unit => unit.isCurrentTenant).length > 0 ? 'Current' : 'Previous'}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p className="truncate">{tenant.tenantEmail}</p>
                              {tenant.tenantIdNumber && (
                                <p><span className="font-medium">ID:</span> {tenant.tenantIdNumber}</p>
                              )}
                              <p><span className="font-medium">Total Balance:</span> {formatMoney(tenant.totalBalance || 0)}</p>
                              {tenant.tenantPhone && (
                                <p><span className="font-medium">Phone:</span> {tenant.tenantPhone}</p>
                              )}
                            </div>
                          </div>

                          {/* Units List - Compact */}
                          <div className="space-y-2">
                            <h6 className="font-medium text-gray-800 text-sm">
                              {selectedCompanyForTenancy ? 'Properties & Units:' : 'Units:'}
                            </h6>
                            <div className="space-y-2 max-h-32 overflow-y-auto">
                              {selectedCompanyForTenancy ? (
                                // Group units by property when showing company-level history
                                Object.entries(
                                  tenant.units.reduce((acc, unit) => {
                                    const propertyName = unit.propertyName || 'Unknown Property';
                                    if (!acc[propertyName]) acc[propertyName] = [];
                                    acc[propertyName].push(unit);
                                    return acc;
                                  }, {})
                                ).map(([propertyName, units]) => (
                                  <div key={propertyName} className="bg-gray-50 p-2 rounded text-sm">
                                    <div className="font-medium text-gray-800 text-xs mb-1 flex items-center gap-1">
                                      <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                      </svg>
                                      {propertyName}
                                    </div>
                                    {units.map((unit, unitIndex) => (
                                      <div key={unitIndex} className="ml-4 mb-2 last:mb-0">
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1 mb-1">
                                              <span className="font-medium text-gray-900 truncate">
                                                Unit {unit.unitLabel}
                                              </span>
                                              {unit.isCurrentTenant && (
                                                <span className="bg-green-100 text-green-800 text-xs px-1 py-0.5 rounded flex-shrink-0">
                                                  Current
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-gray-600 text-xs">
                                              {unit.startDate ? formatDateLong(unit.startDate) : 'N/A'} -
                                              {unit.endDate ? ` ${formatDateLong(unit.endDate)}` : ' Present'}
                                            </p>
                                            <p className="text-gray-700 text-xs"><span className="font-medium">Balance:</span> {formatMoney(unit.currentBalance || 0)}</p>
                                          </div>
                                          <button
                                            onClick={() => {
                                              console.log('=== View Statement Button Clicked ===');
                                              console.log('Tenant ID:', tenant.tenantId);
                                              console.log('Unit ID:', unit.unitId);
                                              console.log('Unit Label:', unit.unitLabel);
                                              console.log('Property Name:', unit.propertyName);
                                              console.log('Is Current Tenant:', unit.isCurrentTenant);
                                              console.log('Start Date:', unit.startDate);
                                              console.log('End Date:', unit.endDate);

                                              // Create a unit object compatible with fetchTransactionStatement
                                              const unitForStatement = {
                                                _id: unit.unitId,
                                                unitLabel: unit.unitLabel
                                              };
                                              console.log('Unit for statement:', unitForStatement);

                                              // Close the tenancy history modal
                                              setShowTenancyHistoryModal(false);

                                              if (!tenant?.tenantId) {
                                                alert("No tenant selected for statement.");
                                                return;
                                              }

                                              // Fetch and show the specific tenant's statement
                                              console.log('Calling fetchTenantMonthlyStatus (tenant view)...');
                                              fetchTenantMonthlyStatus(unitForStatement, "", "", tenant.tenantId);
                                            }}
                                            disabled={tenantMonthlyStatusLoading}
                                            className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition-colors text-xs font-medium flex-shrink-0 disabled:opacity-50"
                                          >
                                            {tenantMonthlyStatusLoading ? 'Loading...' : 'View'}
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ))
                              ) : (
                                // Original units display for property-level history
                                tenant.units.map((unit, unitIndex) => (
                                  <div key={unitIndex} className="bg-gray-50 p-2 rounded text-sm">
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1 mb-1">
                                          <span className="font-medium text-gray-900 truncate">
                                            Unit {unit.unitLabel}
                                          </span>
                                          {unit.isCurrentTenant && (
                                            <span className="bg-green-100 text-green-800 text-xs px-1 py-0.5 rounded flex-shrink-0">
                                              Current
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-gray-600 text-xs">
                                          {unit.startDate ? formatDateLong(unit.startDate) : 'N/A'} -
                                          {unit.endDate ? ` ${formatDateLong(unit.endDate)}` : ' Present'}
                                        </p>
                                        <p className="text-gray-700 text-xs"><span className="font-medium">Balance:</span> {formatMoney(unit.currentBalance || 0)}</p>
                                      </div>
                                      <button
                                        onClick={() => {
                                          console.log('=== View Statement Button Clicked ===');
                                          console.log('Tenant ID:', tenant.tenantId);
                                          console.log('Unit ID:', unit.unitId);
                                          console.log('Unit Label:', unit.unitLabel);
                                          console.log('Is Current Tenant:', unit.isCurrentTenant);
                                          console.log('Start Date:', unit.startDate);
                                          console.log('End Date:', unit.endDate);
                                          console.log('Full tenant object:', tenant);
                                          console.log('Full unit object:', unit);

                                          // Create a unit object compatible with fetchTransactionStatement
                                          const unitForStatement = {
                                            _id: unit.unitId,
                                            unitLabel: unit.unitLabel
                                          };
                                          console.log('Unit for statement:', unitForStatement);

                                          // Close the tenancy history modal
                                          setShowTenancyHistoryModal(false);

                                          if (!tenant?.tenantId) {
                                            alert("No tenant selected for statement.");
                                            return;
                                          }

                                          // Fetch and show the specific tenant's statement
                                          console.log('Calling fetchTenantMonthlyStatus (tenant view)...');
                                          fetchTenantMonthlyStatus(unitForStatement, "", "", tenant.tenantId);
                                        }}
                                        disabled={tenantMonthlyStatusLoading}
                                        className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition-colors text-xs font-medium flex-shrink-0 disabled:opacity-50"
                                      >
                                        {tenantMonthlyStatusLoading ? 'Loading...' : 'View'}
                                      </button>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* No search results message */}
                    {managedTenants.filter(tenant =>
                      tenant.tenantName.toLowerCase().includes(tenantHistorySearch.toLowerCase()) ||
                      tenant.tenantEmail.toLowerCase().includes(tenantHistorySearch.toLowerCase()) ||
                      (tenant.tenantIdNumber && tenant.tenantIdNumber.toLowerCase().includes(tenantHistorySearch.toLowerCase()))
                    ).length === 0 && tenantHistorySearch && (
                        <div className="text-center py-8 text-gray-500">
                          <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <p className="text-lg font-medium mb-2">No tenants found</p>
                          <p>No tenants match your search criteria.</p>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-lg font-medium mb-2">No Tenant History Found</p>
                    <p>No tenants have been managed under your properties yet.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-between">
                <button
                  onClick={() => {
                    if (selectedCompanyForTenancy) {
                      fetchCompanyTenants(selectedCompanyForTenancy._id);
                    } else if (selectedPropertyForTenancy) {
                      fetchPropertyTenants(selectedPropertyForTenancy._id);
                    }
                  }}
                  disabled={tenancyHistoryLoading || (!selectedCompanyForTenancy && !selectedPropertyForTenancy)}
                  className="sm:w-auto px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors text-sm font-medium"
                >
                  {tenancyHistoryLoading ? 'Loading...' : 'Refresh'}
                </button>
                <button
                  onClick={() => {
                    setShowTenancyHistoryModal(false);
                    setManagedTenants([]);
                    setSelectedPropertyForTenancy(null);
                    setSelectedCompanyForTenancy(null);
                  }}
                  className="sm:w-auto px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ManagementCompanyStaffModal
        isOpen={!!staffModalCompany}
        company={staffModalCompany}
        onClose={() => setStaffModalCompany(null)}
      />
    </div>
  );
}

export default FRPModule;
